import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { ProjectAuthMethodId } from '../enums/project-auth-method-id';
import { ProjectKeyScopes } from '../enums/project-key-scopes';
import { ProjectOAuth2Auth0Prompt } from '../enums/project-o-auth-2-auth-0-prompt';
import { ProjectOAuth2DiscordPrompt } from '../enums/project-o-auth-2-discord-prompt';
import { ProjectOAuth2GitHubPrompt } from '../enums/project-o-auth-2-git-hub-prompt';
import { ProjectOAuth2GooglePrompt } from '../enums/project-o-auth-2-google-prompt';
import { ProjectOAuth2KakaoPrompt } from '../enums/project-o-auth-2-kakao-prompt';
import { ProjectOAuth2MicrosoftPrompt } from '../enums/project-o-auth-2-microsoft-prompt';
import { ProjectOAuth2OidcPrompt } from '../enums/project-o-auth-2-oidc-prompt';
import { ProjectOAuth2OktaPrompt } from '../enums/project-o-auth-2-okta-prompt';
import { ProjectOAuth2SalesforcePrompt } from '../enums/project-o-auth-2-salesforce-prompt';
import { ProjectOAuth2ZohoPrompt } from '../enums/project-o-auth-2-zoho-prompt';
import { ProjectOAuthProviderId } from '../enums/project-o-auth-provider-id';
import { ProjectPolicyId } from '../enums/project-policy-id';
import { ProjectProtocolId } from '../enums/project-protocol-id';
import { ProjectServiceId } from '../enums/project-service-id';
import { ProjectSMTPSecure } from '../enums/project-smtp-secure';
import { ProjectEmailTemplateId } from '../enums/project-email-template-id';
import { ProjectEmailTemplateLocale } from '../enums/project-email-template-locale';
export class Project {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Get a project.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    get(): Promise<Models.Project> {
        const apiPath = '/project';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a project.
     *
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    delete(): Promise<{}> {
        const apiPath = '/project';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Update properties of a specific auth method. Use this endpoint to enable or disable a method in your project.
     *
     * @param {ProjectAuthMethodId} params.methodId - Auth Method ID. Possible values: email-password,magic-url,email-otp,anonymous,invites,jwt,phone,passkey
     * @param {boolean} params.enabled - Auth method status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateAuthMethod(params: {
        methodId: ProjectAuthMethodId;
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Update properties of a specific auth method. Use this endpoint to enable or disable a method in your project.
     *
     * @param {ProjectAuthMethodId} methodId - Auth Method ID. Possible values: email-password,magic-url,email-otp,anonymous,invites,jwt,phone,passkey
     * @param {boolean} enabled - Auth method status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateAuthMethod(
        methodId: ProjectAuthMethodId,
        enabled: boolean,
    ): Promise<Models.Project>;
    updateAuthMethod(
        paramsOrFirst:
            | { methodId: ProjectAuthMethodId; enabled: boolean }
            | ProjectAuthMethodId,
        ...rest: [boolean?]
    ): Promise<Models.Project> {
        let params: { methodId: ProjectAuthMethodId; enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('methodId' in paramsOrFirst || 'enabled' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                methodId: ProjectAuthMethodId;
                enabled: boolean;
            };
        } else {
            params = {
                methodId: paramsOrFirst as ProjectAuthMethodId,
                enabled: rest[0] as boolean,
            };
        }

        const methodId = params.methodId;
        const enabled = params.enabled;

        if (typeof methodId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "methodId"',
            );
        }
        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/auth-methods/{methodId}'.replace(
            '{methodId}',
            encodeURIComponent(String(methodId)),
        );
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all environments of the project. You can use the query params to filter your results.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: name, teamId, labels, search, accessedAt, status, parentId
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProjectList>}
     */
    listEnvironments(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.ProjectList>;
    /**
     * Get a list of all environments of the project. You can use the query params to filter your results.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: name, teamId, labels, search, accessedAt, status, parentId
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProjectList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listEnvironments(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.ProjectList>;
    listEnvironments(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.ProjectList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/project/environments';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'projectList',
            models: {
                projectList: {
                    defaults: {
                        total: 0,
                        projects: [],
                    },
                    properties: {
                        projects: {
                            items: 'project',
                        },
                    },
                },
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new environment of the project. An environment is a separate project in the same organization and region, with its own resources and users. Its usage is billed under the project it belongs to.
     *
     * @param {string} params.environmentId - Unique Id. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, and hyphen. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Environment name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    createEnvironment(params: {
        environmentId: string;
        name: string;
    }): Promise<Models.Project>;
    /**
     * Create a new environment of the project. An environment is a separate project in the same organization and region, with its own resources and users. Its usage is billed under the project it belongs to.
     *
     * @param {string} environmentId - Unique Id. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, and hyphen. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Environment name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createEnvironment(
        environmentId: string,
        name: string,
    ): Promise<Models.Project>;
    createEnvironment(
        paramsOrFirst: { environmentId: string; name: string } | string,
        ...rest: [string?]
    ): Promise<Models.Project> {
        let params: { environmentId: string; name: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                environmentId: string;
                name: string;
            };
        } else {
            params = {
                environmentId: paramsOrFirst as string,
                name: rest[0] as string,
            };
        }

        const environmentId = params.environmentId;
        const name = params.name;

        if (typeof environmentId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "environmentId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        const apiPath = '/project/environments';
        const apiPayload: Payload = {};
        if (typeof environmentId !== 'undefined') {
            apiPayload['environmentId'] = environmentId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get an environment of the project by its unique ID.
     *
     * @param {string} params.environmentId - Environment unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    getEnvironment(params: { environmentId: string }): Promise<Models.Project>;
    /**
     * Get an environment of the project by its unique ID.
     *
     * @param {string} environmentId - Environment unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getEnvironment(environmentId: string): Promise<Models.Project>;
    getEnvironment(
        paramsOrFirst: { environmentId: string } | string,
    ): Promise<Models.Project> {
        let params: { environmentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { environmentId: string };
        } else {
            params = {
                environmentId: paramsOrFirst as string,
            };
        }

        const environmentId = params.environmentId;

        if (typeof environmentId === 'undefined' || environmentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "environmentId"',
            );
        }
        const apiPath = '/project/environments/{environmentId}'.replace(
            '{environmentId}',
            encodeURIComponent(String(environmentId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the name of an environment of the project.
     *
     * @param {string} params.environmentId - Environment unique ID.
     * @param {string} params.name - Environment name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateEnvironment(params: {
        environmentId: string;
        name: string;
    }): Promise<Models.Project>;
    /**
     * Update the name of an environment of the project.
     *
     * @param {string} environmentId - Environment unique ID.
     * @param {string} name - Environment name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateEnvironment(
        environmentId: string,
        name: string,
    ): Promise<Models.Project>;
    updateEnvironment(
        paramsOrFirst: { environmentId: string; name: string } | string,
        ...rest: [string?]
    ): Promise<Models.Project> {
        let params: { environmentId: string; name: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                environmentId: string;
                name: string;
            };
        } else {
            params = {
                environmentId: paramsOrFirst as string,
                name: rest[0] as string,
            };
        }

        const environmentId = params.environmentId;
        const name = params.name;

        if (typeof environmentId === 'undefined' || environmentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "environmentId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        const apiPath = '/project/environments/{environmentId}'.replace(
            '{environmentId}',
            encodeURIComponent(String(environmentId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete an environment of the project, with all of its resources and users.
     *
     * @param {string} params.environmentId - Environment unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteEnvironment(params: { environmentId: string }): Promise<{}>;
    /**
     * Delete an environment of the project, with all of its resources and users.
     *
     * @param {string} environmentId - Environment unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteEnvironment(environmentId: string): Promise<{}>;
    deleteEnvironment(
        paramsOrFirst: { environmentId: string } | string,
    ): Promise<{}> {
        let params: { environmentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { environmentId: string };
        } else {
            params = {
                environmentId: paramsOrFirst as string,
            };
        }

        const environmentId = params.environmentId;

        if (typeof environmentId === 'undefined' || environmentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "environmentId"',
            );
        }
        const apiPath = '/project/environments/{environmentId}'.replace(
            '{environmentId}',
            encodeURIComponent(String(environmentId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of all API keys from the current project.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: expire, accessedAt, name, scopes
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.KeyList>}
     */
    listKeys(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.KeyList>;
    /**
     * Get a list of all API keys from the current project.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: expire, accessedAt, name, scopes
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.KeyList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listKeys(queries?: string[], total?: boolean): Promise<Models.KeyList>;
    listKeys(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.KeyList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/project/keys';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'keyList',
            models: {
                keyList: {
                    defaults: {
                        total: 0,
                        keys: [],
                    },
                    properties: {
                        keys: {
                            items: 'key',
                        },
                    },
                },
                key: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new API key. It's recommended to have multiple API keys with strict scopes for separate functions within your project.
     *
     * You can also create an ephemeral API key if you need a short-lived key instead.
     *
     * @param {string} params.keyId - Key ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Key name. Max length: 128 chars.
     * @param {ProjectKeyScopes[]} params.scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {string | null} params.expire - Expiration time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. Use null for unlimited expiration.
     * @param {string} params.description - Optional description for the key.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     */
    createKey(params: {
        keyId: string;
        name: string;
        scopes: ProjectKeyScopes[];
        expire?: string | null;
        description?: string;
    }): Promise<Models.Key>;
    /**
     * Create a new API key. It's recommended to have multiple API keys with strict scopes for separate functions within your project.
     *
     * You can also create an ephemeral API key if you need a short-lived key instead.
     *
     * @param {string} keyId - Key ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Key name. Max length: 128 chars.
     * @param {ProjectKeyScopes[]} scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {string | null} expire - Expiration time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. Use null for unlimited expiration.
     * @param {string} description - Optional description for the key.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createKey(
        keyId: string,
        name: string,
        scopes: ProjectKeyScopes[],
        expire?: string | null,
        description?: string,
    ): Promise<Models.Key>;
    createKey(
        paramsOrFirst:
            | {
                  keyId: string;
                  name: string;
                  scopes: ProjectKeyScopes[];
                  expire?: string | null;
                  description?: string;
              }
            | string,
        ...rest: [string?, ProjectKeyScopes[]?, (string | null)?, string?]
    ): Promise<Models.Key> {
        let params: {
            keyId: string;
            name: string;
            scopes: ProjectKeyScopes[];
            expire?: string | null;
            description?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                keyId: string;
                name: string;
                scopes: ProjectKeyScopes[];
                expire?: string | null;
                description?: string;
            };
        } else {
            params = {
                keyId: paramsOrFirst as string,
                name: rest[0] as string,
                scopes: rest[1] as ProjectKeyScopes[],
                expire: rest[2] as string | null,
                description: rest[3] as string,
            };
        }

        const keyId = params.keyId;
        const name = params.name;
        const scopes = params.scopes;
        const expire = params.expire;
        const description = params.description;

        if (typeof keyId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "keyId"');
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof scopes === 'undefined') {
            throw new AppwriteException('Missing required parameter: "scopes"');
        }
        const apiPath = '/project/keys';
        const apiPayload: Payload = {};
        if (typeof keyId !== 'undefined') {
            apiPayload['keyId'] = keyId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof expire !== 'undefined') {
            apiPayload['expire'] = expire;
        }
        if (typeof description !== 'undefined') {
            apiPayload['description'] = description;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'key',
            models: {
                key: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new ephemeral API key. It's recommended to have multiple API keys with strict scopes for separate functions within your project.
     *
     * You can also create a standard API key if you need a longer-lived key instead.
     *
     * @param {ProjectKeyScopes[]} params.scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {number} params.duration - Time in seconds before ephemeral key expires. Maximum duration is 3600 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EphemeralKey>}
     */
    createEphemeralKey(params: {
        scopes: ProjectKeyScopes[];
        duration: number;
    }): Promise<Models.EphemeralKey>;
    /**
     * Create a new ephemeral API key. It's recommended to have multiple API keys with strict scopes for separate functions within your project.
     *
     * You can also create a standard API key if you need a longer-lived key instead.
     *
     * @param {ProjectKeyScopes[]} scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {number} duration - Time in seconds before ephemeral key expires. Maximum duration is 3600 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EphemeralKey>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createEphemeralKey(
        scopes: ProjectKeyScopes[],
        duration: number,
    ): Promise<Models.EphemeralKey>;
    createEphemeralKey(
        paramsOrFirst:
            | { scopes: ProjectKeyScopes[]; duration: number }
            | ProjectKeyScopes[],
        ...rest: [number?]
    ): Promise<Models.EphemeralKey> {
        let params: { scopes: ProjectKeyScopes[]; duration: number };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('scopes' in paramsOrFirst || 'duration' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                scopes: ProjectKeyScopes[];
                duration: number;
            };
        } else {
            params = {
                scopes: paramsOrFirst as ProjectKeyScopes[],
                duration: rest[0] as number,
            };
        }

        const scopes = params.scopes;
        const duration = params.duration;

        if (typeof scopes === 'undefined') {
            throw new AppwriteException('Missing required parameter: "scopes"');
        }
        if (typeof duration === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "duration"',
            );
        }
        const apiPath = '/project/keys/ephemeral';
        const apiPayload: Payload = {};
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof duration !== 'undefined') {
            apiPayload['duration'] = duration;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'ephemeralKey',
            models: {
                ephemeralKey: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a key by its unique ID.
     *
     * @param {string} params.keyId - Key ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     */
    getKey(params: { keyId: string }): Promise<Models.Key>;
    /**
     * Get a key by its unique ID.
     *
     * @param {string} keyId - Key ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getKey(keyId: string): Promise<Models.Key>;
    getKey(paramsOrFirst: { keyId: string } | string): Promise<Models.Key> {
        let params: { keyId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { keyId: string };
        } else {
            params = {
                keyId: paramsOrFirst as string,
            };
        }

        const keyId = params.keyId;

        if (typeof keyId === 'undefined' || keyId === '') {
            throw new AppwriteException('Missing required parameter: "keyId"');
        }
        const apiPath = '/project/keys/{keyId}'.replace(
            '{keyId}',
            encodeURIComponent(String(keyId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'key',
            models: {
                key: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a key by its unique ID. Use this endpoint to update the name, scopes, or expiration time of an API key.
     *
     * @param {string} params.keyId - Key ID.
     * @param {string} params.name - Key name. Max length: 128 chars.
     * @param {ProjectKeyScopes[]} params.scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {string | null} params.expire - Expiration time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. Use null for unlimited expiration.
     * @param {string | null} params.description - Optional description for the key.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     */
    updateKey(params: {
        keyId: string;
        name: string;
        scopes: ProjectKeyScopes[];
        expire?: string | null;
        description?: string | null;
    }): Promise<Models.Key>;
    /**
     * Update a key by its unique ID. Use this endpoint to update the name, scopes, or expiration time of an API key.
     *
     * @param {string} keyId - Key ID.
     * @param {string} name - Key name. Max length: 128 chars.
     * @param {ProjectKeyScopes[]} scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {string | null} expire - Expiration time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. Use null for unlimited expiration.
     * @param {string | null} description - Optional description for the key.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateKey(
        keyId: string,
        name: string,
        scopes: ProjectKeyScopes[],
        expire?: string | null,
        description?: string | null,
    ): Promise<Models.Key>;
    updateKey(
        paramsOrFirst:
            | {
                  keyId: string;
                  name: string;
                  scopes: ProjectKeyScopes[];
                  expire?: string | null;
                  description?: string | null;
              }
            | string,
        ...rest: [
            string?,
            ProjectKeyScopes[]?,
            (string | null)?,
            (string | null)?,
        ]
    ): Promise<Models.Key> {
        let params: {
            keyId: string;
            name: string;
            scopes: ProjectKeyScopes[];
            expire?: string | null;
            description?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                keyId: string;
                name: string;
                scopes: ProjectKeyScopes[];
                expire?: string | null;
                description?: string | null;
            };
        } else {
            params = {
                keyId: paramsOrFirst as string,
                name: rest[0] as string,
                scopes: rest[1] as ProjectKeyScopes[],
                expire: rest[2] as string | null,
                description: rest[3] as string | null,
            };
        }

        const keyId = params.keyId;
        const name = params.name;
        const scopes = params.scopes;
        const expire = params.expire;
        const description = params.description;

        if (typeof keyId === 'undefined' || keyId === '') {
            throw new AppwriteException('Missing required parameter: "keyId"');
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof scopes === 'undefined') {
            throw new AppwriteException('Missing required parameter: "scopes"');
        }
        const apiPath = '/project/keys/{keyId}'.replace(
            '{keyId}',
            encodeURIComponent(String(keyId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof expire !== 'undefined') {
            apiPayload['expire'] = expire;
        }
        if (typeof description !== 'undefined') {
            apiPayload['description'] = description;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'key',
            models: {
                key: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a key by its unique ID. Once deleted, the key can no longer be used to authenticate API calls.
     *
     * @param {string} params.keyId - Key ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteKey(params: { keyId: string }): Promise<{}>;
    /**
     * Delete a key by its unique ID. Once deleted, the key can no longer be used to authenticate API calls.
     *
     * @param {string} keyId - Key ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteKey(keyId: string): Promise<{}>;
    deleteKey(paramsOrFirst: { keyId: string } | string): Promise<{}> {
        let params: { keyId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { keyId: string };
        } else {
            params = {
                keyId: paramsOrFirst as string,
            };
        }

        const keyId = params.keyId;

        if (typeof keyId === 'undefined' || keyId === '') {
            throw new AppwriteException('Missing required parameter: "keyId"');
        }
        const apiPath = '/project/keys/{keyId}'.replace(
            '{keyId}',
            encodeURIComponent(String(keyId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the project labels. Labels can be used to easily filter projects in an organization.
     *
     * @param {string[]} params.labels - Array of project labels. Replaces the previous labels. Maximum of 1000 labels are allowed, each up to 36 alphanumeric characters long.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateLabels(params: { labels: string[] }): Promise<Models.Project>;
    /**
     * Update the project labels. Labels can be used to easily filter projects in an organization.
     *
     * @param {string[]} labels - Array of project labels. Replaces the previous labels. Maximum of 1000 labels are allowed, each up to 36 alphanumeric characters long.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateLabels(labels: string[]): Promise<Models.Project>;
    updateLabels(
        paramsOrFirst: { labels: string[] } | string[],
    ): Promise<Models.Project> {
        let params: { labels: string[] };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { labels: string[] };
        } else {
            params = {
                labels: paramsOrFirst as string[],
            };
        }

        const labels = params.labels;

        if (typeof labels === 'undefined') {
            throw new AppwriteException('Missing required parameter: "labels"');
        }
        const apiPath = '/project/labels';
        const apiPayload: Payload = {};
        if (typeof labels !== 'undefined') {
            apiPayload['labels'] = labels;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all mock phones in the project. This endpoint returns an array of all mock phones and their OTPs.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MockNumberList>}
     */
    listMockPhones(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.MockNumberList>;
    /**
     * Get a list of all mock phones in the project. This endpoint returns an array of all mock phones and their OTPs.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MockNumberList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listMockPhones(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.MockNumberList>;
    listMockPhones(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.MockNumberList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/project/mock-phones';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mockNumberList',
            models: {
                mockNumberList: {
                    defaults: {
                        total: 0,
                        mockNumbers: [],
                    },
                    properties: {
                        mockNumbers: {
                            items: 'mockNumber',
                        },
                    },
                },
                mockNumber: {
                    defaults: {
                        number: '',
                        otp: '',
                        $createdAt: '',
                        $updatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new mock phone for your project. Use this endpoint to register a mock phone number and its sign-in OTP for your testers.
     *
     * @param {string} params.number - Phone number to associate with the mock phone. Must be a valid E.164 formatted phone number.
     * @param {string} params.otp - One-time password (OTP) to associate with the mock phone. Must be a 6-digit numeric code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MockNumber>}
     */
    createMockPhone(params: {
        number: string;
        otp: string;
    }): Promise<Models.MockNumber>;
    /**
     * Create a new mock phone for your project. Use this endpoint to register a mock phone number and its sign-in OTP for your testers.
     *
     * @param {string} number - Phone number to associate with the mock phone. Must be a valid E.164 formatted phone number.
     * @param {string} otp - One-time password (OTP) to associate with the mock phone. Must be a 6-digit numeric code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MockNumber>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMockPhone(number: string, otp: string): Promise<Models.MockNumber>;
    createMockPhone(
        paramsOrFirst: { number: string; otp: string } | string,
        ...rest: [string?]
    ): Promise<Models.MockNumber> {
        let params: { number: string; otp: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { number: string; otp: string };
        } else {
            params = {
                number: paramsOrFirst as string,
                otp: rest[0] as string,
            };
        }

        const number = params.number;
        const otp = params.otp;

        if (typeof number === 'undefined') {
            throw new AppwriteException('Missing required parameter: "number"');
        }
        if (typeof otp === 'undefined') {
            throw new AppwriteException('Missing required parameter: "otp"');
        }
        const apiPath = '/project/mock-phones';
        const apiPayload: Payload = {};
        if (typeof number !== 'undefined') {
            apiPayload['number'] = number;
        }
        if (typeof otp !== 'undefined') {
            apiPayload['otp'] = otp;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mockNumber',
            models: {
                mockNumber: {
                    defaults: {
                        number: '',
                        otp: '',
                        $createdAt: '',
                        $updatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a mock phone by its unique number. This endpoint returns the mock phone's OTP.
     *
     * @param {string} params.number - Phone number associated with the mock phone. Must be a valid E.164 formatted phone number.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MockNumber>}
     */
    getMockPhone(params: { number: string }): Promise<Models.MockNumber>;
    /**
     * Get a mock phone by its unique number. This endpoint returns the mock phone's OTP.
     *
     * @param {string} number - Phone number associated with the mock phone. Must be a valid E.164 formatted phone number.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MockNumber>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getMockPhone(number: string): Promise<Models.MockNumber>;
    getMockPhone(
        paramsOrFirst: { number: string } | string,
    ): Promise<Models.MockNumber> {
        let params: { number: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { number: string };
        } else {
            params = {
                number: paramsOrFirst as string,
            };
        }

        const number = params.number;

        if (typeof number === 'undefined' || number === '') {
            throw new AppwriteException('Missing required parameter: "number"');
        }
        const apiPath = '/project/mock-phones/{number}'.replace(
            '{number}',
            encodeURIComponent(String(number)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mockNumber',
            models: {
                mockNumber: {
                    defaults: {
                        number: '',
                        otp: '',
                        $createdAt: '',
                        $updatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a mock phone by its unique number. Use this endpoint to update the mock phone's OTP.
     *
     * @param {string} params.number - Phone number associated with the mock phone. Must be a valid E.164 formatted phone number.
     * @param {string} params.otp - One-time password (OTP) to associate with the mock phone. Must be a 6-digit numeric code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MockNumber>}
     */
    updateMockPhone(params: {
        number: string;
        otp: string;
    }): Promise<Models.MockNumber>;
    /**
     * Update a mock phone by its unique number. Use this endpoint to update the mock phone's OTP.
     *
     * @param {string} number - Phone number associated with the mock phone. Must be a valid E.164 formatted phone number.
     * @param {string} otp - One-time password (OTP) to associate with the mock phone. Must be a 6-digit numeric code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MockNumber>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMockPhone(number: string, otp: string): Promise<Models.MockNumber>;
    updateMockPhone(
        paramsOrFirst: { number: string; otp: string } | string,
        ...rest: [string?]
    ): Promise<Models.MockNumber> {
        let params: { number: string; otp: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { number: string; otp: string };
        } else {
            params = {
                number: paramsOrFirst as string,
                otp: rest[0] as string,
            };
        }

        const number = params.number;
        const otp = params.otp;

        if (typeof number === 'undefined' || number === '') {
            throw new AppwriteException('Missing required parameter: "number"');
        }
        if (typeof otp === 'undefined') {
            throw new AppwriteException('Missing required parameter: "otp"');
        }
        const apiPath = '/project/mock-phones/{number}'.replace(
            '{number}',
            encodeURIComponent(String(number)),
        );
        const apiPayload: Payload = {};
        if (typeof otp !== 'undefined') {
            apiPayload['otp'] = otp;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mockNumber',
            models: {
                mockNumber: {
                    defaults: {
                        number: '',
                        otp: '',
                        $createdAt: '',
                        $updatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a mock phone by its unique number. This endpoint removes the mock phone and its OTP configuration from the project.
     *
     * @param {string} params.number - Phone number associated with the mock phone. Must be a valid E.164 formatted phone number.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteMockPhone(params: { number: string }): Promise<{}>;
    /**
     * Delete a mock phone by its unique number. This endpoint removes the mock phone and its OTP configuration from the project.
     *
     * @param {string} number - Phone number associated with the mock phone. Must be a valid E.164 formatted phone number.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteMockPhone(number: string): Promise<{}>;
    deleteMockPhone(paramsOrFirst: { number: string } | string): Promise<{}> {
        let params: { number: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { number: string };
        } else {
            params = {
                number: paramsOrFirst as string,
            };
        }

        const number = params.number;

        if (typeof number === 'undefined' || number === '') {
            throw new AppwriteException('Missing required parameter: "number"');
        }
        const apiPath = '/project/mock-phones/{number}'.replace(
            '{number}',
            encodeURIComponent(String(number)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of all OAuth2 providers supported by the server, along with the project's configuration for each. Credential fields are write-only and always returned empty.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2ProviderList>}
     */
    listOAuth2Providers(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.OAuth2ProviderList>;
    /**
     * Get a list of all OAuth2 providers supported by the server, along with the project's configuration for each. Credential fields are write-only and always returned empty.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2ProviderList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listOAuth2Providers(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.OAuth2ProviderList>;
    listOAuth2Providers(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.OAuth2ProviderList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/project/oauth2';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2ProviderList',
            models: {
                oAuth2ProviderList: {
                    defaults: {
                        total: 0,
                        providers: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the OAuth2 server (OIDC provider) configuration.
     *
     * @param {boolean} params.enabled - Enable or disable the OAuth2 server.
     * @param {string} params.authorizationUrl - URL to your application with consent screen.
     * @param {string[]} params.scopes - List of allowed OAuth2 scopes. Maximum of 100 scopes are allowed, each up to 128 characters long.
     * @param {string[]} params.authorizationDetailsTypes - List of accepted `authorization_details` types. Maximum of 100 types are allowed, each up to 128 characters long.
     * @param {number | null} params.accessTokenDuration - Access token duration in seconds for confidential clients (server-side apps that authenticate with a client secret). Leave empty to use default 8 hours.
     * @param {number | null} params.refreshTokenDuration - Refresh token duration in seconds for confidential clients (server-side apps that authenticate with a client secret). Leave empty to use default 1 year.
     * @param {number | null} params.publicAccessTokenDuration - Access token duration in seconds for public clients (SPAs, mobile, and native apps that cannot keep a client secret). Leave empty to use default 1 hour.
     * @param {number | null} params.publicRefreshTokenDuration - Refresh token duration in seconds for public clients (SPAs, mobile, and native apps that cannot keep a client secret). Leave empty to use default 30 days.
     * @param {number | null} params.installationAccessTokenDuration - Access token duration in seconds for app installation access tokens. Leave empty to use default 1 hour.
     * @param {boolean | null} params.confidentialPkce - When enabled, PKCE is required for confidential clients (server-side flows using client_secret). PKCE is always required for public clients regardless of this setting.
     * @param {string} params.verificationUrl - URL to your application page where users enter the device flow user code. Required to enable the Device Authorization Grant.
     * @param {number | null} params.userCodeLength - Number of characters in the device flow user code, excluding the formatting separator. Shorter codes are easier to type but weaker; pair short codes with short expiry. Leave empty to use default 8.
     * @param {string} params.userCodeFormat - Character set for device flow user codes: `numeric` (digits only — best for numeric keypads and TV remotes), `alphabetic` (letters only), or `alphanumeric` (letters and digits — highest entropy per character). Defaults to `alphanumeric`.
     * @param {number | null} params.deviceCodeDuration - Lifetime in seconds of device flow device codes and user codes. Device codes are intentionally short-lived. Leave empty to use default 600.
     * @param {string[]} params.defaultScopes - List of OAuth2 scopes used when an authorization request omits the scope parameter. Every default scope must also be allowed by the OAuth2 server. Maximum of 100 scopes are allowed, each up to 128 characters long.
     * @param {string[]} params.installationScopes - List of scopes an application may request when installed on a team. Omitting the parameter clears the list, so no installation scopes can be granted. Maximum of 100 scopes are allowed, each up to 128 characters long.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateOAuth2Server(params: {
        enabled: boolean;
        authorizationUrl: string;
        scopes?: string[];
        authorizationDetailsTypes?: string[];
        accessTokenDuration?: number | null;
        refreshTokenDuration?: number | null;
        publicAccessTokenDuration?: number | null;
        publicRefreshTokenDuration?: number | null;
        installationAccessTokenDuration?: number | null;
        confidentialPkce?: boolean | null;
        verificationUrl?: string;
        userCodeLength?: number | null;
        userCodeFormat?: string;
        deviceCodeDuration?: number | null;
        defaultScopes?: string[];
        installationScopes?: string[];
    }): Promise<Models.Project>;
    /**
     * Update the OAuth2 server (OIDC provider) configuration.
     *
     * @param {boolean} enabled - Enable or disable the OAuth2 server.
     * @param {string} authorizationUrl - URL to your application with consent screen.
     * @param {string[]} scopes - List of allowed OAuth2 scopes. Maximum of 100 scopes are allowed, each up to 128 characters long.
     * @param {string[]} authorizationDetailsTypes - List of accepted `authorization_details` types. Maximum of 100 types are allowed, each up to 128 characters long.
     * @param {number | null} accessTokenDuration - Access token duration in seconds for confidential clients (server-side apps that authenticate with a client secret). Leave empty to use default 8 hours.
     * @param {number | null} refreshTokenDuration - Refresh token duration in seconds for confidential clients (server-side apps that authenticate with a client secret). Leave empty to use default 1 year.
     * @param {number | null} publicAccessTokenDuration - Access token duration in seconds for public clients (SPAs, mobile, and native apps that cannot keep a client secret). Leave empty to use default 1 hour.
     * @param {number | null} publicRefreshTokenDuration - Refresh token duration in seconds for public clients (SPAs, mobile, and native apps that cannot keep a client secret). Leave empty to use default 30 days.
     * @param {number | null} installationAccessTokenDuration - Access token duration in seconds for app installation access tokens. Leave empty to use default 1 hour.
     * @param {boolean | null} confidentialPkce - When enabled, PKCE is required for confidential clients (server-side flows using client_secret). PKCE is always required for public clients regardless of this setting.
     * @param {string} verificationUrl - URL to your application page where users enter the device flow user code. Required to enable the Device Authorization Grant.
     * @param {number | null} userCodeLength - Number of characters in the device flow user code, excluding the formatting separator. Shorter codes are easier to type but weaker; pair short codes with short expiry. Leave empty to use default 8.
     * @param {string} userCodeFormat - Character set for device flow user codes: `numeric` (digits only — best for numeric keypads and TV remotes), `alphabetic` (letters only), or `alphanumeric` (letters and digits — highest entropy per character). Defaults to `alphanumeric`.
     * @param {number | null} deviceCodeDuration - Lifetime in seconds of device flow device codes and user codes. Device codes are intentionally short-lived. Leave empty to use default 600.
     * @param {string[]} defaultScopes - List of OAuth2 scopes used when an authorization request omits the scope parameter. Every default scope must also be allowed by the OAuth2 server. Maximum of 100 scopes are allowed, each up to 128 characters long.
     * @param {string[]} installationScopes - List of scopes an application may request when installed on a team. Omitting the parameter clears the list, so no installation scopes can be granted. Maximum of 100 scopes are allowed, each up to 128 characters long.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Server(
        enabled: boolean,
        authorizationUrl: string,
        scopes?: string[],
        authorizationDetailsTypes?: string[],
        accessTokenDuration?: number | null,
        refreshTokenDuration?: number | null,
        publicAccessTokenDuration?: number | null,
        publicRefreshTokenDuration?: number | null,
        installationAccessTokenDuration?: number | null,
        confidentialPkce?: boolean | null,
        verificationUrl?: string,
        userCodeLength?: number | null,
        userCodeFormat?: string,
        deviceCodeDuration?: number | null,
        defaultScopes?: string[],
        installationScopes?: string[],
    ): Promise<Models.Project>;
    updateOAuth2Server(
        paramsOrFirst:
            | {
                  enabled: boolean;
                  authorizationUrl: string;
                  scopes?: string[];
                  authorizationDetailsTypes?: string[];
                  accessTokenDuration?: number | null;
                  refreshTokenDuration?: number | null;
                  publicAccessTokenDuration?: number | null;
                  publicRefreshTokenDuration?: number | null;
                  installationAccessTokenDuration?: number | null;
                  confidentialPkce?: boolean | null;
                  verificationUrl?: string;
                  userCodeLength?: number | null;
                  userCodeFormat?: string;
                  deviceCodeDuration?: number | null;
                  defaultScopes?: string[];
                  installationScopes?: string[];
              }
            | boolean,
        ...rest: [
            string?,
            string[]?,
            string[]?,
            (number | null)?,
            (number | null)?,
            (number | null)?,
            (number | null)?,
            (number | null)?,
            (boolean | null)?,
            string?,
            (number | null)?,
            string?,
            (number | null)?,
            string[]?,
            string[]?,
        ]
    ): Promise<Models.Project> {
        let params: {
            enabled: boolean;
            authorizationUrl: string;
            scopes?: string[];
            authorizationDetailsTypes?: string[];
            accessTokenDuration?: number | null;
            refreshTokenDuration?: number | null;
            publicAccessTokenDuration?: number | null;
            publicRefreshTokenDuration?: number | null;
            installationAccessTokenDuration?: number | null;
            confidentialPkce?: boolean | null;
            verificationUrl?: string;
            userCodeLength?: number | null;
            userCodeFormat?: string;
            deviceCodeDuration?: number | null;
            defaultScopes?: string[];
            installationScopes?: string[];
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                enabled: boolean;
                authorizationUrl: string;
                scopes?: string[];
                authorizationDetailsTypes?: string[];
                accessTokenDuration?: number | null;
                refreshTokenDuration?: number | null;
                publicAccessTokenDuration?: number | null;
                publicRefreshTokenDuration?: number | null;
                installationAccessTokenDuration?: number | null;
                confidentialPkce?: boolean | null;
                verificationUrl?: string;
                userCodeLength?: number | null;
                userCodeFormat?: string;
                deviceCodeDuration?: number | null;
                defaultScopes?: string[];
                installationScopes?: string[];
            };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
                authorizationUrl: rest[0] as string,
                scopes: rest[1] as string[],
                authorizationDetailsTypes: rest[2] as string[],
                accessTokenDuration: rest[3] as number | null,
                refreshTokenDuration: rest[4] as number | null,
                publicAccessTokenDuration: rest[5] as number | null,
                publicRefreshTokenDuration: rest[6] as number | null,
                installationAccessTokenDuration: rest[7] as number | null,
                confidentialPkce: rest[8] as boolean | null,
                verificationUrl: rest[9] as string,
                userCodeLength: rest[10] as number | null,
                userCodeFormat: rest[11] as string,
                deviceCodeDuration: rest[12] as number | null,
                defaultScopes: rest[13] as string[],
                installationScopes: rest[14] as string[],
            };
        }

        const enabled = params.enabled;
        const authorizationUrl = params.authorizationUrl;
        const scopes = params.scopes;
        const authorizationDetailsTypes = params.authorizationDetailsTypes;
        const accessTokenDuration = params.accessTokenDuration;
        const refreshTokenDuration = params.refreshTokenDuration;
        const publicAccessTokenDuration = params.publicAccessTokenDuration;
        const publicRefreshTokenDuration = params.publicRefreshTokenDuration;
        const installationAccessTokenDuration =
            params.installationAccessTokenDuration;
        const confidentialPkce = params.confidentialPkce;
        const verificationUrl = params.verificationUrl;
        const userCodeLength = params.userCodeLength;
        const userCodeFormat = params.userCodeFormat;
        const deviceCodeDuration = params.deviceCodeDuration;
        const defaultScopes = params.defaultScopes;
        const installationScopes = params.installationScopes;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        if (typeof authorizationUrl === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "authorizationUrl"',
            );
        }
        const apiPath = '/project/oauth2-server';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof authorizationUrl !== 'undefined') {
            apiPayload['authorizationUrl'] = authorizationUrl;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof authorizationDetailsTypes !== 'undefined') {
            apiPayload['authorizationDetailsTypes'] = authorizationDetailsTypes;
        }
        if (typeof accessTokenDuration !== 'undefined') {
            apiPayload['accessTokenDuration'] = accessTokenDuration;
        }
        if (typeof refreshTokenDuration !== 'undefined') {
            apiPayload['refreshTokenDuration'] = refreshTokenDuration;
        }
        if (typeof publicAccessTokenDuration !== 'undefined') {
            apiPayload['publicAccessTokenDuration'] = publicAccessTokenDuration;
        }
        if (typeof publicRefreshTokenDuration !== 'undefined') {
            apiPayload['publicRefreshTokenDuration'] =
                publicRefreshTokenDuration;
        }
        if (typeof installationAccessTokenDuration !== 'undefined') {
            apiPayload['installationAccessTokenDuration'] =
                installationAccessTokenDuration;
        }
        if (typeof confidentialPkce !== 'undefined') {
            apiPayload['confidentialPkce'] = confidentialPkce;
        }
        if (typeof verificationUrl !== 'undefined') {
            apiPayload['verificationUrl'] = verificationUrl;
        }
        if (typeof userCodeLength !== 'undefined') {
            apiPayload['userCodeLength'] = userCodeLength;
        }
        if (typeof userCodeFormat !== 'undefined') {
            apiPayload['userCodeFormat'] = userCodeFormat;
        }
        if (typeof deviceCodeDuration !== 'undefined') {
            apiPayload['deviceCodeDuration'] = deviceCodeDuration;
        }
        if (typeof defaultScopes !== 'undefined') {
            apiPayload['defaultScopes'] = defaultScopes;
        }
        if (typeof installationScopes !== 'undefined') {
            apiPayload['installationScopes'] = installationScopes;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Amazon configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Amazon OAuth2 app. For example: amzn1.application-oa2-client.87400c00000000000000000000063d5b2
     * @param {string | null} params.clientSecret - 'Client Secret' of Amazon OAuth2 app. For example: 79ffe4000000000000000000000000000000000000000000000000000002de55
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Amazon>}
     */
    updateOAuth2Amazon(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Amazon>;
    /**
     * Update the project OAuth2 Amazon configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Amazon OAuth2 app. For example: amzn1.application-oa2-client.87400c00000000000000000000063d5b2
     * @param {string | null} clientSecret - 'Client Secret' of Amazon OAuth2 app. For example: 79ffe4000000000000000000000000000000000000000000000000000002de55
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Amazon>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Amazon(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Amazon>;
    updateOAuth2Amazon(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Amazon> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/amazon';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Amazon',
            models: {
                oAuth2Amazon: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Apple configuration.
     *
     * @param {string | null} params.serviceId - 'Service ID' of Apple OAuth2 app. For example: ip.appwrite.app.web
     * @param {string | null} params.keyId - 'Key ID' of Apple OAuth2 app. For example: P4000000N8
     * @param {string | null} params.teamId - 'Team ID' of Apple OAuth2 app. For example: D4000000R6
     * @param {string | null} params.p8File - Contents of the Apple OAuth2 app .p8 private key file. The secret key wrapped by the PEM markers is 200 characters long. For example: -----BEGIN PRIVATE KEY-----MIGTAg...jy2Xbna-----END PRIVATE KEY-----
     * @param {boolean | null} params.enabled - Browser-based OAuth2 sign-in status. Set to true to enable new session creation through the redirect flow. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid. Has no effect on native sign-in, which is controlled by nativeEnabled only.
     * @param {string[] | null} params.nativeClientIds - App bundle IDs accepted as ID token audiences for native Sign in with Apple. For example: com.example.app. Together with the Services ID, these are the only client IDs whose tokens are trusted. Pass an empty array to clear the list.
     * @param {boolean | null} params.nativeEnabled - Native Sign in with Apple status. This is the only switch for creating sessions from ID tokens obtained on device and is independent of enabled. Needs a Services ID or at least one native client ID to match tokens against, but no key or team ID: this method verifies a signature rather than redeeming an authorization code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Apple>}
     */
    updateOAuth2Apple(params?: {
        serviceId?: string | null;
        keyId?: string | null;
        teamId?: string | null;
        p8File?: string | null;
        enabled?: boolean | null;
        nativeClientIds?: string[] | null;
        nativeEnabled?: boolean | null;
    }): Promise<Models.OAuth2Apple>;
    /**
     * Update the project OAuth2 Apple configuration.
     *
     * @param {string | null} serviceId - 'Service ID' of Apple OAuth2 app. For example: ip.appwrite.app.web
     * @param {string | null} keyId - 'Key ID' of Apple OAuth2 app. For example: P4000000N8
     * @param {string | null} teamId - 'Team ID' of Apple OAuth2 app. For example: D4000000R6
     * @param {string | null} p8File - Contents of the Apple OAuth2 app .p8 private key file. The secret key wrapped by the PEM markers is 200 characters long. For example: -----BEGIN PRIVATE KEY-----MIGTAg...jy2Xbna-----END PRIVATE KEY-----
     * @param {boolean | null} enabled - Browser-based OAuth2 sign-in status. Set to true to enable new session creation through the redirect flow. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid. Has no effect on native sign-in, which is controlled by nativeEnabled only.
     * @param {string[] | null} nativeClientIds - App bundle IDs accepted as ID token audiences for native Sign in with Apple. For example: com.example.app. Together with the Services ID, these are the only client IDs whose tokens are trusted. Pass an empty array to clear the list.
     * @param {boolean | null} nativeEnabled - Native Sign in with Apple status. This is the only switch for creating sessions from ID tokens obtained on device and is independent of enabled. Needs a Services ID or at least one native client ID to match tokens against, but no key or team ID: this method verifies a signature rather than redeeming an authorization code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Apple>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Apple(
        serviceId?: string | null,
        keyId?: string | null,
        teamId?: string | null,
        p8File?: string | null,
        enabled?: boolean | null,
        nativeClientIds?: string[] | null,
        nativeEnabled?: boolean | null,
    ): Promise<Models.OAuth2Apple>;
    updateOAuth2Apple(
        paramsOrFirst?:
            | {
                  serviceId?: string | null;
                  keyId?: string | null;
                  teamId?: string | null;
                  p8File?: string | null;
                  enabled?: boolean | null;
                  nativeClientIds?: string[] | null;
                  nativeEnabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (boolean | null)?,
            (string[] | null)?,
            (boolean | null)?,
        ]
    ): Promise<Models.OAuth2Apple> {
        let params: {
            serviceId?: string | null;
            keyId?: string | null;
            teamId?: string | null;
            p8File?: string | null;
            enabled?: boolean | null;
            nativeClientIds?: string[] | null;
            nativeEnabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                serviceId?: string | null;
                keyId?: string | null;
                teamId?: string | null;
                p8File?: string | null;
                enabled?: boolean | null;
                nativeClientIds?: string[] | null;
                nativeEnabled?: boolean | null;
            };
        } else {
            params = {
                serviceId: paramsOrFirst as string | null,
                keyId: rest[0] as string | null,
                teamId: rest[1] as string | null,
                p8File: rest[2] as string | null,
                enabled: rest[3] as boolean | null,
                nativeClientIds: rest[4] as string[] | null,
                nativeEnabled: rest[5] as boolean | null,
            };
        }

        const serviceId = params.serviceId;
        const keyId = params.keyId;
        const teamId = params.teamId;
        const p8File = params.p8File;
        const enabled = params.enabled;
        const nativeClientIds = params.nativeClientIds;
        const nativeEnabled = params.nativeEnabled;

        const apiPath = '/project/oauth2/apple';
        const apiPayload: Payload = {};
        if (typeof serviceId !== 'undefined') {
            apiPayload['serviceId'] = serviceId;
        }
        if (typeof keyId !== 'undefined') {
            apiPayload['keyId'] = keyId;
        }
        if (typeof teamId !== 'undefined') {
            apiPayload['teamId'] = teamId;
        }
        if (typeof p8File !== 'undefined') {
            apiPayload['p8File'] = p8File;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof nativeClientIds !== 'undefined') {
            apiPayload['nativeClientIds'] = nativeClientIds;
        }
        if (typeof nativeEnabled !== 'undefined') {
            apiPayload['nativeEnabled'] = nativeEnabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Apple',
            models: {
                oAuth2Apple: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        serviceId: '',
                        keyId: '',
                        teamId: '',
                        p8File: '',
                        nativeEnabled: false,
                        nativeClientIds: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Appwrite configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Appwrite OAuth2 app. For example: 6a42000000000000b5a0
     * @param {string | null} params.clientSecret - 'Client Secret' of Appwrite OAuth2 app. For example: b86afd000000000000000000000000000000000000000000000000000ced5f93
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Appwrite>}
     */
    updateOAuth2Appwrite(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Appwrite>;
    /**
     * Update the project OAuth2 Appwrite configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Appwrite OAuth2 app. For example: 6a42000000000000b5a0
     * @param {string | null} clientSecret - 'Client Secret' of Appwrite OAuth2 app. For example: b86afd000000000000000000000000000000000000000000000000000ced5f93
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Appwrite>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Appwrite(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Appwrite>;
    updateOAuth2Appwrite(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Appwrite> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/appwrite';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Appwrite',
            models: {
                oAuth2Appwrite: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Auth0 configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Auth0 OAuth2 app. For example: OaOkIA000000000000000000005KLSYq
     * @param {string | null} params.clientSecret - 'Client Secret' of Auth0 OAuth2 app. For example: zXz0000-00000000000000000000000000000-00000000000000000000PJafnF
     * @param {string | null} params.endpoint - Domain of Auth0 instance. For example: example.us.auth0.com
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2Auth0Prompt[] | null} params.prompt - Array of Auth0 OAuth2 prompt values. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. Pass an empty array to use the Auth0 default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Auth0>}
     */
    updateOAuth2Auth0(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        endpoint?: string | null;
        enabled?: boolean | null;
        prompt?: ProjectOAuth2Auth0Prompt[] | null;
    }): Promise<Models.OAuth2Auth0>;
    /**
     * Update the project OAuth2 Auth0 configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Auth0 OAuth2 app. For example: OaOkIA000000000000000000005KLSYq
     * @param {string | null} clientSecret - 'Client Secret' of Auth0 OAuth2 app. For example: zXz0000-00000000000000000000000000000-00000000000000000000PJafnF
     * @param {string | null} endpoint - Domain of Auth0 instance. For example: example.us.auth0.com
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2Auth0Prompt[] | null} prompt - Array of Auth0 OAuth2 prompt values. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. Pass an empty array to use the Auth0 default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Auth0>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Auth0(
        clientId?: string | null,
        clientSecret?: string | null,
        endpoint?: string | null,
        enabled?: boolean | null,
        prompt?: ProjectOAuth2Auth0Prompt[] | null,
    ): Promise<Models.OAuth2Auth0>;
    updateOAuth2Auth0(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  endpoint?: string | null;
                  enabled?: boolean | null;
                  prompt?: ProjectOAuth2Auth0Prompt[] | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (string | null)?,
            (boolean | null)?,
            (ProjectOAuth2Auth0Prompt[] | null)?,
        ]
    ): Promise<Models.OAuth2Auth0> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            endpoint?: string | null;
            enabled?: boolean | null;
            prompt?: ProjectOAuth2Auth0Prompt[] | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                endpoint?: string | null;
                enabled?: boolean | null;
                prompt?: ProjectOAuth2Auth0Prompt[] | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                endpoint: rest[1] as string | null,
                enabled: rest[2] as boolean | null,
                prompt: rest[3] as ProjectOAuth2Auth0Prompt[] | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const endpoint = params.endpoint;
        const enabled = params.enabled;
        const prompt = params.prompt;

        const apiPath = '/project/oauth2/auth0';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof endpoint !== 'undefined') {
            apiPayload['endpoint'] = endpoint;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Auth0',
            models: {
                oAuth2Auth0: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        prompt: [],
                        endpoint: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Authentik configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Authentik OAuth2 app. For example: dTKOPa0000000000000000000000000000e7G8hv
     * @param {string | null} params.clientSecret - 'Client Secret' of Authentik OAuth2 app. For example: ntQadq000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000Hp5WK
     * @param {string | null} params.endpoint - Domain of Authentik instance. For example: example.authentik.com
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Authentik>}
     */
    updateOAuth2Authentik(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        endpoint?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Authentik>;
    /**
     * Update the project OAuth2 Authentik configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Authentik OAuth2 app. For example: dTKOPa0000000000000000000000000000e7G8hv
     * @param {string | null} clientSecret - 'Client Secret' of Authentik OAuth2 app. For example: ntQadq000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000Hp5WK
     * @param {string | null} endpoint - Domain of Authentik instance. For example: example.authentik.com
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Authentik>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Authentik(
        clientId?: string | null,
        clientSecret?: string | null,
        endpoint?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Authentik>;
    updateOAuth2Authentik(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  endpoint?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Authentik> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            endpoint?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                endpoint?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                endpoint: rest[1] as string | null,
                enabled: rest[2] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const endpoint = params.endpoint;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/authentik';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof endpoint !== 'undefined') {
            apiPayload['endpoint'] = endpoint;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Authentik',
            models: {
                oAuth2Authentik: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        endpoint: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Autodesk configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Autodesk OAuth2 app. For example: 5zw90v00000000000000000000kVYXN7
     * @param {string | null} params.clientSecret - 'Client Secret' of Autodesk OAuth2 app. For example: 7I000000000000MW
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Autodesk>}
     */
    updateOAuth2Autodesk(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Autodesk>;
    /**
     * Update the project OAuth2 Autodesk configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Autodesk OAuth2 app. For example: 5zw90v00000000000000000000kVYXN7
     * @param {string | null} clientSecret - 'Client Secret' of Autodesk OAuth2 app. For example: 7I000000000000MW
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Autodesk>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Autodesk(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Autodesk>;
    updateOAuth2Autodesk(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Autodesk> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/autodesk';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Autodesk',
            models: {
                oAuth2Autodesk: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Bitbucket configuration.
     *
     * @param {string | null} params.key - 'Key' of Bitbucket OAuth2 app. For example: Knt70000000000ByRc
     * @param {string | null} params.secret - 'Secret' of Bitbucket OAuth2 app. For example: NMfLZJ00000000000000000000TLQdDx
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Bitbucket>}
     */
    updateOAuth2Bitbucket(params?: {
        key?: string | null;
        secret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Bitbucket>;
    /**
     * Update the project OAuth2 Bitbucket configuration.
     *
     * @param {string | null} key - 'Key' of Bitbucket OAuth2 app. For example: Knt70000000000ByRc
     * @param {string | null} secret - 'Secret' of Bitbucket OAuth2 app. For example: NMfLZJ00000000000000000000TLQdDx
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Bitbucket>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Bitbucket(
        key?: string | null,
        secret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Bitbucket>;
    updateOAuth2Bitbucket(
        paramsOrFirst?:
            | {
                  key?: string | null;
                  secret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Bitbucket> {
        let params: {
            key?: string | null;
            secret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                key?: string | null;
                secret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                key: paramsOrFirst as string | null,
                secret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const key = params.key;
        const secret = params.secret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/bitbucket';
        const apiPayload: Payload = {};
        if (typeof key !== 'undefined') {
            apiPayload['key'] = key;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Bitbucket',
            models: {
                oAuth2Bitbucket: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        key: '',
                        secret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Bitly configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Bitly OAuth2 app. For example: d95151000000000000000000000000000067af9b
     * @param {string | null} params.clientSecret - 'Client Secret' of Bitly OAuth2 app. For example: a13e250000000000000000000000000000d73095
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Bitly>}
     */
    updateOAuth2Bitly(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Bitly>;
    /**
     * Update the project OAuth2 Bitly configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Bitly OAuth2 app. For example: d95151000000000000000000000000000067af9b
     * @param {string | null} clientSecret - 'Client Secret' of Bitly OAuth2 app. For example: a13e250000000000000000000000000000d73095
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Bitly>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Bitly(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Bitly>;
    updateOAuth2Bitly(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Bitly> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/bitly';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Bitly',
            models: {
                oAuth2Bitly: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Box configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Box OAuth2 app. For example: deglcs00000000000000000000x2og6y
     * @param {string | null} params.clientSecret - 'Client Secret' of Box OAuth2 app. For example: OKM1f100000000000000000000eshEif
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Box>}
     */
    updateOAuth2Box(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Box>;
    /**
     * Update the project OAuth2 Box configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Box OAuth2 app. For example: deglcs00000000000000000000x2og6y
     * @param {string | null} clientSecret - 'Client Secret' of Box OAuth2 app. For example: OKM1f100000000000000000000eshEif
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Box>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Box(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Box>;
    updateOAuth2Box(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Box> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/box';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Box',
            models: {
                oAuth2Box: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Cloudflare configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Cloudflare OAuth2 app. For example: 4b866000000000000000000000c9e4e2
     * @param {string | null} params.clientSecret - 'Client Secret' of Cloudflare OAuth2 app. For example: cfoc_5Q6YRl0000000000000000000000000000000000003d214f
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Cloudflare>}
     */
    updateOAuth2Cloudflare(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Cloudflare>;
    /**
     * Update the project OAuth2 Cloudflare configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Cloudflare OAuth2 app. For example: 4b866000000000000000000000c9e4e2
     * @param {string | null} clientSecret - 'Client Secret' of Cloudflare OAuth2 app. For example: cfoc_5Q6YRl0000000000000000000000000000000000003d214f
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Cloudflare>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Cloudflare(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Cloudflare>;
    updateOAuth2Cloudflare(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Cloudflare> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/cloudflare';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Cloudflare',
            models: {
                oAuth2Cloudflare: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Dailymotion configuration.
     *
     * @param {string | null} params.apiKey - 'API Key' of Dailymotion OAuth2 app. For example: 07a9000000000000067f
     * @param {string | null} params.apiSecret - 'API Secret' of Dailymotion OAuth2 app. For example: a399a90000000000000000000000000000d90639
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Dailymotion>}
     */
    updateOAuth2Dailymotion(params?: {
        apiKey?: string | null;
        apiSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Dailymotion>;
    /**
     * Update the project OAuth2 Dailymotion configuration.
     *
     * @param {string | null} apiKey - 'API Key' of Dailymotion OAuth2 app. For example: 07a9000000000000067f
     * @param {string | null} apiSecret - 'API Secret' of Dailymotion OAuth2 app. For example: a399a90000000000000000000000000000d90639
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Dailymotion>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Dailymotion(
        apiKey?: string | null,
        apiSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Dailymotion>;
    updateOAuth2Dailymotion(
        paramsOrFirst?:
            | {
                  apiKey?: string | null;
                  apiSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Dailymotion> {
        let params: {
            apiKey?: string | null;
            apiSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                apiKey?: string | null;
                apiSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                apiKey: paramsOrFirst as string | null,
                apiSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const apiKey = params.apiKey;
        const apiSecret = params.apiSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/dailymotion';
        const apiPayload: Payload = {};
        if (typeof apiKey !== 'undefined') {
            apiPayload['apiKey'] = apiKey;
        }
        if (typeof apiSecret !== 'undefined') {
            apiPayload['apiSecret'] = apiSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Dailymotion',
            models: {
                oAuth2Dailymotion: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        apiKey: '',
                        apiSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Discord configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Discord OAuth2 app. For example: 950722000000343754
     * @param {string | null} params.clientSecret - 'Client Secret' of Discord OAuth2 app. For example: YmPXnM000000000000000000002zFg5D
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2DiscordPrompt[] | null} params.prompt - Array with at most one Discord OAuth2 prompt value. "none" means: skip the authorization screen for users who already authorized the app with the requested scopes. "consent" means: ask users who already authorized the app to approve it again. Pass an empty array to use the Discord default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Discord>}
     */
    updateOAuth2Discord(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
        prompt?: ProjectOAuth2DiscordPrompt[] | null;
    }): Promise<Models.OAuth2Discord>;
    /**
     * Update the project OAuth2 Discord configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Discord OAuth2 app. For example: 950722000000343754
     * @param {string | null} clientSecret - 'Client Secret' of Discord OAuth2 app. For example: YmPXnM000000000000000000002zFg5D
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2DiscordPrompt[] | null} prompt - Array with at most one Discord OAuth2 prompt value. "none" means: skip the authorization screen for users who already authorized the app with the requested scopes. "consent" means: ask users who already authorized the app to approve it again. Pass an empty array to use the Discord default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Discord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Discord(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
        prompt?: ProjectOAuth2DiscordPrompt[] | null,
    ): Promise<Models.OAuth2Discord>;
    updateOAuth2Discord(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
                  prompt?: ProjectOAuth2DiscordPrompt[] | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (boolean | null)?,
            (ProjectOAuth2DiscordPrompt[] | null)?,
        ]
    ): Promise<Models.OAuth2Discord> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
            prompt?: ProjectOAuth2DiscordPrompt[] | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
                prompt?: ProjectOAuth2DiscordPrompt[] | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
                prompt: rest[2] as ProjectOAuth2DiscordPrompt[] | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;
        const prompt = params.prompt;

        const apiPath = '/project/oauth2/discord';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Discord',
            models: {
                oAuth2Discord: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        prompt: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Disqus configuration.
     *
     * @param {string | null} params.publicKey - 'Public Key, also known as API Key' of Disqus OAuth2 app. For example: cgegH70000000000000000000000000000000000000000000000000000Hr1nYX
     * @param {string | null} params.secretKey - 'Secret Key, also known as API Secret' of Disqus OAuth2 app. For example: W7Bykj00000000000000000000000000000000000000000000000000003o43w9
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Disqus>}
     */
    updateOAuth2Disqus(params?: {
        publicKey?: string | null;
        secretKey?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Disqus>;
    /**
     * Update the project OAuth2 Disqus configuration.
     *
     * @param {string | null} publicKey - 'Public Key, also known as API Key' of Disqus OAuth2 app. For example: cgegH70000000000000000000000000000000000000000000000000000Hr1nYX
     * @param {string | null} secretKey - 'Secret Key, also known as API Secret' of Disqus OAuth2 app. For example: W7Bykj00000000000000000000000000000000000000000000000000003o43w9
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Disqus>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Disqus(
        publicKey?: string | null,
        secretKey?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Disqus>;
    updateOAuth2Disqus(
        paramsOrFirst?:
            | {
                  publicKey?: string | null;
                  secretKey?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Disqus> {
        let params: {
            publicKey?: string | null;
            secretKey?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                publicKey?: string | null;
                secretKey?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                publicKey: paramsOrFirst as string | null,
                secretKey: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const publicKey = params.publicKey;
        const secretKey = params.secretKey;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/disqus';
        const apiPayload: Payload = {};
        if (typeof publicKey !== 'undefined') {
            apiPayload['publicKey'] = publicKey;
        }
        if (typeof secretKey !== 'undefined') {
            apiPayload['secretKey'] = secretKey;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Disqus',
            models: {
                oAuth2Disqus: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        publicKey: '',
                        secretKey: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Dropbox configuration.
     *
     * @param {string | null} params.appKey - 'App Key' of Dropbox OAuth2 app. For example: jl000000000009t
     * @param {string | null} params.appSecret - 'App Secret' of Dropbox OAuth2 app. For example: g200000000000vw
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Dropbox>}
     */
    updateOAuth2Dropbox(params?: {
        appKey?: string | null;
        appSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Dropbox>;
    /**
     * Update the project OAuth2 Dropbox configuration.
     *
     * @param {string | null} appKey - 'App Key' of Dropbox OAuth2 app. For example: jl000000000009t
     * @param {string | null} appSecret - 'App Secret' of Dropbox OAuth2 app. For example: g200000000000vw
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Dropbox>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Dropbox(
        appKey?: string | null,
        appSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Dropbox>;
    updateOAuth2Dropbox(
        paramsOrFirst?:
            | {
                  appKey?: string | null;
                  appSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Dropbox> {
        let params: {
            appKey?: string | null;
            appSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                appKey?: string | null;
                appSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                appKey: paramsOrFirst as string | null,
                appSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const appKey = params.appKey;
        const appSecret = params.appSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/dropbox';
        const apiPayload: Payload = {};
        if (typeof appKey !== 'undefined') {
            apiPayload['appKey'] = appKey;
        }
        if (typeof appSecret !== 'undefined') {
            apiPayload['appSecret'] = appSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Dropbox',
            models: {
                oAuth2Dropbox: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        appKey: '',
                        appSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Etsy configuration.
     *
     * @param {string | null} params.keyString - 'Keystring' of Etsy OAuth2 app. For example: nsgzxh0000000000008j85a2
     * @param {string | null} params.sharedSecret - 'Shared Secret' of Etsy OAuth2 app. For example: tp000000ru
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Etsy>}
     */
    updateOAuth2Etsy(params?: {
        keyString?: string | null;
        sharedSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Etsy>;
    /**
     * Update the project OAuth2 Etsy configuration.
     *
     * @param {string | null} keyString - 'Keystring' of Etsy OAuth2 app. For example: nsgzxh0000000000008j85a2
     * @param {string | null} sharedSecret - 'Shared Secret' of Etsy OAuth2 app. For example: tp000000ru
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Etsy>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Etsy(
        keyString?: string | null,
        sharedSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Etsy>;
    updateOAuth2Etsy(
        paramsOrFirst?:
            | {
                  keyString?: string | null;
                  sharedSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Etsy> {
        let params: {
            keyString?: string | null;
            sharedSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                keyString?: string | null;
                sharedSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                keyString: paramsOrFirst as string | null,
                sharedSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const keyString = params.keyString;
        const sharedSecret = params.sharedSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/etsy';
        const apiPayload: Payload = {};
        if (typeof keyString !== 'undefined') {
            apiPayload['keyString'] = keyString;
        }
        if (typeof sharedSecret !== 'undefined') {
            apiPayload['sharedSecret'] = sharedSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Etsy',
            models: {
                oAuth2Etsy: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        keyString: '',
                        sharedSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Facebook configuration.
     *
     * @param {string | null} params.appId - 'App ID' of Facebook OAuth2 app. For example: 260600000007694
     * @param {string | null} params.appSecret - 'App Secret' of Facebook OAuth2 app. For example: 2d0b2800000000000000000000d38af4
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Facebook>}
     */
    updateOAuth2Facebook(params?: {
        appId?: string | null;
        appSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Facebook>;
    /**
     * Update the project OAuth2 Facebook configuration.
     *
     * @param {string | null} appId - 'App ID' of Facebook OAuth2 app. For example: 260600000007694
     * @param {string | null} appSecret - 'App Secret' of Facebook OAuth2 app. For example: 2d0b2800000000000000000000d38af4
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Facebook>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Facebook(
        appId?: string | null,
        appSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Facebook>;
    updateOAuth2Facebook(
        paramsOrFirst?:
            | {
                  appId?: string | null;
                  appSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Facebook> {
        let params: {
            appId?: string | null;
            appSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                appId?: string | null;
                appSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                appId: paramsOrFirst as string | null,
                appSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const appId = params.appId;
        const appSecret = params.appSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/facebook';
        const apiPayload: Payload = {};
        if (typeof appId !== 'undefined') {
            apiPayload['appId'] = appId;
        }
        if (typeof appSecret !== 'undefined') {
            apiPayload['appSecret'] = appSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Facebook',
            models: {
                oAuth2Facebook: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        appId: '',
                        appSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Figma configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Figma OAuth2 app. For example: byay5H0000000000VtiI40
     * @param {string | null} params.clientSecret - 'Client Secret' of Figma OAuth2 app. For example: yEpOYn0000000000000000004iIsU5
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Figma>}
     */
    updateOAuth2Figma(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Figma>;
    /**
     * Update the project OAuth2 Figma configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Figma OAuth2 app. For example: byay5H0000000000VtiI40
     * @param {string | null} clientSecret - 'Client Secret' of Figma OAuth2 app. For example: yEpOYn0000000000000000004iIsU5
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Figma>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Figma(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Figma>;
    updateOAuth2Figma(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Figma> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/figma';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Figma',
            models: {
                oAuth2Figma: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 FusionAuth configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of FusionAuth OAuth2 app. For example: b2222c00-0000-0000-0000-000000862097
     * @param {string | null} params.clientSecret - 'Client Secret' of FusionAuth OAuth2 app. For example: Jx4s0C0000000000000000000000000000000wGqLsc
     * @param {string | null} params.endpoint - Domain of FusionAuth instance. For example: example.fusionauth.io
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2FusionAuth>}
     */
    updateOAuth2FusionAuth(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        endpoint?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2FusionAuth>;
    /**
     * Update the project OAuth2 FusionAuth configuration.
     *
     * @param {string | null} clientId - 'Client ID' of FusionAuth OAuth2 app. For example: b2222c00-0000-0000-0000-000000862097
     * @param {string | null} clientSecret - 'Client Secret' of FusionAuth OAuth2 app. For example: Jx4s0C0000000000000000000000000000000wGqLsc
     * @param {string | null} endpoint - Domain of FusionAuth instance. For example: example.fusionauth.io
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2FusionAuth>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2FusionAuth(
        clientId?: string | null,
        clientSecret?: string | null,
        endpoint?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2FusionAuth>;
    updateOAuth2FusionAuth(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  endpoint?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2FusionAuth> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            endpoint?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                endpoint?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                endpoint: rest[1] as string | null,
                enabled: rest[2] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const endpoint = params.endpoint;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/fusionauth';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof endpoint !== 'undefined') {
            apiPayload['endpoint'] = endpoint;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2FusionAuth',
            models: {
                oAuth2FusionAuth: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        endpoint: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 GitHub configuration.
     *
     * @param {string | null} params.clientId - 'OAuth2 app Client ID, or App ID' of GitHub OAuth2 app. For example: e4d87900000000540733. Example of wrong value: 370006
     * @param {string | null} params.clientSecret - 'Client Secret' of GitHub OAuth2 app. For example: 5e07c00000000000000000000000000000198bcc
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2GitHubPrompt[] | null} params.prompt - Array of GitHub OAuth2 prompt values. "select_account" means: prompt the user to select an account. Pass an empty array to use the GitHub default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Github>}
     */
    updateOAuth2GitHub(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
        prompt?: ProjectOAuth2GitHubPrompt[] | null;
    }): Promise<Models.OAuth2Github>;
    /**
     * Update the project OAuth2 GitHub configuration.
     *
     * @param {string | null} clientId - 'OAuth2 app Client ID, or App ID' of GitHub OAuth2 app. For example: e4d87900000000540733. Example of wrong value: 370006
     * @param {string | null} clientSecret - 'Client Secret' of GitHub OAuth2 app. For example: 5e07c00000000000000000000000000000198bcc
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2GitHubPrompt[] | null} prompt - Array of GitHub OAuth2 prompt values. "select_account" means: prompt the user to select an account. Pass an empty array to use the GitHub default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Github>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2GitHub(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
        prompt?: ProjectOAuth2GitHubPrompt[] | null,
    ): Promise<Models.OAuth2Github>;
    updateOAuth2GitHub(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
                  prompt?: ProjectOAuth2GitHubPrompt[] | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (boolean | null)?,
            (ProjectOAuth2GitHubPrompt[] | null)?,
        ]
    ): Promise<Models.OAuth2Github> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
            prompt?: ProjectOAuth2GitHubPrompt[] | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
                prompt?: ProjectOAuth2GitHubPrompt[] | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
                prompt: rest[2] as ProjectOAuth2GitHubPrompt[] | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;
        const prompt = params.prompt;

        const apiPath = '/project/oauth2/github';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Github',
            models: {
                oAuth2Github: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        prompt: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Gitlab configuration.
     *
     * @param {string | null} params.applicationId - 'Application ID' of Gitlab OAuth2 app. For example: d41ffe0000000000000000000000000000000000000000000000000000d5e252
     * @param {string | null} params.secret - 'Secret' of Gitlab OAuth2 app. For example: gloas-838cfa0000000000000000000000000000000000000000000000000000ecbb38
     * @param {string | null} params.endpoint - Endpoint URL of self-hosted GitLab instance. For example: https://gitlab.com
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Gitlab>}
     */
    updateOAuth2Gitlab(params?: {
        applicationId?: string | null;
        secret?: string | null;
        endpoint?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Gitlab>;
    /**
     * Update the project OAuth2 Gitlab configuration.
     *
     * @param {string | null} applicationId - 'Application ID' of Gitlab OAuth2 app. For example: d41ffe0000000000000000000000000000000000000000000000000000d5e252
     * @param {string | null} secret - 'Secret' of Gitlab OAuth2 app. For example: gloas-838cfa0000000000000000000000000000000000000000000000000000ecbb38
     * @param {string | null} endpoint - Endpoint URL of self-hosted GitLab instance. For example: https://gitlab.com
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Gitlab>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Gitlab(
        applicationId?: string | null,
        secret?: string | null,
        endpoint?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Gitlab>;
    updateOAuth2Gitlab(
        paramsOrFirst?:
            | {
                  applicationId?: string | null;
                  secret?: string | null;
                  endpoint?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Gitlab> {
        let params: {
            applicationId?: string | null;
            secret?: string | null;
            endpoint?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                applicationId?: string | null;
                secret?: string | null;
                endpoint?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                applicationId: paramsOrFirst as string | null,
                secret: rest[0] as string | null,
                endpoint: rest[1] as string | null,
                enabled: rest[2] as boolean | null,
            };
        }

        const applicationId = params.applicationId;
        const secret = params.secret;
        const endpoint = params.endpoint;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/gitlab';
        const apiPayload: Payload = {};
        if (typeof applicationId !== 'undefined') {
            apiPayload['applicationId'] = applicationId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        if (typeof endpoint !== 'undefined') {
            apiPayload['endpoint'] = endpoint;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Gitlab',
            models: {
                oAuth2Gitlab: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        applicationId: '',
                        secret: '',
                        endpoint: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Google configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Google OAuth2 app. For example: 120000000095-92ifjb00000000000000000000g7ijfb.apps.googleusercontent.com
     * @param {string | null} params.clientSecret - 'Client Secret' of Google OAuth2 app. For example: GOCSPX-2k8gsR0000000000000000VNahJj
     * @param {ProjectOAuth2GooglePrompt[] | null} params.prompt - Array of Google OAuth2 prompt values. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. Must not be specified with other values. "consent" means: prompt the user for consent. "select_account" means: prompt the user to select an account.
     * @param {boolean | null} params.enabled - Browser-based OAuth2 sign-in status. Set to true to enable new session creation through the redirect flow. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid. Has no effect on native sign-in, which is controlled by nativeEnabled only.
     * @param {string[] | null} params.nativeClientIds - Additional OAuth2 client IDs accepted as ID token audiences for native sign-in (Android and iOS client IDs). Together with the client ID, which is always accepted, these are the only client IDs whose tokens are trusted. Pass an empty array to clear the list.
     * @param {boolean | null} params.nativeEnabled - Native Google sign-in status. This is the only switch for creating sessions from ID tokens obtained on device and is independent of enabled. Needs a client ID or at least one native client ID to match tokens against, but no client secret: this method verifies a signature rather than redeeming an authorization code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Google>}
     */
    updateOAuth2Google(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        prompt?: ProjectOAuth2GooglePrompt[] | null;
        enabled?: boolean | null;
        nativeClientIds?: string[] | null;
        nativeEnabled?: boolean | null;
    }): Promise<Models.OAuth2Google>;
    /**
     * Update the project OAuth2 Google configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Google OAuth2 app. For example: 120000000095-92ifjb00000000000000000000g7ijfb.apps.googleusercontent.com
     * @param {string | null} clientSecret - 'Client Secret' of Google OAuth2 app. For example: GOCSPX-2k8gsR0000000000000000VNahJj
     * @param {ProjectOAuth2GooglePrompt[] | null} prompt - Array of Google OAuth2 prompt values. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. Must not be specified with other values. "consent" means: prompt the user for consent. "select_account" means: prompt the user to select an account.
     * @param {boolean | null} enabled - Browser-based OAuth2 sign-in status. Set to true to enable new session creation through the redirect flow. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid. Has no effect on native sign-in, which is controlled by nativeEnabled only.
     * @param {string[] | null} nativeClientIds - Additional OAuth2 client IDs accepted as ID token audiences for native sign-in (Android and iOS client IDs). Together with the client ID, which is always accepted, these are the only client IDs whose tokens are trusted. Pass an empty array to clear the list.
     * @param {boolean | null} nativeEnabled - Native Google sign-in status. This is the only switch for creating sessions from ID tokens obtained on device and is independent of enabled. Needs a client ID or at least one native client ID to match tokens against, but no client secret: this method verifies a signature rather than redeeming an authorization code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Google>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Google(
        clientId?: string | null,
        clientSecret?: string | null,
        prompt?: ProjectOAuth2GooglePrompt[] | null,
        enabled?: boolean | null,
        nativeClientIds?: string[] | null,
        nativeEnabled?: boolean | null,
    ): Promise<Models.OAuth2Google>;
    updateOAuth2Google(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  prompt?: ProjectOAuth2GooglePrompt[] | null;
                  enabled?: boolean | null;
                  nativeClientIds?: string[] | null;
                  nativeEnabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (ProjectOAuth2GooglePrompt[] | null)?,
            (boolean | null)?,
            (string[] | null)?,
            (boolean | null)?,
        ]
    ): Promise<Models.OAuth2Google> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            prompt?: ProjectOAuth2GooglePrompt[] | null;
            enabled?: boolean | null;
            nativeClientIds?: string[] | null;
            nativeEnabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                prompt?: ProjectOAuth2GooglePrompt[] | null;
                enabled?: boolean | null;
                nativeClientIds?: string[] | null;
                nativeEnabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                prompt: rest[1] as ProjectOAuth2GooglePrompt[] | null,
                enabled: rest[2] as boolean | null,
                nativeClientIds: rest[3] as string[] | null,
                nativeEnabled: rest[4] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const prompt = params.prompt;
        const enabled = params.enabled;
        const nativeClientIds = params.nativeClientIds;
        const nativeEnabled = params.nativeEnabled;

        const apiPath = '/project/oauth2/google';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof nativeClientIds !== 'undefined') {
            apiPayload['nativeClientIds'] = nativeClientIds;
        }
        if (typeof nativeEnabled !== 'undefined') {
            apiPayload['nativeEnabled'] = nativeEnabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Google',
            models: {
                oAuth2Google: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        prompt: ['consent'],
                        nativeEnabled: false,
                        nativeClientIds: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Hugging Face configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Hugging Face OAuth2 app. For example: 2ab9cff9-d711-40ad-a91e-b08a49c42d24
     * @param {string | null} params.clientSecret - 'Client Secret' of Hugging Face OAuth2 app. For example: oauth_app_secret_wcLhRtl000000000000000000000xbNdLt
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2HuggingFace>}
     */
    updateOAuth2HuggingFace(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2HuggingFace>;
    /**
     * Update the project OAuth2 Hugging Face configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Hugging Face OAuth2 app. For example: 2ab9cff9-d711-40ad-a91e-b08a49c42d24
     * @param {string | null} clientSecret - 'Client Secret' of Hugging Face OAuth2 app. For example: oauth_app_secret_wcLhRtl000000000000000000000xbNdLt
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2HuggingFace>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2HuggingFace(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2HuggingFace>;
    updateOAuth2HuggingFace(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2HuggingFace> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/huggingface';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2HuggingFace',
            models: {
                oAuth2HuggingFace: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Kakao configuration.
     *
     * @param {string | null} params.clientId - 'REST API key' of Kakao OAuth2 app. For example: 839ff5000000000000000000013206de
     * @param {string | null} params.clientSecret - 'Client Secret' of Kakao OAuth2 app. For example: jLNVOK00000000000000000000yJebea. Generate it under Kakao Login > Security and set its status to enabled
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2KakaoPrompt[] | null} params.prompt - Array of Kakao OAuth2 prompt values. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "create" means: prompt the user to sign up. "select_account" means: prompt the user to select an account. Pass an empty array to use the Kakao default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Kakao>}
     */
    updateOAuth2Kakao(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
        prompt?: ProjectOAuth2KakaoPrompt[] | null;
    }): Promise<Models.OAuth2Kakao>;
    /**
     * Update the project OAuth2 Kakao configuration.
     *
     * @param {string | null} clientId - 'REST API key' of Kakao OAuth2 app. For example: 839ff5000000000000000000013206de
     * @param {string | null} clientSecret - 'Client Secret' of Kakao OAuth2 app. For example: jLNVOK00000000000000000000yJebea. Generate it under Kakao Login > Security and set its status to enabled
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2KakaoPrompt[] | null} prompt - Array of Kakao OAuth2 prompt values. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "create" means: prompt the user to sign up. "select_account" means: prompt the user to select an account. Pass an empty array to use the Kakao default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Kakao>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Kakao(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
        prompt?: ProjectOAuth2KakaoPrompt[] | null,
    ): Promise<Models.OAuth2Kakao>;
    updateOAuth2Kakao(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
                  prompt?: ProjectOAuth2KakaoPrompt[] | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (boolean | null)?,
            (ProjectOAuth2KakaoPrompt[] | null)?,
        ]
    ): Promise<Models.OAuth2Kakao> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
            prompt?: ProjectOAuth2KakaoPrompt[] | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
                prompt?: ProjectOAuth2KakaoPrompt[] | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
                prompt: rest[2] as ProjectOAuth2KakaoPrompt[] | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;
        const prompt = params.prompt;

        const apiPath = '/project/oauth2/kakao';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Kakao',
            models: {
                oAuth2Kakao: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        prompt: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Keycloak configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Keycloak OAuth2 app. For example: appwrite-o0000000st-app
     * @param {string | null} params.clientSecret - 'Client Secret' of Keycloak OAuth2 app. For example: jdjrJd00000000000000000000HUsaZO
     * @param {string | null} params.endpoint - Domain of Keycloak instance. For example: keycloak.example.com
     * @param {string | null} params.realmName - Keycloak realm name. For example: appwrite-realm
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Keycloak>}
     */
    updateOAuth2Keycloak(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        endpoint?: string | null;
        realmName?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Keycloak>;
    /**
     * Update the project OAuth2 Keycloak configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Keycloak OAuth2 app. For example: appwrite-o0000000st-app
     * @param {string | null} clientSecret - 'Client Secret' of Keycloak OAuth2 app. For example: jdjrJd00000000000000000000HUsaZO
     * @param {string | null} endpoint - Domain of Keycloak instance. For example: keycloak.example.com
     * @param {string | null} realmName - Keycloak realm name. For example: appwrite-realm
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Keycloak>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Keycloak(
        clientId?: string | null,
        clientSecret?: string | null,
        endpoint?: string | null,
        realmName?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Keycloak>;
    updateOAuth2Keycloak(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  endpoint?: string | null;
                  realmName?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (boolean | null)?,
        ]
    ): Promise<Models.OAuth2Keycloak> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            endpoint?: string | null;
            realmName?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                endpoint?: string | null;
                realmName?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                endpoint: rest[1] as string | null,
                realmName: rest[2] as string | null,
                enabled: rest[3] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const endpoint = params.endpoint;
        const realmName = params.realmName;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/keycloak';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof endpoint !== 'undefined') {
            apiPayload['endpoint'] = endpoint;
        }
        if (typeof realmName !== 'undefined') {
            apiPayload['realmName'] = realmName;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Keycloak',
            models: {
                oAuth2Keycloak: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        endpoint: '',
                        realmName: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Kick configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Kick OAuth2 app. For example: 01KQ7C00000000000001MFHS32
     * @param {string | null} params.clientSecret - 'Client Secret' of Kick OAuth2 app. For example: 34ac5600000000000000000000000000000000000000000000000000e830c8b
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Kick>}
     */
    updateOAuth2Kick(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Kick>;
    /**
     * Update the project OAuth2 Kick configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Kick OAuth2 app. For example: 01KQ7C00000000000001MFHS32
     * @param {string | null} clientSecret - 'Client Secret' of Kick OAuth2 app. For example: 34ac5600000000000000000000000000000000000000000000000000e830c8b
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Kick>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Kick(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Kick>;
    updateOAuth2Kick(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Kick> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/kick';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Kick',
            models: {
                oAuth2Kick: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Linkedin configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Linkedin OAuth2 app. For example: 770000000000dv
     * @param {string | null} params.primaryClientSecret - 'Primary Client Secret or Secondary Client Secret' of Linkedin OAuth2 app. For example: WPL_AP1.2Bf0000000000000./HtlYw==
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Linkedin>}
     */
    updateOAuth2Linkedin(params?: {
        clientId?: string | null;
        primaryClientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Linkedin>;
    /**
     * Update the project OAuth2 Linkedin configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Linkedin OAuth2 app. For example: 770000000000dv
     * @param {string | null} primaryClientSecret - 'Primary Client Secret or Secondary Client Secret' of Linkedin OAuth2 app. For example: WPL_AP1.2Bf0000000000000./HtlYw==
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Linkedin>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Linkedin(
        clientId?: string | null,
        primaryClientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Linkedin>;
    updateOAuth2Linkedin(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  primaryClientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Linkedin> {
        let params: {
            clientId?: string | null;
            primaryClientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                primaryClientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                primaryClientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const primaryClientSecret = params.primaryClientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/linkedin';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof primaryClientSecret !== 'undefined') {
            apiPayload['primaryClientSecret'] = primaryClientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Linkedin',
            models: {
                oAuth2Linkedin: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        primaryClientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Microsoft configuration.
     *
     * @param {string | null} params.applicationId - 'Entra ID Application ID, also known as Client ID' of Microsoft OAuth2 app. For example: 00001111-aaaa-2222-bbbb-3333cccc4444
     * @param {string | null} params.applicationSecret - 'Entra ID Application Secret, also known as Client Secret' of Microsoft OAuth2 app. For example: A1bC2dE3fH4iJ5kL6mN7oP8qR9sT0u
     * @param {string | null} params.tenant - Microsoft Entra ID tenant identifier. Use 'common', 'organizations', 'consumers' or a specific tenant ID. For example: common
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2MicrosoftPrompt[] | null} params.prompt - Array of Microsoft OAuth2 prompt values. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. "select_account" means: prompt the user to select an account. Pass an empty array to use the Microsoft default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Microsoft>}
     */
    updateOAuth2Microsoft(params?: {
        applicationId?: string | null;
        applicationSecret?: string | null;
        tenant?: string | null;
        enabled?: boolean | null;
        prompt?: ProjectOAuth2MicrosoftPrompt[] | null;
    }): Promise<Models.OAuth2Microsoft>;
    /**
     * Update the project OAuth2 Microsoft configuration.
     *
     * @param {string | null} applicationId - 'Entra ID Application ID, also known as Client ID' of Microsoft OAuth2 app. For example: 00001111-aaaa-2222-bbbb-3333cccc4444
     * @param {string | null} applicationSecret - 'Entra ID Application Secret, also known as Client Secret' of Microsoft OAuth2 app. For example: A1bC2dE3fH4iJ5kL6mN7oP8qR9sT0u
     * @param {string | null} tenant - Microsoft Entra ID tenant identifier. Use 'common', 'organizations', 'consumers' or a specific tenant ID. For example: common
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2MicrosoftPrompt[] | null} prompt - Array of Microsoft OAuth2 prompt values. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. "select_account" means: prompt the user to select an account. Pass an empty array to use the Microsoft default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Microsoft>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Microsoft(
        applicationId?: string | null,
        applicationSecret?: string | null,
        tenant?: string | null,
        enabled?: boolean | null,
        prompt?: ProjectOAuth2MicrosoftPrompt[] | null,
    ): Promise<Models.OAuth2Microsoft>;
    updateOAuth2Microsoft(
        paramsOrFirst?:
            | {
                  applicationId?: string | null;
                  applicationSecret?: string | null;
                  tenant?: string | null;
                  enabled?: boolean | null;
                  prompt?: ProjectOAuth2MicrosoftPrompt[] | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (string | null)?,
            (boolean | null)?,
            (ProjectOAuth2MicrosoftPrompt[] | null)?,
        ]
    ): Promise<Models.OAuth2Microsoft> {
        let params: {
            applicationId?: string | null;
            applicationSecret?: string | null;
            tenant?: string | null;
            enabled?: boolean | null;
            prompt?: ProjectOAuth2MicrosoftPrompt[] | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                applicationId?: string | null;
                applicationSecret?: string | null;
                tenant?: string | null;
                enabled?: boolean | null;
                prompt?: ProjectOAuth2MicrosoftPrompt[] | null;
            };
        } else {
            params = {
                applicationId: paramsOrFirst as string | null,
                applicationSecret: rest[0] as string | null,
                tenant: rest[1] as string | null,
                enabled: rest[2] as boolean | null,
                prompt: rest[3] as ProjectOAuth2MicrosoftPrompt[] | null,
            };
        }

        const applicationId = params.applicationId;
        const applicationSecret = params.applicationSecret;
        const tenant = params.tenant;
        const enabled = params.enabled;
        const prompt = params.prompt;

        const apiPath = '/project/oauth2/microsoft';
        const apiPayload: Payload = {};
        if (typeof applicationId !== 'undefined') {
            apiPayload['applicationId'] = applicationId;
        }
        if (typeof applicationSecret !== 'undefined') {
            apiPayload['applicationSecret'] = applicationSecret;
        }
        if (typeof tenant !== 'undefined') {
            apiPayload['tenant'] = tenant;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Microsoft',
            models: {
                oAuth2Microsoft: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        applicationId: '',
                        applicationSecret: '',
                        prompt: [],
                        tenant: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Notion configuration.
     *
     * @param {string | null} params.oauthClientId - 'OAuth Client ID' of Notion OAuth2 app. For example: 341d8700-0000-0000-0000-000000446ee3
     * @param {string | null} params.oauthClientSecret - 'OAuth Client Secret' of Notion OAuth2 app. For example: secret_dLUr4b000000000000000000000000000000lFHAa9
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Notion>}
     */
    updateOAuth2Notion(params?: {
        oauthClientId?: string | null;
        oauthClientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Notion>;
    /**
     * Update the project OAuth2 Notion configuration.
     *
     * @param {string | null} oauthClientId - 'OAuth Client ID' of Notion OAuth2 app. For example: 341d8700-0000-0000-0000-000000446ee3
     * @param {string | null} oauthClientSecret - 'OAuth Client Secret' of Notion OAuth2 app. For example: secret_dLUr4b000000000000000000000000000000lFHAa9
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Notion>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Notion(
        oauthClientId?: string | null,
        oauthClientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Notion>;
    updateOAuth2Notion(
        paramsOrFirst?:
            | {
                  oauthClientId?: string | null;
                  oauthClientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Notion> {
        let params: {
            oauthClientId?: string | null;
            oauthClientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                oauthClientId?: string | null;
                oauthClientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                oauthClientId: paramsOrFirst as string | null,
                oauthClientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const oauthClientId = params.oauthClientId;
        const oauthClientSecret = params.oauthClientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/notion';
        const apiPayload: Payload = {};
        if (typeof oauthClientId !== 'undefined') {
            apiPayload['oauthClientId'] = oauthClientId;
        }
        if (typeof oauthClientSecret !== 'undefined') {
            apiPayload['oauthClientSecret'] = oauthClientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Notion',
            models: {
                oAuth2Notion: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        oauthClientId: '',
                        oauthClientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Oidc configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Oidc OAuth2 app. For example: qibI2x0000000000000000000000000006L2YFoG
     * @param {string | null} params.clientSecret - 'Client Secret' of Oidc OAuth2 app. For example: Ah68ed000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000003qpcHV
     * @param {string | null} params.wellKnownURL - OpenID Connect well-known configuration URL. When provided, authorization, token, and user info endpoints can be discovered automatically. For example: https://myoauth.com/.well-known/openid-configuration
     * @param {string | null} params.authorizationURL - OpenID Connect authorization endpoint URL. Required when wellKnownURL is not provided. For example: https://myoauth.com/oauth2/authorize
     * @param {string | null} params.tokenURL - OpenID Connect token endpoint URL. Required when wellKnownURL is not provided. For example: https://myoauth.com/oauth2/token
     * @param {string | null} params.userInfoURL - OpenID Connect user info endpoint URL. Required when wellKnownURL is not provided. For example: https://myoauth.com/oauth2/userinfo
     * @param {ProjectOAuth2OidcPrompt[] | null} params.prompt - Array of OpenID Connect prompt values controlling the authentication and consent screens. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. "select_account" means: prompt the user to select an account.
     * @param {number | null} params.maxAge - Maximum authentication age in seconds. When set, the user must have authenticated within this many seconds, otherwise they are prompted to re-authenticate.
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Oidc>}
     */
    updateOAuth2Oidc(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        wellKnownURL?: string | null;
        authorizationURL?: string | null;
        tokenURL?: string | null;
        userInfoURL?: string | null;
        prompt?: ProjectOAuth2OidcPrompt[] | null;
        maxAge?: number | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Oidc>;
    /**
     * Update the project OAuth2 Oidc configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Oidc OAuth2 app. For example: qibI2x0000000000000000000000000006L2YFoG
     * @param {string | null} clientSecret - 'Client Secret' of Oidc OAuth2 app. For example: Ah68ed000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000003qpcHV
     * @param {string | null} wellKnownURL - OpenID Connect well-known configuration URL. When provided, authorization, token, and user info endpoints can be discovered automatically. For example: https://myoauth.com/.well-known/openid-configuration
     * @param {string | null} authorizationURL - OpenID Connect authorization endpoint URL. Required when wellKnownURL is not provided. For example: https://myoauth.com/oauth2/authorize
     * @param {string | null} tokenURL - OpenID Connect token endpoint URL. Required when wellKnownURL is not provided. For example: https://myoauth.com/oauth2/token
     * @param {string | null} userInfoURL - OpenID Connect user info endpoint URL. Required when wellKnownURL is not provided. For example: https://myoauth.com/oauth2/userinfo
     * @param {ProjectOAuth2OidcPrompt[] | null} prompt - Array of OpenID Connect prompt values controlling the authentication and consent screens. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. "select_account" means: prompt the user to select an account.
     * @param {number | null} maxAge - Maximum authentication age in seconds. When set, the user must have authenticated within this many seconds, otherwise they are prompted to re-authenticate.
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Oidc>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Oidc(
        clientId?: string | null,
        clientSecret?: string | null,
        wellKnownURL?: string | null,
        authorizationURL?: string | null,
        tokenURL?: string | null,
        userInfoURL?: string | null,
        prompt?: ProjectOAuth2OidcPrompt[] | null,
        maxAge?: number | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Oidc>;
    updateOAuth2Oidc(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  wellKnownURL?: string | null;
                  authorizationURL?: string | null;
                  tokenURL?: string | null;
                  userInfoURL?: string | null;
                  prompt?: ProjectOAuth2OidcPrompt[] | null;
                  maxAge?: number | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (ProjectOAuth2OidcPrompt[] | null)?,
            (number | null)?,
            (boolean | null)?,
        ]
    ): Promise<Models.OAuth2Oidc> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            wellKnownURL?: string | null;
            authorizationURL?: string | null;
            tokenURL?: string | null;
            userInfoURL?: string | null;
            prompt?: ProjectOAuth2OidcPrompt[] | null;
            maxAge?: number | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                wellKnownURL?: string | null;
                authorizationURL?: string | null;
                tokenURL?: string | null;
                userInfoURL?: string | null;
                prompt?: ProjectOAuth2OidcPrompt[] | null;
                maxAge?: number | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                wellKnownURL: rest[1] as string | null,
                authorizationURL: rest[2] as string | null,
                tokenURL: rest[3] as string | null,
                userInfoURL: rest[4] as string | null,
                prompt: rest[5] as ProjectOAuth2OidcPrompt[] | null,
                maxAge: rest[6] as number | null,
                enabled: rest[7] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const wellKnownURL = params.wellKnownURL;
        const authorizationURL = params.authorizationURL;
        const tokenURL = params.tokenURL;
        const userInfoURL = params.userInfoURL;
        const prompt = params.prompt;
        const maxAge = params.maxAge;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/oidc';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof wellKnownURL !== 'undefined') {
            apiPayload['wellKnownURL'] = wellKnownURL;
        }
        if (typeof authorizationURL !== 'undefined') {
            apiPayload['authorizationURL'] = authorizationURL;
        }
        if (typeof tokenURL !== 'undefined') {
            apiPayload['tokenURL'] = tokenURL;
        }
        if (typeof userInfoURL !== 'undefined') {
            apiPayload['userInfoURL'] = userInfoURL;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        if (typeof maxAge !== 'undefined') {
            apiPayload['maxAge'] = maxAge;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Oidc',
            models: {
                oAuth2Oidc: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        prompt: [],
                        wellKnownURL: '',
                        authorizationURL: '',
                        tokenURL: '',
                        userInfoURL: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Okta configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Okta OAuth2 app. For example: 0oa00000000000000698
     * @param {string | null} params.clientSecret - 'Client Secret' of Okta OAuth2 app. For example: Kiq0000000000000000000000000000000000000-00000000000H2L5-3SJ-vRV
     * @param {string | null} params.domain - Okta company domain. Required when enabling the provider. For example: trial-6400025.okta.com. Example of wrong value: trial-6400025-admin.okta.com, or https://trial-6400025.okta.com/
     * @param {string | null} params.authorizationServerId - Custom Authorization Servers. Optional, can be left empty or unconfigured. For example: aus000000000000000h7z
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2OktaPrompt[] | null} params.prompt - Array of Okta OAuth2 prompt values. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. Pass an empty array to use the Okta default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Okta>}
     */
    updateOAuth2Okta(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        domain?: string | null;
        authorizationServerId?: string | null;
        enabled?: boolean | null;
        prompt?: ProjectOAuth2OktaPrompt[] | null;
    }): Promise<Models.OAuth2Okta>;
    /**
     * Update the project OAuth2 Okta configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Okta OAuth2 app. For example: 0oa00000000000000698
     * @param {string | null} clientSecret - 'Client Secret' of Okta OAuth2 app. For example: Kiq0000000000000000000000000000000000000-00000000000H2L5-3SJ-vRV
     * @param {string | null} domain - Okta company domain. Required when enabling the provider. For example: trial-6400025.okta.com. Example of wrong value: trial-6400025-admin.okta.com, or https://trial-6400025.okta.com/
     * @param {string | null} authorizationServerId - Custom Authorization Servers. Optional, can be left empty or unconfigured. For example: aus000000000000000h7z
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2OktaPrompt[] | null} prompt - Array of Okta OAuth2 prompt values. If "none" is included, it must be the only element. "none" means: don't display any authentication or consent screens. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. Pass an empty array to use the Okta default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Okta>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Okta(
        clientId?: string | null,
        clientSecret?: string | null,
        domain?: string | null,
        authorizationServerId?: string | null,
        enabled?: boolean | null,
        prompt?: ProjectOAuth2OktaPrompt[] | null,
    ): Promise<Models.OAuth2Okta>;
    updateOAuth2Okta(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  domain?: string | null;
                  authorizationServerId?: string | null;
                  enabled?: boolean | null;
                  prompt?: ProjectOAuth2OktaPrompt[] | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (boolean | null)?,
            (ProjectOAuth2OktaPrompt[] | null)?,
        ]
    ): Promise<Models.OAuth2Okta> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            domain?: string | null;
            authorizationServerId?: string | null;
            enabled?: boolean | null;
            prompt?: ProjectOAuth2OktaPrompt[] | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                domain?: string | null;
                authorizationServerId?: string | null;
                enabled?: boolean | null;
                prompt?: ProjectOAuth2OktaPrompt[] | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                domain: rest[1] as string | null,
                authorizationServerId: rest[2] as string | null,
                enabled: rest[3] as boolean | null,
                prompt: rest[4] as ProjectOAuth2OktaPrompt[] | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const domain = params.domain;
        const authorizationServerId = params.authorizationServerId;
        const enabled = params.enabled;
        const prompt = params.prompt;

        const apiPath = '/project/oauth2/okta';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof authorizationServerId !== 'undefined') {
            apiPayload['authorizationServerId'] = authorizationServerId;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Okta',
            models: {
                oAuth2Okta: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        prompt: [],
                        domain: '',
                        authorizationServerId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Paypal configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Paypal OAuth2 app. For example: AdhIEG7-000000000000-0000000000000000000000000000000-0000000000000000000000-2pyB
     * @param {string | null} params.secretKey - 'Secret Key 1 or Secret Key 2' of Paypal OAuth2 app. For example: EH8KCXtew--000000000000000000000000000000000000000_C-1_5UP_000000000000000CB7KDp
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Paypal>}
     */
    updateOAuth2Paypal(params?: {
        clientId?: string | null;
        secretKey?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Paypal>;
    /**
     * Update the project OAuth2 Paypal configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Paypal OAuth2 app. For example: AdhIEG7-000000000000-0000000000000000000000000000000-0000000000000000000000-2pyB
     * @param {string | null} secretKey - 'Secret Key 1 or Secret Key 2' of Paypal OAuth2 app. For example: EH8KCXtew--000000000000000000000000000000000000000_C-1_5UP_000000000000000CB7KDp
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Paypal>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Paypal(
        clientId?: string | null,
        secretKey?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Paypal>;
    updateOAuth2Paypal(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  secretKey?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Paypal> {
        let params: {
            clientId?: string | null;
            secretKey?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                secretKey?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                secretKey: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const secretKey = params.secretKey;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/paypal';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof secretKey !== 'undefined') {
            apiPayload['secretKey'] = secretKey;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Paypal',
            models: {
                oAuth2Paypal: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        secretKey: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 PaypalSandbox configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of PaypalSandbox OAuth2 app. For example: AdhIEG7-000000000000-0000000000000000000000000000000-0000000000000000000000-2pyB
     * @param {string | null} params.secretKey - 'Secret Key 1 or Secret Key 2' of PaypalSandbox OAuth2 app. For example: EH8KCXtew--000000000000000000000000000000000000000_C-1_5UP_000000000000000CB7KDp
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Paypal>}
     */
    updateOAuth2PaypalSandbox(params?: {
        clientId?: string | null;
        secretKey?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Paypal>;
    /**
     * Update the project OAuth2 PaypalSandbox configuration.
     *
     * @param {string | null} clientId - 'Client ID' of PaypalSandbox OAuth2 app. For example: AdhIEG7-000000000000-0000000000000000000000000000000-0000000000000000000000-2pyB
     * @param {string | null} secretKey - 'Secret Key 1 or Secret Key 2' of PaypalSandbox OAuth2 app. For example: EH8KCXtew--000000000000000000000000000000000000000_C-1_5UP_000000000000000CB7KDp
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Paypal>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2PaypalSandbox(
        clientId?: string | null,
        secretKey?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Paypal>;
    updateOAuth2PaypalSandbox(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  secretKey?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Paypal> {
        let params: {
            clientId?: string | null;
            secretKey?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                secretKey?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                secretKey: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const secretKey = params.secretKey;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/paypalSandbox';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof secretKey !== 'undefined') {
            apiPayload['secretKey'] = secretKey;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Paypal',
            models: {
                oAuth2Paypal: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        secretKey: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Podio configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Podio OAuth2 app. For example: appwrite-o0000000st-app
     * @param {string | null} params.clientSecret - 'Client Secret' of Podio OAuth2 app. For example: Rn247T0000000000000000000000000000000000000000000000000000W2zWTN
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Podio>}
     */
    updateOAuth2Podio(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Podio>;
    /**
     * Update the project OAuth2 Podio configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Podio OAuth2 app. For example: appwrite-o0000000st-app
     * @param {string | null} clientSecret - 'Client Secret' of Podio OAuth2 app. For example: Rn247T0000000000000000000000000000000000000000000000000000W2zWTN
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Podio>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Podio(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Podio>;
    updateOAuth2Podio(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Podio> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/podio';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Podio',
            models: {
                oAuth2Podio: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Resend configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Resend OAuth2 app. For example: f47ac10b-58cc-4372-a567-0e02b2c3d479
     * @param {string | null} params.clientSecret - 'Client Secret' of Resend OAuth2 app. For example: 9c1e4b00000000000000000000000000000000000000000000000000a72d5f4
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Resend>}
     */
    updateOAuth2Resend(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Resend>;
    /**
     * Update the project OAuth2 Resend configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Resend OAuth2 app. For example: f47ac10b-58cc-4372-a567-0e02b2c3d479
     * @param {string | null} clientSecret - 'Client Secret' of Resend OAuth2 app. For example: 9c1e4b00000000000000000000000000000000000000000000000000a72d5f4
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Resend>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Resend(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Resend>;
    updateOAuth2Resend(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Resend> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/resend';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Resend',
            models: {
                oAuth2Resend: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Salesforce configuration.
     *
     * @param {string | null} params.customerKey - 'Consumer Key' of Salesforce OAuth2 app. For example: 3MVG9I0000000000000000000000000000000000000000000000000000000000000000000000000C5Aejq
     * @param {string | null} params.customerSecret - 'Consumer Secret' of Salesforce OAuth2 app. For example: 3w000000000000e2
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2SalesforcePrompt[] | null} params.prompt - Array of Salesforce OAuth2 prompt values. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. Pass an empty array to use the Salesforce default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Salesforce>}
     */
    updateOAuth2Salesforce(params?: {
        customerKey?: string | null;
        customerSecret?: string | null;
        enabled?: boolean | null;
        prompt?: ProjectOAuth2SalesforcePrompt[] | null;
    }): Promise<Models.OAuth2Salesforce>;
    /**
     * Update the project OAuth2 Salesforce configuration.
     *
     * @param {string | null} customerKey - 'Consumer Key' of Salesforce OAuth2 app. For example: 3MVG9I0000000000000000000000000000000000000000000000000000000000000000000000000C5Aejq
     * @param {string | null} customerSecret - 'Consumer Secret' of Salesforce OAuth2 app. For example: 3w000000000000e2
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2SalesforcePrompt[] | null} prompt - Array of Salesforce OAuth2 prompt values. "login" means: prompt the user to re-authenticate. "consent" means: prompt the user for consent. Pass an empty array to use the Salesforce default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Salesforce>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Salesforce(
        customerKey?: string | null,
        customerSecret?: string | null,
        enabled?: boolean | null,
        prompt?: ProjectOAuth2SalesforcePrompt[] | null,
    ): Promise<Models.OAuth2Salesforce>;
    updateOAuth2Salesforce(
        paramsOrFirst?:
            | {
                  customerKey?: string | null;
                  customerSecret?: string | null;
                  enabled?: boolean | null;
                  prompt?: ProjectOAuth2SalesforcePrompt[] | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (boolean | null)?,
            (ProjectOAuth2SalesforcePrompt[] | null)?,
        ]
    ): Promise<Models.OAuth2Salesforce> {
        let params: {
            customerKey?: string | null;
            customerSecret?: string | null;
            enabled?: boolean | null;
            prompt?: ProjectOAuth2SalesforcePrompt[] | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                customerKey?: string | null;
                customerSecret?: string | null;
                enabled?: boolean | null;
                prompt?: ProjectOAuth2SalesforcePrompt[] | null;
            };
        } else {
            params = {
                customerKey: paramsOrFirst as string | null,
                customerSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
                prompt: rest[2] as ProjectOAuth2SalesforcePrompt[] | null,
            };
        }

        const customerKey = params.customerKey;
        const customerSecret = params.customerSecret;
        const enabled = params.enabled;
        const prompt = params.prompt;

        const apiPath = '/project/oauth2/salesforce';
        const apiPayload: Payload = {};
        if (typeof customerKey !== 'undefined') {
            apiPayload['customerKey'] = customerKey;
        }
        if (typeof customerSecret !== 'undefined') {
            apiPayload['customerSecret'] = customerSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Salesforce',
            models: {
                oAuth2Salesforce: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        customerKey: '',
                        customerSecret: '',
                        prompt: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Slack configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Slack OAuth2 app. For example: 23000000089.15000000000023
     * @param {string | null} params.clientSecret - 'Client Secret' of Slack OAuth2 app. For example: 81656000000000000000000000f3d2fd
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Slack>}
     */
    updateOAuth2Slack(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Slack>;
    /**
     * Update the project OAuth2 Slack configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Slack OAuth2 app. For example: 23000000089.15000000000023
     * @param {string | null} clientSecret - 'Client Secret' of Slack OAuth2 app. For example: 81656000000000000000000000f3d2fd
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Slack>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Slack(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Slack>;
    updateOAuth2Slack(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Slack> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/slack';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Slack',
            models: {
                oAuth2Slack: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Spotify configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Spotify OAuth2 app. For example: 6ec271000000000000000000009beace
     * @param {string | null} params.clientSecret - 'Client Secret' of Spotify OAuth2 app. For example: db068a000000000000000000008b5b9f
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Spotify>}
     */
    updateOAuth2Spotify(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Spotify>;
    /**
     * Update the project OAuth2 Spotify configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Spotify OAuth2 app. For example: 6ec271000000000000000000009beace
     * @param {string | null} clientSecret - 'Client Secret' of Spotify OAuth2 app. For example: db068a000000000000000000008b5b9f
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Spotify>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Spotify(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Spotify>;
    updateOAuth2Spotify(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Spotify> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/spotify';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Spotify',
            models: {
                oAuth2Spotify: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Stripe configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Stripe OAuth2 app. For example: ca_UKibXX0000000000000000000006byvR
     * @param {string | null} params.apiSecretKey - 'API Secret Key' of Stripe OAuth2 app. For example: sk_51SfOd000000000000000000000000000000000000000000000000000000000000000000000000000000000000000QGWYfp
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Stripe>}
     */
    updateOAuth2Stripe(params?: {
        clientId?: string | null;
        apiSecretKey?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Stripe>;
    /**
     * Update the project OAuth2 Stripe configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Stripe OAuth2 app. For example: ca_UKibXX0000000000000000000006byvR
     * @param {string | null} apiSecretKey - 'API Secret Key' of Stripe OAuth2 app. For example: sk_51SfOd000000000000000000000000000000000000000000000000000000000000000000000000000000000000000QGWYfp
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Stripe>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Stripe(
        clientId?: string | null,
        apiSecretKey?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Stripe>;
    updateOAuth2Stripe(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  apiSecretKey?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Stripe> {
        let params: {
            clientId?: string | null;
            apiSecretKey?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                apiSecretKey?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                apiSecretKey: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const apiSecretKey = params.apiSecretKey;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/stripe';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof apiSecretKey !== 'undefined') {
            apiPayload['apiSecretKey'] = apiSecretKey;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Stripe',
            models: {
                oAuth2Stripe: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        apiSecretKey: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 TikTok configuration.
     *
     * @param {string | null} params.clientId - 'Client key' of TikTok OAuth2 app. For example: awz000000000tyw0
     * @param {string | null} params.clientSecret - 'Client secret' of TikTok OAuth2 app. For example: 6wXewM00000000000000000000yXnite
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2TikTok>}
     */
    updateOAuth2TikTok(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2TikTok>;
    /**
     * Update the project OAuth2 TikTok configuration.
     *
     * @param {string | null} clientId - 'Client key' of TikTok OAuth2 app. For example: awz000000000tyw0
     * @param {string | null} clientSecret - 'Client secret' of TikTok OAuth2 app. For example: 6wXewM00000000000000000000yXnite
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2TikTok>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2TikTok(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2TikTok>;
    updateOAuth2TikTok(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2TikTok> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/tiktok';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2TikTok',
            models: {
                oAuth2TikTok: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Tradeshift configuration.
     *
     * @param {string | null} params.oauth2ClientId - 'OAuth2 Client ID' of Tradeshift OAuth2 app. For example: appwrite-tes00000.0000000000est-app
     * @param {string | null} params.oauth2ClientSecret - 'OAuth2 Client Secret' of Tradeshift OAuth2 app. For example: 7cb52700-0000-0000-0000-000000ca5b83
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Tradeshift>}
     */
    updateOAuth2Tradeshift(params?: {
        oauth2ClientId?: string | null;
        oauth2ClientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Tradeshift>;
    /**
     * Update the project OAuth2 Tradeshift configuration.
     *
     * @param {string | null} oauth2ClientId - 'OAuth2 Client ID' of Tradeshift OAuth2 app. For example: appwrite-tes00000.0000000000est-app
     * @param {string | null} oauth2ClientSecret - 'OAuth2 Client Secret' of Tradeshift OAuth2 app. For example: 7cb52700-0000-0000-0000-000000ca5b83
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Tradeshift>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Tradeshift(
        oauth2ClientId?: string | null,
        oauth2ClientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Tradeshift>;
    updateOAuth2Tradeshift(
        paramsOrFirst?:
            | {
                  oauth2ClientId?: string | null;
                  oauth2ClientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Tradeshift> {
        let params: {
            oauth2ClientId?: string | null;
            oauth2ClientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                oauth2ClientId?: string | null;
                oauth2ClientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                oauth2ClientId: paramsOrFirst as string | null,
                oauth2ClientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const oauth2ClientId = params.oauth2ClientId;
        const oauth2ClientSecret = params.oauth2ClientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/tradeshift';
        const apiPayload: Payload = {};
        if (typeof oauth2ClientId !== 'undefined') {
            apiPayload['oauth2ClientId'] = oauth2ClientId;
        }
        if (typeof oauth2ClientSecret !== 'undefined') {
            apiPayload['oauth2ClientSecret'] = oauth2ClientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Tradeshift',
            models: {
                oAuth2Tradeshift: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        oauth2ClientId: '',
                        oauth2ClientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Tradeshift Sandbox configuration.
     *
     * @param {string | null} params.oauth2ClientId - 'OAuth2 Client ID' of Tradeshift Sandbox OAuth2 app. For example: appwrite-tes00000.0000000000est-app
     * @param {string | null} params.oauth2ClientSecret - 'OAuth2 Client Secret' of Tradeshift Sandbox OAuth2 app. For example: 7cb52700-0000-0000-0000-000000ca5b83
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Tradeshift>}
     */
    updateOAuth2TradeshiftSandbox(params?: {
        oauth2ClientId?: string | null;
        oauth2ClientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Tradeshift>;
    /**
     * Update the project OAuth2 Tradeshift Sandbox configuration.
     *
     * @param {string | null} oauth2ClientId - 'OAuth2 Client ID' of Tradeshift Sandbox OAuth2 app. For example: appwrite-tes00000.0000000000est-app
     * @param {string | null} oauth2ClientSecret - 'OAuth2 Client Secret' of Tradeshift Sandbox OAuth2 app. For example: 7cb52700-0000-0000-0000-000000ca5b83
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Tradeshift>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2TradeshiftSandbox(
        oauth2ClientId?: string | null,
        oauth2ClientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Tradeshift>;
    updateOAuth2TradeshiftSandbox(
        paramsOrFirst?:
            | {
                  oauth2ClientId?: string | null;
                  oauth2ClientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Tradeshift> {
        let params: {
            oauth2ClientId?: string | null;
            oauth2ClientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                oauth2ClientId?: string | null;
                oauth2ClientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                oauth2ClientId: paramsOrFirst as string | null,
                oauth2ClientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const oauth2ClientId = params.oauth2ClientId;
        const oauth2ClientSecret = params.oauth2ClientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/tradeshiftBox';
        const apiPayload: Payload = {};
        if (typeof oauth2ClientId !== 'undefined') {
            apiPayload['oauth2ClientId'] = oauth2ClientId;
        }
        if (typeof oauth2ClientSecret !== 'undefined') {
            apiPayload['oauth2ClientSecret'] = oauth2ClientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Tradeshift',
            models: {
                oAuth2Tradeshift: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        oauth2ClientId: '',
                        oauth2ClientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Twitch configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Twitch OAuth2 app. For example: vvi0in000000000000000000ikmt9p
     * @param {string | null} params.clientSecret - 'Client Secret' of Twitch OAuth2 app. For example: pmapue000000000000000000zylw3v
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Twitch>}
     */
    updateOAuth2Twitch(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Twitch>;
    /**
     * Update the project OAuth2 Twitch configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Twitch OAuth2 app. For example: vvi0in000000000000000000ikmt9p
     * @param {string | null} clientSecret - 'Client Secret' of Twitch OAuth2 app. For example: pmapue000000000000000000zylw3v
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Twitch>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Twitch(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Twitch>;
    updateOAuth2Twitch(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Twitch> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/twitch';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Twitch',
            models: {
                oAuth2Twitch: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Webflow configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Webflow OAuth2 app. For example: 8bb20000000000000000000000000000000000000000000000000000000040dd
     * @param {string | null} params.clientSecret - 'Client Secret' of Webflow OAuth2 app. For example: 59bf00000000000000000000000000000000000000000000000000000000fe59
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Webflow>}
     */
    updateOAuth2Webflow(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Webflow>;
    /**
     * Update the project OAuth2 Webflow configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Webflow OAuth2 app. For example: 8bb20000000000000000000000000000000000000000000000000000000040dd
     * @param {string | null} clientSecret - 'Client Secret' of Webflow OAuth2 app. For example: 59bf00000000000000000000000000000000000000000000000000000000fe59
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Webflow>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Webflow(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Webflow>;
    updateOAuth2Webflow(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Webflow> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/webflow';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Webflow',
            models: {
                oAuth2Webflow: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 WordPress configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of WordPress OAuth2 app. For example: 130005
     * @param {string | null} params.clientSecret - 'Client Secret' of WordPress OAuth2 app. For example: PlBfJS0000000000000000000000000000000000000000000000000000EdUZJk
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2WordPress>}
     */
    updateOAuth2WordPress(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2WordPress>;
    /**
     * Update the project OAuth2 WordPress configuration.
     *
     * @param {string | null} clientId - 'Client ID' of WordPress OAuth2 app. For example: 130005
     * @param {string | null} clientSecret - 'Client Secret' of WordPress OAuth2 app. For example: PlBfJS0000000000000000000000000000000000000000000000000000EdUZJk
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2WordPress>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2WordPress(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2WordPress>;
    updateOAuth2WordPress(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2WordPress> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/wordpress';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2WordPress',
            models: {
                oAuth2WordPress: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 X configuration.
     *
     * @param {string | null} params.customerKey - 'Customer Key' of X OAuth2 app. For example: slzZV0000000000000NFLaWT
     * @param {string | null} params.secretKey - 'Secret Key' of X OAuth2 app. For example: tkEPkp00000000000000000000000000000000000000FTxbI9
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2X>}
     */
    updateOAuth2X(params?: {
        customerKey?: string | null;
        secretKey?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2X>;
    /**
     * Update the project OAuth2 X configuration.
     *
     * @param {string | null} customerKey - 'Customer Key' of X OAuth2 app. For example: slzZV0000000000000NFLaWT
     * @param {string | null} secretKey - 'Secret Key' of X OAuth2 app. For example: tkEPkp00000000000000000000000000000000000000FTxbI9
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2X>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2X(
        customerKey?: string | null,
        secretKey?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2X>;
    updateOAuth2X(
        paramsOrFirst?:
            | {
                  customerKey?: string | null;
                  secretKey?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2X> {
        let params: {
            customerKey?: string | null;
            secretKey?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                customerKey?: string | null;
                secretKey?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                customerKey: paramsOrFirst as string | null,
                secretKey: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const customerKey = params.customerKey;
        const secretKey = params.secretKey;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/x';
        const apiPayload: Payload = {};
        if (typeof customerKey !== 'undefined') {
            apiPayload['customerKey'] = customerKey;
        }
        if (typeof secretKey !== 'undefined') {
            apiPayload['secretKey'] = secretKey;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2X',
            models: {
                oAuth2X: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        customerKey: '',
                        secretKey: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Yahoo configuration.
     *
     * @param {string | null} params.clientId - 'Client ID, also known as Customer Key' of Yahoo OAuth2 app. For example: dj0yJm000000000000000000000000000000000000000000000000000000000000000000000000000000000000Z4PWRm
     * @param {string | null} params.clientSecret - 'Client Secret, also known as Customer Secret' of Yahoo OAuth2 app. For example: cf978f0000000000000000000000000000c5e2e9
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Yahoo>}
     */
    updateOAuth2Yahoo(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Yahoo>;
    /**
     * Update the project OAuth2 Yahoo configuration.
     *
     * @param {string | null} clientId - 'Client ID, also known as Customer Key' of Yahoo OAuth2 app. For example: dj0yJm000000000000000000000000000000000000000000000000000000000000000000000000000000000000Z4PWRm
     * @param {string | null} clientSecret - 'Client Secret, also known as Customer Secret' of Yahoo OAuth2 app. For example: cf978f0000000000000000000000000000c5e2e9
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Yahoo>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Yahoo(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Yahoo>;
    updateOAuth2Yahoo(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Yahoo> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/yahoo';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Yahoo',
            models: {
                oAuth2Yahoo: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Yandex configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Yandex OAuth2 app. For example: 6a8a6a0000000000000000000091483c
     * @param {string | null} params.clientSecret - 'Client Secret' of Yandex OAuth2 app. For example: bbf98500000000000000000000c75a63
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Yandex>}
     */
    updateOAuth2Yandex(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Yandex>;
    /**
     * Update the project OAuth2 Yandex configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Yandex OAuth2 app. For example: 6a8a6a0000000000000000000091483c
     * @param {string | null} clientSecret - 'Client Secret' of Yandex OAuth2 app. For example: bbf98500000000000000000000c75a63
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Yandex>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Yandex(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Yandex>;
    updateOAuth2Yandex(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Yandex> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/yandex';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Yandex',
            models: {
                oAuth2Yandex: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Zoho configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Zoho OAuth2 app. For example: 1000.83C178000000000000000000RPNX0B
     * @param {string | null} params.clientSecret - 'Client Secret' of Zoho OAuth2 app. For example: fb5cac000000000000000000000000000000a68f6e
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2ZohoPrompt[] | null} params.prompt - Array of Zoho OAuth2 prompt values. "consent" means: prompt the user for consent. Pass an empty array to use the Zoho default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Zoho>}
     */
    updateOAuth2Zoho(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
        prompt?: ProjectOAuth2ZohoPrompt[] | null;
    }): Promise<Models.OAuth2Zoho>;
    /**
     * Update the project OAuth2 Zoho configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Zoho OAuth2 app. For example: 1000.83C178000000000000000000RPNX0B
     * @param {string | null} clientSecret - 'Client Secret' of Zoho OAuth2 app. For example: fb5cac000000000000000000000000000000a68f6e
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @param {ProjectOAuth2ZohoPrompt[] | null} prompt - Array of Zoho OAuth2 prompt values. "consent" means: prompt the user for consent. Pass an empty array to use the Zoho default.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Zoho>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Zoho(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
        prompt?: ProjectOAuth2ZohoPrompt[] | null,
    ): Promise<Models.OAuth2Zoho>;
    updateOAuth2Zoho(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
                  prompt?: ProjectOAuth2ZohoPrompt[] | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (boolean | null)?,
            (ProjectOAuth2ZohoPrompt[] | null)?,
        ]
    ): Promise<Models.OAuth2Zoho> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
            prompt?: ProjectOAuth2ZohoPrompt[] | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
                prompt?: ProjectOAuth2ZohoPrompt[] | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
                prompt: rest[2] as ProjectOAuth2ZohoPrompt[] | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;
        const prompt = params.prompt;

        const apiPath = '/project/oauth2/zoho';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof prompt !== 'undefined') {
            apiPayload['prompt'] = prompt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Zoho',
            models: {
                oAuth2Zoho: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                        prompt: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the project OAuth2 Zoom configuration.
     *
     * @param {string | null} params.clientId - 'Client ID' of Zoom OAuth2 app. For example: QMAC00000000000000w0AQ
     * @param {string | null} params.clientSecret - 'Client Secret' of Zoom OAuth2 app. For example: GAWsG4000000000000000000007U01ON
     * @param {boolean | null} params.enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Zoom>}
     */
    updateOAuth2Zoom(params?: {
        clientId?: string | null;
        clientSecret?: string | null;
        enabled?: boolean | null;
    }): Promise<Models.OAuth2Zoom>;
    /**
     * Update the project OAuth2 Zoom configuration.
     *
     * @param {string | null} clientId - 'Client ID' of Zoom OAuth2 app. For example: QMAC00000000000000w0AQ
     * @param {string | null} clientSecret - 'Client Secret' of Zoom OAuth2 app. For example: GAWsG4000000000000000000007U01ON
     * @param {boolean | null} enabled - OAuth2 sign-in method status. Set to true to enable new session creation. Setting to true will trigger end-to-end credentials validation, and will throw if the credentials are invalid.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Zoom>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateOAuth2Zoom(
        clientId?: string | null,
        clientSecret?: string | null,
        enabled?: boolean | null,
    ): Promise<Models.OAuth2Zoom>;
    updateOAuth2Zoom(
        paramsOrFirst?:
            | {
                  clientId?: string | null;
                  clientSecret?: string | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [(string | null)?, (boolean | null)?]
    ): Promise<Models.OAuth2Zoom> {
        let params: {
            clientId?: string | null;
            clientSecret?: string | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                clientId?: string | null;
                clientSecret?: string | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                clientId: paramsOrFirst as string | null,
                clientSecret: rest[0] as string | null,
                enabled: rest[1] as boolean | null,
            };
        }

        const clientId = params.clientId;
        const clientSecret = params.clientSecret;
        const enabled = params.enabled;

        const apiPath = '/project/oauth2/zoom';
        const apiPayload: Payload = {};
        if (typeof clientId !== 'undefined') {
            apiPayload['clientId'] = clientId;
        }
        if (typeof clientSecret !== 'undefined') {
            apiPayload['clientSecret'] = clientSecret;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oAuth2Zoom',
            models: {
                oAuth2Zoom: {
                    defaults: {
                        $id: '',
                        enabled: false,
                        clientId: '',
                        clientSecret: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a single OAuth2 provider configuration. Credential fields (client secret, p8 file, key/team IDs) are write-only and always returned empty.
     *
     * @param {ProjectOAuthProviderId} params.providerId - OAuth2 provider key. For example: github, google, apple.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Github | Models.OAuth2Discord | Models.OAuth2Figma | Models.OAuth2Dropbox | Models.OAuth2Dailymotion | Models.OAuth2Bitbucket | Models.OAuth2Bitly | Models.OAuth2Box | Models.OAuth2Autodesk | Models.OAuth2Google | Models.OAuth2Zoom | Models.OAuth2Zoho | Models.OAuth2Yandex | Models.OAuth2X | Models.OAuth2WordPress | Models.OAuth2Webflow | Models.OAuth2Twitch | Models.OAuth2Stripe | Models.OAuth2Spotify | Models.OAuth2Slack | Models.OAuth2Podio | Models.OAuth2Notion | Models.OAuth2Salesforce | Models.OAuth2Yahoo | Models.OAuth2HuggingFace | Models.OAuth2Resend | Models.OAuth2Cloudflare | Models.OAuth2Linkedin | Models.OAuth2Disqus | Models.OAuth2Amazon | Models.OAuth2Etsy | Models.OAuth2Facebook | Models.OAuth2Tradeshift | Models.OAuth2Paypal | Models.OAuth2Gitlab | Models.OAuth2Authentik | Models.OAuth2Auth0 | Models.OAuth2FusionAuth | Models.OAuth2Keycloak | Models.OAuth2Oidc | Models.OAuth2Apple | Models.OAuth2Okta | Models.OAuth2Kick | Models.OAuth2Microsoft | Models.OAuth2TikTok | Models.OAuth2Kakao>}
     */
    getOAuth2Provider(params: {
        providerId: ProjectOAuthProviderId;
    }): Promise<
        | Models.OAuth2Github
        | Models.OAuth2Discord
        | Models.OAuth2Figma
        | Models.OAuth2Dropbox
        | Models.OAuth2Dailymotion
        | Models.OAuth2Bitbucket
        | Models.OAuth2Bitly
        | Models.OAuth2Box
        | Models.OAuth2Autodesk
        | Models.OAuth2Google
        | Models.OAuth2Zoom
        | Models.OAuth2Zoho
        | Models.OAuth2Yandex
        | Models.OAuth2X
        | Models.OAuth2WordPress
        | Models.OAuth2Webflow
        | Models.OAuth2Twitch
        | Models.OAuth2Stripe
        | Models.OAuth2Spotify
        | Models.OAuth2Slack
        | Models.OAuth2Podio
        | Models.OAuth2Notion
        | Models.OAuth2Salesforce
        | Models.OAuth2Yahoo
        | Models.OAuth2HuggingFace
        | Models.OAuth2Resend
        | Models.OAuth2Cloudflare
        | Models.OAuth2Linkedin
        | Models.OAuth2Disqus
        | Models.OAuth2Amazon
        | Models.OAuth2Etsy
        | Models.OAuth2Facebook
        | Models.OAuth2Tradeshift
        | Models.OAuth2Paypal
        | Models.OAuth2Gitlab
        | Models.OAuth2Authentik
        | Models.OAuth2Auth0
        | Models.OAuth2FusionAuth
        | Models.OAuth2Keycloak
        | Models.OAuth2Oidc
        | Models.OAuth2Apple
        | Models.OAuth2Okta
        | Models.OAuth2Kick
        | Models.OAuth2Microsoft
        | Models.OAuth2TikTok
        | Models.OAuth2Kakao
    >;
    /**
     * Get a single OAuth2 provider configuration. Credential fields (client secret, p8 file, key/team IDs) are write-only and always returned empty.
     *
     * @param {ProjectOAuthProviderId} providerId - OAuth2 provider key. For example: github, google, apple.
     * @throws {AppwriteException}
     * @returns {Promise<Models.OAuth2Github | Models.OAuth2Discord | Models.OAuth2Figma | Models.OAuth2Dropbox | Models.OAuth2Dailymotion | Models.OAuth2Bitbucket | Models.OAuth2Bitly | Models.OAuth2Box | Models.OAuth2Autodesk | Models.OAuth2Google | Models.OAuth2Zoom | Models.OAuth2Zoho | Models.OAuth2Yandex | Models.OAuth2X | Models.OAuth2WordPress | Models.OAuth2Webflow | Models.OAuth2Twitch | Models.OAuth2Stripe | Models.OAuth2Spotify | Models.OAuth2Slack | Models.OAuth2Podio | Models.OAuth2Notion | Models.OAuth2Salesforce | Models.OAuth2Yahoo | Models.OAuth2HuggingFace | Models.OAuth2Resend | Models.OAuth2Cloudflare | Models.OAuth2Linkedin | Models.OAuth2Disqus | Models.OAuth2Amazon | Models.OAuth2Etsy | Models.OAuth2Facebook | Models.OAuth2Tradeshift | Models.OAuth2Paypal | Models.OAuth2Gitlab | Models.OAuth2Authentik | Models.OAuth2Auth0 | Models.OAuth2FusionAuth | Models.OAuth2Keycloak | Models.OAuth2Oidc | Models.OAuth2Apple | Models.OAuth2Okta | Models.OAuth2Kick | Models.OAuth2Microsoft | Models.OAuth2TikTok | Models.OAuth2Kakao>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getOAuth2Provider(
        providerId: ProjectOAuthProviderId,
    ): Promise<
        | Models.OAuth2Github
        | Models.OAuth2Discord
        | Models.OAuth2Figma
        | Models.OAuth2Dropbox
        | Models.OAuth2Dailymotion
        | Models.OAuth2Bitbucket
        | Models.OAuth2Bitly
        | Models.OAuth2Box
        | Models.OAuth2Autodesk
        | Models.OAuth2Google
        | Models.OAuth2Zoom
        | Models.OAuth2Zoho
        | Models.OAuth2Yandex
        | Models.OAuth2X
        | Models.OAuth2WordPress
        | Models.OAuth2Webflow
        | Models.OAuth2Twitch
        | Models.OAuth2Stripe
        | Models.OAuth2Spotify
        | Models.OAuth2Slack
        | Models.OAuth2Podio
        | Models.OAuth2Notion
        | Models.OAuth2Salesforce
        | Models.OAuth2Yahoo
        | Models.OAuth2HuggingFace
        | Models.OAuth2Resend
        | Models.OAuth2Cloudflare
        | Models.OAuth2Linkedin
        | Models.OAuth2Disqus
        | Models.OAuth2Amazon
        | Models.OAuth2Etsy
        | Models.OAuth2Facebook
        | Models.OAuth2Tradeshift
        | Models.OAuth2Paypal
        | Models.OAuth2Gitlab
        | Models.OAuth2Authentik
        | Models.OAuth2Auth0
        | Models.OAuth2FusionAuth
        | Models.OAuth2Keycloak
        | Models.OAuth2Oidc
        | Models.OAuth2Apple
        | Models.OAuth2Okta
        | Models.OAuth2Kick
        | Models.OAuth2Microsoft
        | Models.OAuth2TikTok
        | Models.OAuth2Kakao
    >;
    getOAuth2Provider(
        paramsOrFirst:
            { providerId: ProjectOAuthProviderId } | ProjectOAuthProviderId,
    ): Promise<
        | Models.OAuth2Github
        | Models.OAuth2Discord
        | Models.OAuth2Figma
        | Models.OAuth2Dropbox
        | Models.OAuth2Dailymotion
        | Models.OAuth2Bitbucket
        | Models.OAuth2Bitly
        | Models.OAuth2Box
        | Models.OAuth2Autodesk
        | Models.OAuth2Google
        | Models.OAuth2Zoom
        | Models.OAuth2Zoho
        | Models.OAuth2Yandex
        | Models.OAuth2X
        | Models.OAuth2WordPress
        | Models.OAuth2Webflow
        | Models.OAuth2Twitch
        | Models.OAuth2Stripe
        | Models.OAuth2Spotify
        | Models.OAuth2Slack
        | Models.OAuth2Podio
        | Models.OAuth2Notion
        | Models.OAuth2Salesforce
        | Models.OAuth2Yahoo
        | Models.OAuth2HuggingFace
        | Models.OAuth2Resend
        | Models.OAuth2Cloudflare
        | Models.OAuth2Linkedin
        | Models.OAuth2Disqus
        | Models.OAuth2Amazon
        | Models.OAuth2Etsy
        | Models.OAuth2Facebook
        | Models.OAuth2Tradeshift
        | Models.OAuth2Paypal
        | Models.OAuth2Gitlab
        | Models.OAuth2Authentik
        | Models.OAuth2Auth0
        | Models.OAuth2FusionAuth
        | Models.OAuth2Keycloak
        | Models.OAuth2Oidc
        | Models.OAuth2Apple
        | Models.OAuth2Okta
        | Models.OAuth2Kick
        | Models.OAuth2Microsoft
        | Models.OAuth2TikTok
        | Models.OAuth2Kakao
    > {
        let params: { providerId: ProjectOAuthProviderId };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'providerId' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as {
                providerId: ProjectOAuthProviderId;
            };
        } else {
            params = {
                providerId: paramsOrFirst as ProjectOAuthProviderId,
            };
        }

        const providerId = params.providerId;

        if (typeof providerId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "providerId"',
            );
        }
        const apiPath = '/project/oauth2/{providerId}'.replace(
            '{providerId}',
            encodeURIComponent(String(providerId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of all platforms in the project. This endpoint returns an array of all platforms and their configurations.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: type, name, hostname, bundleIdentifier, applicationId, packageIdentifierName, packageName
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformList>}
     */
    listPlatforms(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.PlatformList>;
    /**
     * Get a list of all platforms in the project. This endpoint returns an array of all platforms and their configurations.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: type, name, hostname, bundleIdentifier, applicationId, packageIdentifierName, packageName
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listPlatforms(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.PlatformList>;
    listPlatforms(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.PlatformList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/project/platforms';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformList',
            models: {
                platformList: {
                    defaults: {
                        total: 0,
                        platforms: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new Android platform for your project. Use this endpoint to register a new Android platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} params.platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.applicationId - Android application ID. Max length: 256 chars.
     * @param {string[]} params.fingerprints - SHA-256 fingerprints of the certificates your app is signed with, such as "14:6D:E9:...:2B:6A". Passkeys created in the app are accepted when its signature matches one. Find them under App signing in Play Console, or with `./gradlew signingReport`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformAndroid>}
     */
    createAndroidPlatform(params: {
        platformId: string;
        name: string;
        applicationId: string;
        fingerprints?: string[];
    }): Promise<Models.PlatformAndroid>;
    /**
     * Create a new Android platform for your project. Use this endpoint to register a new Android platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} applicationId - Android application ID. Max length: 256 chars.
     * @param {string[]} fingerprints - SHA-256 fingerprints of the certificates your app is signed with, such as "14:6D:E9:...:2B:6A". Passkeys created in the app are accepted when its signature matches one. Find them under App signing in Play Console, or with `./gradlew signingReport`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformAndroid>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createAndroidPlatform(
        platformId: string,
        name: string,
        applicationId: string,
        fingerprints?: string[],
    ): Promise<Models.PlatformAndroid>;
    createAndroidPlatform(
        paramsOrFirst:
            | {
                  platformId: string;
                  name: string;
                  applicationId: string;
                  fingerprints?: string[];
              }
            | string,
        ...rest: [string?, string?, string[]?]
    ): Promise<Models.PlatformAndroid> {
        let params: {
            platformId: string;
            name: string;
            applicationId: string;
            fingerprints?: string[];
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                applicationId: string;
                fingerprints?: string[];
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                applicationId: rest[1] as string,
                fingerprints: rest[2] as string[],
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const applicationId = params.applicationId;
        const fingerprints = params.fingerprints;

        if (typeof platformId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof applicationId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "applicationId"',
            );
        }
        const apiPath = '/project/platforms/android';
        const apiPayload: Payload = {};
        if (typeof platformId !== 'undefined') {
            apiPayload['platformId'] = platformId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof applicationId !== 'undefined') {
            apiPayload['applicationId'] = applicationId;
        }
        if (typeof fingerprints !== 'undefined') {
            apiPayload['fingerprints'] = fingerprints;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformAndroid',
            models: {
                platformAndroid: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        applicationId: '',
                        fingerprints: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an Android platform by its unique ID. Use this endpoint to update the platform's name, application ID or signing certificate fingerprints.
     *
     * @param {string} params.platformId - Platform ID.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.applicationId - Android application ID. Max length: 256 chars.
     * @param {string[] | null} params.fingerprints - SHA-256 fingerprints of the certificates your app is signed with, such as "14:6D:E9:...:2B:6A". Passkeys created in the app are accepted when its signature matches one. Find them under App signing in Play Console, or with `./gradlew signingReport`. Omit to keep the current list, or pass an empty array to clear it.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformAndroid>}
     */
    updateAndroidPlatform(params: {
        platformId: string;
        name: string;
        applicationId: string;
        fingerprints?: string[] | null;
    }): Promise<Models.PlatformAndroid>;
    /**
     * Update an Android platform by its unique ID. Use this endpoint to update the platform's name, application ID or signing certificate fingerprints.
     *
     * @param {string} platformId - Platform ID.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} applicationId - Android application ID. Max length: 256 chars.
     * @param {string[] | null} fingerprints - SHA-256 fingerprints of the certificates your app is signed with, such as "14:6D:E9:...:2B:6A". Passkeys created in the app are accepted when its signature matches one. Find them under App signing in Play Console, or with `./gradlew signingReport`. Omit to keep the current list, or pass an empty array to clear it.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformAndroid>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateAndroidPlatform(
        platformId: string,
        name: string,
        applicationId: string,
        fingerprints?: string[] | null,
    ): Promise<Models.PlatformAndroid>;
    updateAndroidPlatform(
        paramsOrFirst:
            | {
                  platformId: string;
                  name: string;
                  applicationId: string;
                  fingerprints?: string[] | null;
              }
            | string,
        ...rest: [string?, string?, (string[] | null)?]
    ): Promise<Models.PlatformAndroid> {
        let params: {
            platformId: string;
            name: string;
            applicationId: string;
            fingerprints?: string[] | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                applicationId: string;
                fingerprints?: string[] | null;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                applicationId: rest[1] as string,
                fingerprints: rest[2] as string[] | null,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const applicationId = params.applicationId;
        const fingerprints = params.fingerprints;

        if (typeof platformId === 'undefined' || platformId === '') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof applicationId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "applicationId"',
            );
        }
        const apiPath = '/project/platforms/android/{platformId}'.replace(
            '{platformId}',
            encodeURIComponent(String(platformId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof applicationId !== 'undefined') {
            apiPayload['applicationId'] = applicationId;
        }
        if (typeof fingerprints !== 'undefined') {
            apiPayload['fingerprints'] = fingerprints;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformAndroid',
            models: {
                platformAndroid: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        applicationId: '',
                        fingerprints: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new Apple platform for your project. Use this endpoint to register a new Apple platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} params.platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.bundleIdentifier - Apple bundle identifier. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformApple>}
     */
    createApplePlatform(params: {
        platformId: string;
        name: string;
        bundleIdentifier: string;
    }): Promise<Models.PlatformApple>;
    /**
     * Create a new Apple platform for your project. Use this endpoint to register a new Apple platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} bundleIdentifier - Apple bundle identifier. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformApple>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createApplePlatform(
        platformId: string,
        name: string,
        bundleIdentifier: string,
    ): Promise<Models.PlatformApple>;
    createApplePlatform(
        paramsOrFirst:
            | { platformId: string; name: string; bundleIdentifier: string }
            | string,
        ...rest: [string?, string?]
    ): Promise<Models.PlatformApple> {
        let params: {
            platformId: string;
            name: string;
            bundleIdentifier: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                bundleIdentifier: string;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                bundleIdentifier: rest[1] as string,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const bundleIdentifier = params.bundleIdentifier;

        if (typeof platformId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof bundleIdentifier === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "bundleIdentifier"',
            );
        }
        const apiPath = '/project/platforms/apple';
        const apiPayload: Payload = {};
        if (typeof platformId !== 'undefined') {
            apiPayload['platformId'] = platformId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof bundleIdentifier !== 'undefined') {
            apiPayload['bundleIdentifier'] = bundleIdentifier;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformApple',
            models: {
                platformApple: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        bundleIdentifier: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an Apple platform by its unique ID. Use this endpoint to update the platform's name or bundle identifier.
     *
     * @param {string} params.platformId - Platform ID.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.bundleIdentifier - Apple bundle identifier. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformApple>}
     */
    updateApplePlatform(params: {
        platformId: string;
        name: string;
        bundleIdentifier: string;
    }): Promise<Models.PlatformApple>;
    /**
     * Update an Apple platform by its unique ID. Use this endpoint to update the platform's name or bundle identifier.
     *
     * @param {string} platformId - Platform ID.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} bundleIdentifier - Apple bundle identifier. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformApple>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateApplePlatform(
        platformId: string,
        name: string,
        bundleIdentifier: string,
    ): Promise<Models.PlatformApple>;
    updateApplePlatform(
        paramsOrFirst:
            | { platformId: string; name: string; bundleIdentifier: string }
            | string,
        ...rest: [string?, string?]
    ): Promise<Models.PlatformApple> {
        let params: {
            platformId: string;
            name: string;
            bundleIdentifier: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                bundleIdentifier: string;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                bundleIdentifier: rest[1] as string,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const bundleIdentifier = params.bundleIdentifier;

        if (typeof platformId === 'undefined' || platformId === '') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof bundleIdentifier === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "bundleIdentifier"',
            );
        }
        const apiPath = '/project/platforms/apple/{platformId}'.replace(
            '{platformId}',
            encodeURIComponent(String(platformId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof bundleIdentifier !== 'undefined') {
            apiPayload['bundleIdentifier'] = bundleIdentifier;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformApple',
            models: {
                platformApple: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        bundleIdentifier: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new Linux platform for your project. Use this endpoint to register a new Linux platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} params.platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.packageName - Linux package name. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformLinux>}
     */
    createLinuxPlatform(params: {
        platformId: string;
        name: string;
        packageName: string;
    }): Promise<Models.PlatformLinux>;
    /**
     * Create a new Linux platform for your project. Use this endpoint to register a new Linux platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} packageName - Linux package name. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformLinux>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createLinuxPlatform(
        platformId: string,
        name: string,
        packageName: string,
    ): Promise<Models.PlatformLinux>;
    createLinuxPlatform(
        paramsOrFirst:
            { platformId: string; name: string; packageName: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.PlatformLinux> {
        let params: { platformId: string; name: string; packageName: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                packageName: string;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                packageName: rest[1] as string,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const packageName = params.packageName;

        if (typeof platformId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof packageName === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "packageName"',
            );
        }
        const apiPath = '/project/platforms/linux';
        const apiPayload: Payload = {};
        if (typeof platformId !== 'undefined') {
            apiPayload['platformId'] = platformId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof packageName !== 'undefined') {
            apiPayload['packageName'] = packageName;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformLinux',
            models: {
                platformLinux: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        packageName: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a Linux platform by its unique ID. Use this endpoint to update the platform's name or package name.
     *
     * @param {string} params.platformId - Platform ID.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.packageName - Linux package name. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformLinux>}
     */
    updateLinuxPlatform(params: {
        platformId: string;
        name: string;
        packageName: string;
    }): Promise<Models.PlatformLinux>;
    /**
     * Update a Linux platform by its unique ID. Use this endpoint to update the platform's name or package name.
     *
     * @param {string} platformId - Platform ID.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} packageName - Linux package name. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformLinux>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateLinuxPlatform(
        platformId: string,
        name: string,
        packageName: string,
    ): Promise<Models.PlatformLinux>;
    updateLinuxPlatform(
        paramsOrFirst:
            { platformId: string; name: string; packageName: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.PlatformLinux> {
        let params: { platformId: string; name: string; packageName: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                packageName: string;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                packageName: rest[1] as string,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const packageName = params.packageName;

        if (typeof platformId === 'undefined' || platformId === '') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof packageName === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "packageName"',
            );
        }
        const apiPath = '/project/platforms/linux/{platformId}'.replace(
            '{platformId}',
            encodeURIComponent(String(platformId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof packageName !== 'undefined') {
            apiPayload['packageName'] = packageName;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformLinux',
            models: {
                platformLinux: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        packageName: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new web platform for your project. Use this endpoint to register a new platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} params.platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.hostname - Platform web hostname. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWeb>}
     */
    createWebPlatform(params: {
        platformId: string;
        name: string;
        hostname: string;
    }): Promise<Models.PlatformWeb>;
    /**
     * Create a new web platform for your project. Use this endpoint to register a new platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} hostname - Platform web hostname. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWeb>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createWebPlatform(
        platformId: string,
        name: string,
        hostname: string,
    ): Promise<Models.PlatformWeb>;
    createWebPlatform(
        paramsOrFirst:
            { platformId: string; name: string; hostname: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.PlatformWeb> {
        let params: { platformId: string; name: string; hostname: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                hostname: string;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                hostname: rest[1] as string,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const hostname = params.hostname;

        if (typeof platformId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof hostname === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "hostname"',
            );
        }
        const apiPath = '/project/platforms/web';
        const apiPayload: Payload = {};
        if (typeof platformId !== 'undefined') {
            apiPayload['platformId'] = platformId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof hostname !== 'undefined') {
            apiPayload['hostname'] = hostname;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformWeb',
            models: {
                platformWeb: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        hostname: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a web platform by its unique ID. Use this endpoint to update the platform's name or hostname.
     *
     * @param {string} params.platformId - Platform ID.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.hostname - Platform web hostname. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWeb>}
     */
    updateWebPlatform(params: {
        platformId: string;
        name: string;
        hostname: string;
    }): Promise<Models.PlatformWeb>;
    /**
     * Update a web platform by its unique ID. Use this endpoint to update the platform's name or hostname.
     *
     * @param {string} platformId - Platform ID.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} hostname - Platform web hostname. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWeb>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateWebPlatform(
        platformId: string,
        name: string,
        hostname: string,
    ): Promise<Models.PlatformWeb>;
    updateWebPlatform(
        paramsOrFirst:
            { platformId: string; name: string; hostname: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.PlatformWeb> {
        let params: { platformId: string; name: string; hostname: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                hostname: string;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                hostname: rest[1] as string,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const hostname = params.hostname;

        if (typeof platformId === 'undefined' || platformId === '') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof hostname === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "hostname"',
            );
        }
        const apiPath = '/project/platforms/web/{platformId}'.replace(
            '{platformId}',
            encodeURIComponent(String(platformId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof hostname !== 'undefined') {
            apiPayload['hostname'] = hostname;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformWeb',
            models: {
                platformWeb: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        hostname: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new Windows platform for your project. Use this endpoint to register a new Windows platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} params.platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.packageIdentifierName - Windows package identifier name. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWindows>}
     */
    createWindowsPlatform(params: {
        platformId: string;
        name: string;
        packageIdentifierName: string;
    }): Promise<Models.PlatformWindows>;
    /**
     * Create a new Windows platform for your project. Use this endpoint to register a new Windows platform where your users will run your application which will interact with the Appwrite API.
     *
     * @param {string} platformId - Platform ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} packageIdentifierName - Windows package identifier name. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWindows>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createWindowsPlatform(
        platformId: string,
        name: string,
        packageIdentifierName: string,
    ): Promise<Models.PlatformWindows>;
    createWindowsPlatform(
        paramsOrFirst:
            | {
                  platformId: string;
                  name: string;
                  packageIdentifierName: string;
              }
            | string,
        ...rest: [string?, string?]
    ): Promise<Models.PlatformWindows> {
        let params: {
            platformId: string;
            name: string;
            packageIdentifierName: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                packageIdentifierName: string;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                packageIdentifierName: rest[1] as string,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const packageIdentifierName = params.packageIdentifierName;

        if (typeof platformId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof packageIdentifierName === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "packageIdentifierName"',
            );
        }
        const apiPath = '/project/platforms/windows';
        const apiPayload: Payload = {};
        if (typeof platformId !== 'undefined') {
            apiPayload['platformId'] = platformId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof packageIdentifierName !== 'undefined') {
            apiPayload['packageIdentifierName'] = packageIdentifierName;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformWindows',
            models: {
                platformWindows: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        packageIdentifierName: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a Windows platform by its unique ID. Use this endpoint to update the platform's name or package identifier name.
     *
     * @param {string} params.platformId - Platform ID.
     * @param {string} params.name - Platform name. Max length: 128 chars.
     * @param {string} params.packageIdentifierName - Windows package identifier name. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWindows>}
     */
    updateWindowsPlatform(params: {
        platformId: string;
        name: string;
        packageIdentifierName: string;
    }): Promise<Models.PlatformWindows>;
    /**
     * Update a Windows platform by its unique ID. Use this endpoint to update the platform's name or package identifier name.
     *
     * @param {string} platformId - Platform ID.
     * @param {string} name - Platform name. Max length: 128 chars.
     * @param {string} packageIdentifierName - Windows package identifier name. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWindows>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateWindowsPlatform(
        platformId: string,
        name: string,
        packageIdentifierName: string,
    ): Promise<Models.PlatformWindows>;
    updateWindowsPlatform(
        paramsOrFirst:
            | {
                  platformId: string;
                  name: string;
                  packageIdentifierName: string;
              }
            | string,
        ...rest: [string?, string?]
    ): Promise<Models.PlatformWindows> {
        let params: {
            platformId: string;
            name: string;
            packageIdentifierName: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                platformId: string;
                name: string;
                packageIdentifierName: string;
            };
        } else {
            params = {
                platformId: paramsOrFirst as string,
                name: rest[0] as string,
                packageIdentifierName: rest[1] as string,
            };
        }

        const platformId = params.platformId;
        const name = params.name;
        const packageIdentifierName = params.packageIdentifierName;

        if (typeof platformId === 'undefined' || platformId === '') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof packageIdentifierName === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "packageIdentifierName"',
            );
        }
        const apiPath = '/project/platforms/windows/{platformId}'.replace(
            '{platformId}',
            encodeURIComponent(String(platformId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof packageIdentifierName !== 'undefined') {
            apiPayload['packageIdentifierName'] = packageIdentifierName;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'platformWindows',
            models: {
                platformWindows: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        packageIdentifierName: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a platform by its unique ID. This endpoint returns the platform's details, including its name, type, and key configurations.
     *
     * @param {string} params.platformId - Platform ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWeb | Models.PlatformApple | Models.PlatformAndroid | Models.PlatformWindows | Models.PlatformLinux>}
     */
    getPlatform(params: {
        platformId: string;
    }): Promise<
        | Models.PlatformWeb
        | Models.PlatformApple
        | Models.PlatformAndroid
        | Models.PlatformWindows
        | Models.PlatformLinux
    >;
    /**
     * Get a platform by its unique ID. This endpoint returns the platform's details, including its name, type, and key configurations.
     *
     * @param {string} platformId - Platform ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PlatformWeb | Models.PlatformApple | Models.PlatformAndroid | Models.PlatformWindows | Models.PlatformLinux>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPlatform(
        platformId: string,
    ): Promise<
        | Models.PlatformWeb
        | Models.PlatformApple
        | Models.PlatformAndroid
        | Models.PlatformWindows
        | Models.PlatformLinux
    >;
    getPlatform(
        paramsOrFirst: { platformId: string } | string,
    ): Promise<
        | Models.PlatformWeb
        | Models.PlatformApple
        | Models.PlatformAndroid
        | Models.PlatformWindows
        | Models.PlatformLinux
    > {
        let params: { platformId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { platformId: string };
        } else {
            params = {
                platformId: paramsOrFirst as string,
            };
        }

        const platformId = params.platformId;

        if (typeof platformId === 'undefined' || platformId === '') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        const apiPath = '/project/platforms/{platformId}'.replace(
            '{platformId}',
            encodeURIComponent(String(platformId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete a platform by its unique ID. This endpoint removes the platform and all its configurations from the project.
     *
     * @param {string} params.platformId - Platform ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deletePlatform(params: { platformId: string }): Promise<{}>;
    /**
     * Delete a platform by its unique ID. This endpoint removes the platform and all its configurations from the project.
     *
     * @param {string} platformId - Platform ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deletePlatform(platformId: string): Promise<{}>;
    deletePlatform(
        paramsOrFirst: { platformId: string } | string,
    ): Promise<{}> {
        let params: { platformId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { platformId: string };
        } else {
            params = {
                platformId: paramsOrFirst as string,
            };
        }

        const platformId = params.platformId;

        if (typeof platformId === 'undefined' || platformId === '') {
            throw new AppwriteException(
                'Missing required parameter: "platformId"',
            );
        }
        const apiPath = '/project/platforms/{platformId}'.replace(
            '{platformId}',
            encodeURIComponent(String(platformId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of all project policies and their current configuration.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PolicyList>}
     */
    listPolicies(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.PolicyList>;
    /**
     * Get a list of all project policies and their current configuration.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PolicyList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listPolicies(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.PolicyList>;
    listPolicies(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.PolicyList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/project/policies';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'policyList',
            models: {
                policyList: {
                    defaults: {
                        total: 0,
                        policies: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Configures if aliased emails such as subaddresses and emails with suffixes are denied during new users sign-ups and email updates.
     *
     * @param {boolean} params.enabled - Set whether or not to block aliased emails during signup and email updates.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateDenyAliasedEmailPolicy(params: {
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Configures if aliased emails such as subaddresses and emails with suffixes are denied during new users sign-ups and email updates.
     *
     * @param {boolean} enabled - Set whether or not to block aliased emails during signup and email updates.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateDenyAliasedEmailPolicy(enabled: boolean): Promise<Models.Project>;
    updateDenyAliasedEmailPolicy(
        paramsOrFirst: { enabled: boolean } | boolean,
    ): Promise<Models.Project> {
        let params: { enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { enabled: boolean };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
            };
        }

        const enabled = params.enabled;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/policies/deny-aliased-email';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Configures if only corporate email addresses (non-free and non-disposable domains) are allowed during new user sign-ups and email updates.
     *
     * @param {boolean} params.enabled - Set whether or not to restrict sign-ups and email updates to corporate email addresses only.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateDenyCorporateEmailPolicy(params: {
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Configures if only corporate email addresses (non-free and non-disposable domains) are allowed during new user sign-ups and email updates.
     *
     * @param {boolean} enabled - Set whether or not to restrict sign-ups and email updates to corporate email addresses only.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateDenyCorporateEmailPolicy(enabled: boolean): Promise<Models.Project>;
    updateDenyCorporateEmailPolicy(
        paramsOrFirst: { enabled: boolean } | boolean,
    ): Promise<Models.Project> {
        let params: { enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { enabled: boolean };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
            };
        }

        const enabled = params.enabled;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/policies/deny-corporate-email';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Configures if disposable emails from known temporary domains are denied during new users sign-ups and email updates.
     *
     * @param {boolean} params.enabled - Set whether or not to block disposable email addresses during signup and email updates.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateDenyDisposableEmailPolicy(params: {
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Configures if disposable emails from known temporary domains are denied during new users sign-ups and email updates.
     *
     * @param {boolean} enabled - Set whether or not to block disposable email addresses during signup and email updates.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateDenyDisposableEmailPolicy(enabled: boolean): Promise<Models.Project>;
    updateDenyDisposableEmailPolicy(
        paramsOrFirst: { enabled: boolean } | boolean,
    ): Promise<Models.Project> {
        let params: { enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { enabled: boolean };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
            };
        }

        const enabled = params.enabled;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/policies/deny-disposable-email';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Configures if emails from free providers such as Gmail or Yahoo are denied during new users sign-ups and email updates.
     *
     * @param {boolean} params.enabled - Set whether or not to block free email addresses during signup and email updates.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateDenyFreeEmailPolicy(params: {
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Configures if emails from free providers such as Gmail or Yahoo are denied during new users sign-ups and email updates.
     *
     * @param {boolean} enabled - Set whether or not to block free email addresses during signup and email updates.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateDenyFreeEmailPolicy(enabled: boolean): Promise<Models.Project>;
    updateDenyFreeEmailPolicy(
        paramsOrFirst: { enabled: boolean } | boolean,
    ): Promise<Models.Project> {
        let params: { enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { enabled: boolean };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
            };
        }

        const enabled = params.enabled;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/policies/deny-free-email';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updating this policy allows you to control if team members can see other members information. When enabled, all team members can see ID, name, email, phone number, and MFA status of other members..
     *
     * @param {boolean} params.userId - Set to true if you want make user ID visible to all team members, or false to hide it.
     * @param {boolean} params.userEmail - Set to true if you want make user email visible to all team members, or false to hide it.
     * @param {boolean} params.userPhone - Set to true if you want make user phone number visible to all team members, or false to hide it.
     * @param {boolean} params.userName - Set to true if you want make user name visible to all team members, or false to hide it.
     * @param {boolean} params.userMFA - Set to true if you want make user MFA status visible to all team members, or false to hide it.
     * @param {boolean} params.userAccessedAt - Set to true if you want make user last access time visible to all team members, or false to hide it.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateMembershipPrivacyPolicy(params?: {
        userId?: boolean;
        userEmail?: boolean;
        userPhone?: boolean;
        userName?: boolean;
        userMFA?: boolean;
        userAccessedAt?: boolean;
    }): Promise<Models.Project>;
    /**
     * Updating this policy allows you to control if team members can see other members information. When enabled, all team members can see ID, name, email, phone number, and MFA status of other members..
     *
     * @param {boolean} userId - Set to true if you want make user ID visible to all team members, or false to hide it.
     * @param {boolean} userEmail - Set to true if you want make user email visible to all team members, or false to hide it.
     * @param {boolean} userPhone - Set to true if you want make user phone number visible to all team members, or false to hide it.
     * @param {boolean} userName - Set to true if you want make user name visible to all team members, or false to hide it.
     * @param {boolean} userMFA - Set to true if you want make user MFA status visible to all team members, or false to hide it.
     * @param {boolean} userAccessedAt - Set to true if you want make user last access time visible to all team members, or false to hide it.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMembershipPrivacyPolicy(
        userId?: boolean,
        userEmail?: boolean,
        userPhone?: boolean,
        userName?: boolean,
        userMFA?: boolean,
        userAccessedAt?: boolean,
    ): Promise<Models.Project>;
    updateMembershipPrivacyPolicy(
        paramsOrFirst?:
            | {
                  userId?: boolean;
                  userEmail?: boolean;
                  userPhone?: boolean;
                  userName?: boolean;
                  userMFA?: boolean;
                  userAccessedAt?: boolean;
              }
            | boolean,
        ...rest: [boolean?, boolean?, boolean?, boolean?, boolean?]
    ): Promise<Models.Project> {
        let params: {
            userId?: boolean;
            userEmail?: boolean;
            userPhone?: boolean;
            userName?: boolean;
            userMFA?: boolean;
            userAccessedAt?: boolean;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                userId?: boolean;
                userEmail?: boolean;
                userPhone?: boolean;
                userName?: boolean;
                userMFA?: boolean;
                userAccessedAt?: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as boolean,
                userEmail: rest[0] as boolean,
                userPhone: rest[1] as boolean,
                userName: rest[2] as boolean,
                userMFA: rest[3] as boolean,
                userAccessedAt: rest[4] as boolean,
            };
        }

        const userId = params.userId;
        const userEmail = params.userEmail;
        const userPhone = params.userPhone;
        const userName = params.userName;
        const userMFA = params.userMFA;
        const userAccessedAt = params.userAccessedAt;

        const apiPath = '/project/policies/membership-privacy';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof userEmail !== 'undefined') {
            apiPayload['userEmail'] = userEmail;
        }
        if (typeof userPhone !== 'undefined') {
            apiPayload['userPhone'] = userPhone;
        }
        if (typeof userName !== 'undefined') {
            apiPayload['userName'] = userName;
        }
        if (typeof userMFA !== 'undefined') {
            apiPayload['userMFA'] = userMFA;
        }
        if (typeof userAccessedAt !== 'undefined') {
            apiPayload['userAccessedAt'] = userAccessedAt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updating this policy allows you to control which factors users can use to complete an MFA challenge. Disabled factors cannot be used to create a challenge and are reported as unavailable when listing factors. The custom factor is disabled by default; enable it to deliver challenge codes through your own channel. Recovery codes always remain available as a fallback.
     *
     * @param {boolean} params.totp - Set to true to allow TOTP to complete an MFA challenge, or false to disable it.
     * @param {boolean} params.email - Set to true to allow email to complete an MFA challenge, or false to disable it.
     * @param {boolean} params.phone - Set to true to allow phone (SMS) to complete an MFA challenge, or false to disable it.
     * @param {boolean} params.custom - Set to true to allow the custom factor to complete an MFA challenge, or false to disable it.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateMFAFactorsPolicy(params?: {
        totp?: boolean;
        email?: boolean;
        phone?: boolean;
        custom?: boolean;
    }): Promise<Models.Project>;
    /**
     * Updating this policy allows you to control which factors users can use to complete an MFA challenge. Disabled factors cannot be used to create a challenge and are reported as unavailable when listing factors. The custom factor is disabled by default; enable it to deliver challenge codes through your own channel. Recovery codes always remain available as a fallback.
     *
     * @param {boolean} totp - Set to true to allow TOTP to complete an MFA challenge, or false to disable it.
     * @param {boolean} email - Set to true to allow email to complete an MFA challenge, or false to disable it.
     * @param {boolean} phone - Set to true to allow phone (SMS) to complete an MFA challenge, or false to disable it.
     * @param {boolean} custom - Set to true to allow the custom factor to complete an MFA challenge, or false to disable it.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMFAFactorsPolicy(
        totp?: boolean,
        email?: boolean,
        phone?: boolean,
        custom?: boolean,
    ): Promise<Models.Project>;
    updateMFAFactorsPolicy(
        paramsOrFirst?:
            | {
                  totp?: boolean;
                  email?: boolean;
                  phone?: boolean;
                  custom?: boolean;
              }
            | boolean,
        ...rest: [boolean?, boolean?, boolean?]
    ): Promise<Models.Project> {
        let params: {
            totp?: boolean;
            email?: boolean;
            phone?: boolean;
            custom?: boolean;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                totp?: boolean;
                email?: boolean;
                phone?: boolean;
                custom?: boolean;
            };
        } else {
            params = {
                totp: paramsOrFirst as boolean,
                email: rest[0] as boolean,
                phone: rest[1] as boolean,
                custom: rest[2] as boolean,
            };
        }

        const totp = params.totp;
        const email = params.email;
        const phone = params.phone;
        const custom = params.custom;

        const apiPath = '/project/policies/mfa-factors';
        const apiPayload: Payload = {};
        if (typeof totp !== 'undefined') {
            apiPayload['totp'] = totp;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof phone !== 'undefined') {
            apiPayload['phone'] = phone;
        }
        if (typeof custom !== 'undefined') {
            apiPayload['custom'] = custom;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Set the relying party ID passkeys are bound to: the domain of your application. Passkeys work on the project's web platforms on that domain or its subdomains, and on the domain itself for Apple apps. A localhost web platform enables passkeys for local development without a relying party ID. The relying party ID cannot change while users have passkeys, and any change invalidates ceremonies in progress.
     *
     * @param {string} params.rpId - Relying party ID: the domain of your application, such as `example.com`. Use `localhost` for local development, or an empty string to unset it.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updatePasskeyPolicy(params: { rpId: string }): Promise<Models.Project>;
    /**
     * Set the relying party ID passkeys are bound to: the domain of your application. Passkeys work on the project's web platforms on that domain or its subdomains, and on the domain itself for Apple apps. A localhost web platform enables passkeys for local development without a relying party ID. The relying party ID cannot change while users have passkeys, and any change invalidates ceremonies in progress.
     *
     * @param {string} rpId - Relying party ID: the domain of your application, such as `example.com`. Use `localhost` for local development, or an empty string to unset it.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasskeyPolicy(rpId: string): Promise<Models.Project>;
    updatePasskeyPolicy(
        paramsOrFirst: { rpId: string } | string,
    ): Promise<Models.Project> {
        let params: { rpId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { rpId: string };
        } else {
            params = {
                rpId: paramsOrFirst as string,
            };
        }

        const rpId = params.rpId;

        if (typeof rpId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "rpId"');
        }
        const apiPath = '/project/policies/passkey';
        const apiPayload: Payload = {};
        if (typeof rpId !== 'undefined') {
            apiPayload['rpId'] = rpId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updating this policy allows you to control if new passwords are checked against most common passwords dictionary. When enabled, and user changes their password, password must not be contained in the dictionary.
     *
     * Custom denyList entries use case-sensitive equality and apply only while this policy is enabled. Omit denyList or pass null to preserve it, pass an array to replace it, or pass [] to clear it. Entries are stored without trimming or deduplication. Existing passwords remain valid for sign-in and password confirmation. The combined serialized project authentication configuration must fit within 16,384 characters, even when each list meets its entry limits.
     *
     * @param {boolean} params.enabled - Toggle password dictionary policy. Set to true if you want password change to block passwords in the dictionary, or false to allow them. When changing this policy, existing passwords remain valid.
     * @param {string[] | null} params.denyList - Custom blocked values using case-sensitive equality. Up to 100 entries of 1 to 256 characters. Omit or pass null to preserve, an array to replace, or [] to clear. Combined authentication settings must fit within 16,384 characters.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updatePasswordDictionaryPolicy(params: {
        enabled: boolean;
        denyList?: string[] | null;
    }): Promise<Models.Project>;
    /**
     * Updating this policy allows you to control if new passwords are checked against most common passwords dictionary. When enabled, and user changes their password, password must not be contained in the dictionary.
     *
     * Custom denyList entries use case-sensitive equality and apply only while this policy is enabled. Omit denyList or pass null to preserve it, pass an array to replace it, or pass [] to clear it. Entries are stored without trimming or deduplication. Existing passwords remain valid for sign-in and password confirmation. The combined serialized project authentication configuration must fit within 16,384 characters, even when each list meets its entry limits.
     *
     * @param {boolean} enabled - Toggle password dictionary policy. Set to true if you want password change to block passwords in the dictionary, or false to allow them. When changing this policy, existing passwords remain valid.
     * @param {string[] | null} denyList - Custom blocked values using case-sensitive equality. Up to 100 entries of 1 to 256 characters. Omit or pass null to preserve, an array to replace, or [] to clear. Combined authentication settings must fit within 16,384 characters.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasswordDictionaryPolicy(
        enabled: boolean,
        denyList?: string[] | null,
    ): Promise<Models.Project>;
    updatePasswordDictionaryPolicy(
        paramsOrFirst:
            { enabled: boolean; denyList?: string[] | null } | boolean,
        ...rest: [(string[] | null)?]
    ): Promise<Models.Project> {
        let params: { enabled: boolean; denyList?: string[] | null };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                enabled: boolean;
                denyList?: string[] | null;
            };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
                denyList: rest[0] as string[] | null,
            };
        }

        const enabled = params.enabled;
        const denyList = params.denyList;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/policies/password-dictionary';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof denyList !== 'undefined') {
            apiPayload['denyList'] = denyList;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updates one of password strength policies. Based on total length configured, previous password hashes are stored, and users cannot choose a new password that is already stored in the passwird history list, when updating an user password, or setting new one through password recovery.
     *
     * Keep in mind, while password history policy is disabled, the history is not being stored. Enabling the policy will not have any history on existing users, and it will only start to collect and enforce the policy on password changes since the policy is enabled.
     *
     * @param {number | null} params.total - Set the password history length per user. Value can be between 1 and 20, or null to disable the limit.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updatePasswordHistoryPolicy(params: {
        total?: number | null;
    }): Promise<Models.Project>;
    /**
     * Updates one of password strength policies. Based on total length configured, previous password hashes are stored, and users cannot choose a new password that is already stored in the passwird history list, when updating an user password, or setting new one through password recovery.
     *
     * Keep in mind, while password history policy is disabled, the history is not being stored. Enabling the policy will not have any history on existing users, and it will only start to collect and enforce the policy on password changes since the policy is enabled.
     *
     * @param {number | null} total - Set the password history length per user. Value can be between 1 and 20, or null to disable the limit.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasswordHistoryPolicy(total?: number | null): Promise<Models.Project>;
    updatePasswordHistoryPolicy(
        paramsOrFirst?: { total?: number | null } | number | null,
    ): Promise<Models.Project> {
        let params: { total?: number | null };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { total?: number | null };
        } else {
            params = {
                total: paramsOrFirst as number | null,
            };
        }

        const total = params.total;

        if (typeof total === 'undefined') {
            throw new AppwriteException('Missing required parameter: "total"');
        }
        const apiPath = '/project/policies/password-history';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updating this policy allows you to control if password strength is checked against personal data. When enabled, and user sets or changes their password, the password must not contain user ID, name, email or phone number.
     *
     * Custom denyList entries use case-insensitive substring matching and apply only while this policy is enabled. Omit denyList or pass null to preserve it, pass an array to replace it, or pass [] to clear it. Entries are stored without trimming or deduplication. Existing passwords remain valid for sign-in and password confirmation. The combined serialized project authentication configuration must fit within 16,384 characters, even when each list meets its entry limits.
     * Password recovery also checks the recovered user profile, closing the previous gap in profile-based personal-data validation.
     *
     * @param {boolean} params.enabled - Toggle password personal data policy. Set to true if you want to block passwords including user's personal data, or false to allow it. When changing this policy, existing passwords remain valid.
     * @param {string[] | null} params.denyList - Custom blocked values using case-insensitive substring matching. Up to 100 entries of 1 to 256 characters. Omit or pass null to preserve, an array to replace, or [] to clear. Combined authentication settings must fit within 16,384 characters.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updatePasswordPersonalDataPolicy(params: {
        enabled: boolean;
        denyList?: string[] | null;
    }): Promise<Models.Project>;
    /**
     * Updating this policy allows you to control if password strength is checked against personal data. When enabled, and user sets or changes their password, the password must not contain user ID, name, email or phone number.
     *
     * Custom denyList entries use case-insensitive substring matching and apply only while this policy is enabled. Omit denyList or pass null to preserve it, pass an array to replace it, or pass [] to clear it. Entries are stored without trimming or deduplication. Existing passwords remain valid for sign-in and password confirmation. The combined serialized project authentication configuration must fit within 16,384 characters, even when each list meets its entry limits.
     * Password recovery also checks the recovered user profile, closing the previous gap in profile-based personal-data validation.
     *
     * @param {boolean} enabled - Toggle password personal data policy. Set to true if you want to block passwords including user's personal data, or false to allow it. When changing this policy, existing passwords remain valid.
     * @param {string[] | null} denyList - Custom blocked values using case-insensitive substring matching. Up to 100 entries of 1 to 256 characters. Omit or pass null to preserve, an array to replace, or [] to clear. Combined authentication settings must fit within 16,384 characters.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasswordPersonalDataPolicy(
        enabled: boolean,
        denyList?: string[] | null,
    ): Promise<Models.Project>;
    updatePasswordPersonalDataPolicy(
        paramsOrFirst:
            { enabled: boolean; denyList?: string[] | null } | boolean,
        ...rest: [(string[] | null)?]
    ): Promise<Models.Project> {
        let params: { enabled: boolean; denyList?: string[] | null };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                enabled: boolean;
                denyList?: string[] | null;
            };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
                denyList: rest[0] as string[] | null,
            };
        }

        const enabled = params.enabled;
        const denyList = params.denyList;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/policies/password-personal-data';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof denyList !== 'undefined') {
            apiPayload['denyList'] = denyList;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updating this policy allows you to control if passwords are checked against the Have I Been Pwned breach database. When enabled, every password a user signs up, signs in or resets with is checked and the result is recorded on the user as `passwordPwned`. On its own the policy only records. Enable `users` to reject a breached password when a user signs up or sets a new password, and `sessions` to refuse a sign-in with a breached password until it is reset. Only the first five characters of the password hash are ever shared with the service. Options left out keep their current value.
     *
     * @param {boolean} params.enabled - Toggle password pwned policy. Set to true to check passwords against known data breaches and record the result on the user, or false to never check. Default is true. On its own this only records; use `users` and `sessions` to block. When changing this policy, existing passwords remain valid.
     * @param {boolean} params.sessions - Whether a sign-in with a breached password is refused until the password is reset. Default is false, which allows the sign-in and only records the result.
     * @param {boolean} params.users - Whether a breached password is rejected when a user signs up or sets a new password. Default is false, which allows the password and only records the result.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updatePasswordPwnedPolicy(params?: {
        enabled?: boolean;
        sessions?: boolean;
        users?: boolean;
    }): Promise<Models.Project>;
    /**
     * Updating this policy allows you to control if passwords are checked against the Have I Been Pwned breach database. When enabled, every password a user signs up, signs in or resets with is checked and the result is recorded on the user as `passwordPwned`. On its own the policy only records. Enable `users` to reject a breached password when a user signs up or sets a new password, and `sessions` to refuse a sign-in with a breached password until it is reset. Only the first five characters of the password hash are ever shared with the service. Options left out keep their current value.
     *
     * @param {boolean} enabled - Toggle password pwned policy. Set to true to check passwords against known data breaches and record the result on the user, or false to never check. Default is true. On its own this only records; use `users` and `sessions` to block. When changing this policy, existing passwords remain valid.
     * @param {boolean} sessions - Whether a sign-in with a breached password is refused until the password is reset. Default is false, which allows the sign-in and only records the result.
     * @param {boolean} users - Whether a breached password is rejected when a user signs up or sets a new password. Default is false, which allows the password and only records the result.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasswordPwnedPolicy(
        enabled?: boolean,
        sessions?: boolean,
        users?: boolean,
    ): Promise<Models.Project>;
    updatePasswordPwnedPolicy(
        paramsOrFirst?:
            | { enabled?: boolean; sessions?: boolean; users?: boolean }
            | boolean,
        ...rest: [boolean?, boolean?]
    ): Promise<Models.Project> {
        let params: { enabled?: boolean; sessions?: boolean; users?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                enabled?: boolean;
                sessions?: boolean;
                users?: boolean;
            };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
                sessions: rest[0] as boolean,
                users: rest[1] as boolean,
            };
        }

        const enabled = params.enabled;
        const sessions = params.sessions;
        const users = params.users;

        const apiPath = '/project/policies/password-pwned';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof sessions !== 'undefined') {
            apiPayload['sessions'] = sessions;
        }
        if (typeof users !== 'undefined') {
            apiPayload['users'] = users;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Require a password reset when an email/password sign-in uses a password older than the configured maximum age. Options left out keep their current value.
     *
     * @param {boolean} params.enabled - Enable password rotation. Disabled by default.
     * @param {number} params.maxAge - Maximum password age in seconds. Minimum allowed value is 86400 seconds (1 day), and maximum is 10 years, which is 315360000 seconds. Defaults to 1 year, which is 31536000 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updatePasswordRotationPolicy(params?: {
        enabled?: boolean;
        maxAge?: number;
    }): Promise<Models.Project>;
    /**
     * Require a password reset when an email/password sign-in uses a password older than the configured maximum age. Options left out keep their current value.
     *
     * @param {boolean} enabled - Enable password rotation. Disabled by default.
     * @param {number} maxAge - Maximum password age in seconds. Minimum allowed value is 86400 seconds (1 day), and maximum is 10 years, which is 315360000 seconds. Defaults to 1 year, which is 31536000 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasswordRotationPolicy(
        enabled?: boolean,
        maxAge?: number,
    ): Promise<Models.Project>;
    updatePasswordRotationPolicy(
        paramsOrFirst?: { enabled?: boolean; maxAge?: number } | boolean,
        ...rest: [number?]
    ): Promise<Models.Project> {
        let params: { enabled?: boolean; maxAge?: number };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                enabled?: boolean;
                maxAge?: number;
            };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
                maxAge: rest[0] as number,
            };
        }

        const enabled = params.enabled;
        const maxAge = params.maxAge;

        const apiPath = '/project/policies/password-rotation';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof maxAge !== 'undefined') {
            apiPayload['maxAge'] = maxAge;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the password strength requirements for users in the project.
     *
     * @param {number} params.min - Minimum password length. Value must be between 8 and 256. Default is 8.
     * @param {boolean} params.uppercase - Whether passwords must include at least one uppercase letter.
     * @param {boolean} params.lowercase - Whether passwords must include at least one lowercase letter.
     * @param {boolean} params.number - Whether passwords must include at least one number.
     * @param {boolean} params.symbols - Whether passwords must include at least one symbol.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PolicyPasswordStrength>}
     */
    updatePasswordStrengthPolicy(params?: {
        min?: number;
        uppercase?: boolean;
        lowercase?: boolean;
        number?: boolean;
        symbols?: boolean;
    }): Promise<Models.PolicyPasswordStrength>;
    /**
     * Update the password strength requirements for users in the project.
     *
     * @param {number} min - Minimum password length. Value must be between 8 and 256. Default is 8.
     * @param {boolean} uppercase - Whether passwords must include at least one uppercase letter.
     * @param {boolean} lowercase - Whether passwords must include at least one lowercase letter.
     * @param {boolean} number - Whether passwords must include at least one number.
     * @param {boolean} symbols - Whether passwords must include at least one symbol.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PolicyPasswordStrength>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasswordStrengthPolicy(
        min?: number,
        uppercase?: boolean,
        lowercase?: boolean,
        number?: boolean,
        symbols?: boolean,
    ): Promise<Models.PolicyPasswordStrength>;
    updatePasswordStrengthPolicy(
        paramsOrFirst?:
            | {
                  min?: number;
                  uppercase?: boolean;
                  lowercase?: boolean;
                  number?: boolean;
                  symbols?: boolean;
              }
            | number,
        ...rest: [boolean?, boolean?, boolean?, boolean?]
    ): Promise<Models.PolicyPasswordStrength> {
        let params: {
            min?: number;
            uppercase?: boolean;
            lowercase?: boolean;
            number?: boolean;
            symbols?: boolean;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                min?: number;
                uppercase?: boolean;
                lowercase?: boolean;
                number?: boolean;
                symbols?: boolean;
            };
        } else {
            params = {
                min: paramsOrFirst as number,
                uppercase: rest[0] as boolean,
                lowercase: rest[1] as boolean,
                number: rest[2] as boolean,
                symbols: rest[3] as boolean,
            };
        }

        const min = params.min;
        const uppercase = params.uppercase;
        const lowercase = params.lowercase;
        const number = params.number;
        const symbols = params.symbols;

        const apiPath = '/project/policies/password-strength';
        const apiPayload: Payload = {};
        if (typeof min !== 'undefined') {
            apiPayload['min'] = min;
        }
        if (typeof uppercase !== 'undefined') {
            apiPayload['uppercase'] = uppercase;
        }
        if (typeof lowercase !== 'undefined') {
            apiPayload['lowercase'] = lowercase;
        }
        if (typeof number !== 'undefined') {
            apiPayload['number'] = number;
        }
        if (typeof symbols !== 'undefined') {
            apiPayload['symbols'] = symbols;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'policyPasswordStrength',
            models: {
                policyPasswordStrength: {
                    defaults: {
                        $id: '',
                        min: 8,
                        uppercase: false,
                        lowercase: false,
                        number: false,
                        symbols: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updating this policy allows you to control if email alert is sent upon session creation. When enabled, and user signs into their account, they will be sent an email notification. There is an exception, the first session after a new sign up does not trigger an alert, even if the policy is enabled.
     *
     * @param {boolean} params.enabled - Toggle session alert policy. Set to true if you want users to receive email notifications when a sessions are created for their users, or false to not send email alerts.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateSessionAlertPolicy(params: {
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Updating this policy allows you to control if email alert is sent upon session creation. When enabled, and user signs into their account, they will be sent an email notification. There is an exception, the first session after a new sign up does not trigger an alert, even if the policy is enabled.
     *
     * @param {boolean} enabled - Toggle session alert policy. Set to true if you want users to receive email notifications when a sessions are created for their users, or false to not send email alerts.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateSessionAlertPolicy(enabled: boolean): Promise<Models.Project>;
    updateSessionAlertPolicy(
        paramsOrFirst: { enabled: boolean } | boolean,
    ): Promise<Models.Project> {
        let params: { enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { enabled: boolean };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
            };
        }

        const enabled = params.enabled;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/policies/session-alert';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update maximum duration how long sessions created within a project should stay active for.
     *
     * @param {number} params.duration - Maximum session length in seconds. Minium allowed value is 60 seconds, and maximum is 1 year, which is 31536000 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateSessionDurationPolicy(params: {
        duration: number;
    }): Promise<Models.Project>;
    /**
     * Update maximum duration how long sessions created within a project should stay active for.
     *
     * @param {number} duration - Maximum session length in seconds. Minium allowed value is 60 seconds, and maximum is 1 year, which is 31536000 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateSessionDurationPolicy(duration: number): Promise<Models.Project>;
    updateSessionDurationPolicy(
        paramsOrFirst: { duration: number } | number,
    ): Promise<Models.Project> {
        let params: { duration: number };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { duration: number };
        } else {
            params = {
                duration: paramsOrFirst as number,
            };
        }

        const duration = params.duration;

        if (typeof duration === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "duration"',
            );
        }
        const apiPath = '/project/policies/session-duration';
        const apiPayload: Payload = {};
        if (typeof duration !== 'undefined') {
            apiPayload['duration'] = duration;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Updating this policy allows you to control if existing sessions should be invalidated when a password of a user is changed. When enabled, and user changes their password, they will be logged out of all their devices.
     *
     * @param {boolean} params.enabled - Toggle session invalidation policy. Set to true if you want password change to invalidate all sessions of an user, or false to keep sessions active.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateSessionInvalidationPolicy(params: {
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Updating this policy allows you to control if existing sessions should be invalidated when a password of a user is changed. When enabled, and user changes their password, they will be logged out of all their devices.
     *
     * @param {boolean} enabled - Toggle session invalidation policy. Set to true if you want password change to invalidate all sessions of an user, or false to keep sessions active.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateSessionInvalidationPolicy(enabled: boolean): Promise<Models.Project>;
    updateSessionInvalidationPolicy(
        paramsOrFirst: { enabled: boolean } | boolean,
    ): Promise<Models.Project> {
        let params: { enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { enabled: boolean };
        } else {
            params = {
                enabled: paramsOrFirst as boolean,
            };
        }

        const enabled = params.enabled;

        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/policies/session-invalidation';
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the maximum number of sessions allowed per user. When the limit is hit, the oldest session will be deleted to make room for new one.
     *
     * @param {number} params.total - Set the maximum number of sessions allowed per user. Value can be between 1 and 100.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateSessionLimitPolicy(params: {
        total: number;
    }): Promise<Models.Project>;
    /**
     * Update the maximum number of sessions allowed per user. When the limit is hit, the oldest session will be deleted to make room for new one.
     *
     * @param {number} total - Set the maximum number of sessions allowed per user. Value can be between 1 and 100.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateSessionLimitPolicy(total: number): Promise<Models.Project>;
    updateSessionLimitPolicy(
        paramsOrFirst: { total: number } | number,
    ): Promise<Models.Project> {
        let params: { total: number };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { total: number };
        } else {
            params = {
                total: paramsOrFirst as number,
            };
        }

        const total = params.total;

        if (typeof total === 'undefined') {
            throw new AppwriteException('Missing required parameter: "total"');
        }
        const apiPath = '/project/policies/session-limit';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the maximum number of users in the project. When the limit is hit or amount of existing users already exceeded the limit, all users remain active, but new user sign up will be prohibited.
     *
     * @param {number | null} params.total - Set the maximum number of users allowed in the project. Value can be between 0 and 10000. Use 0 or null to disable the limit.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateUserLimitPolicy(params: {
        total?: number | null;
    }): Promise<Models.Project>;
    /**
     * Update the maximum number of users in the project. When the limit is hit or amount of existing users already exceeded the limit, all users remain active, but new user sign up will be prohibited.
     *
     * @param {number | null} total - Set the maximum number of users allowed in the project. Value can be between 0 and 10000. Use 0 or null to disable the limit.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateUserLimitPolicy(total?: number | null): Promise<Models.Project>;
    updateUserLimitPolicy(
        paramsOrFirst?: { total?: number | null } | number | null,
    ): Promise<Models.Project> {
        let params: { total?: number | null };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { total?: number | null };
        } else {
            params = {
                total: paramsOrFirst as number | null,
            };
        }

        const total = params.total;

        if (typeof total === 'undefined') {
            throw new AppwriteException('Missing required parameter: "total"');
        }
        const apiPath = '/project/policies/user-limit';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a policy by its unique ID. This endpoint returns the current configuration for the requested project policy.
     *
     * @param {ProjectPolicyId} params.policyId - Policy ID. Can be one of: password-dictionary, password-history, password-strength, password-personal-data, password-pwned, password-rotation, session-alert, session-duration, session-invalidation, session-limit, user-limit, membership-privacy, mfa-factors, deny-aliased-email, deny-disposable-email, deny-free-email, deny-corporate-email, passkey.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PolicyPasswordDictionary | Models.PolicyPasswordHistory | Models.PolicyPasswordStrength | Models.PolicyPasswordPersonalData | Models.PolicyPasswordPwned | Models.PolicyPasswordRotation | Models.PolicySessionAlert | Models.PolicySessionDuration | Models.PolicySessionInvalidation | Models.PolicySessionLimit | Models.PolicyUserLimit | Models.PolicyMembershipPrivacy | Models.PolicyMfaFactors | Models.PolicyDenyAliasedEmail | Models.PolicyDenyDisposableEmail | Models.PolicyDenyFreeEmail | Models.PolicyDenyCorporateEmail | Models.PolicyPasskey>}
     */
    getPolicy(params: {
        policyId: ProjectPolicyId;
    }): Promise<
        | Models.PolicyPasswordDictionary
        | Models.PolicyPasswordHistory
        | Models.PolicyPasswordStrength
        | Models.PolicyPasswordPersonalData
        | Models.PolicyPasswordPwned
        | Models.PolicyPasswordRotation
        | Models.PolicySessionAlert
        | Models.PolicySessionDuration
        | Models.PolicySessionInvalidation
        | Models.PolicySessionLimit
        | Models.PolicyUserLimit
        | Models.PolicyMembershipPrivacy
        | Models.PolicyMfaFactors
        | Models.PolicyDenyAliasedEmail
        | Models.PolicyDenyDisposableEmail
        | Models.PolicyDenyFreeEmail
        | Models.PolicyDenyCorporateEmail
        | Models.PolicyPasskey
    >;
    /**
     * Get a policy by its unique ID. This endpoint returns the current configuration for the requested project policy.
     *
     * @param {ProjectPolicyId} policyId - Policy ID. Can be one of: password-dictionary, password-history, password-strength, password-personal-data, password-pwned, password-rotation, session-alert, session-duration, session-invalidation, session-limit, user-limit, membership-privacy, mfa-factors, deny-aliased-email, deny-disposable-email, deny-free-email, deny-corporate-email, passkey.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PolicyPasswordDictionary | Models.PolicyPasswordHistory | Models.PolicyPasswordStrength | Models.PolicyPasswordPersonalData | Models.PolicyPasswordPwned | Models.PolicyPasswordRotation | Models.PolicySessionAlert | Models.PolicySessionDuration | Models.PolicySessionInvalidation | Models.PolicySessionLimit | Models.PolicyUserLimit | Models.PolicyMembershipPrivacy | Models.PolicyMfaFactors | Models.PolicyDenyAliasedEmail | Models.PolicyDenyDisposableEmail | Models.PolicyDenyFreeEmail | Models.PolicyDenyCorporateEmail | Models.PolicyPasskey>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPolicy(
        policyId: ProjectPolicyId,
    ): Promise<
        | Models.PolicyPasswordDictionary
        | Models.PolicyPasswordHistory
        | Models.PolicyPasswordStrength
        | Models.PolicyPasswordPersonalData
        | Models.PolicyPasswordPwned
        | Models.PolicyPasswordRotation
        | Models.PolicySessionAlert
        | Models.PolicySessionDuration
        | Models.PolicySessionInvalidation
        | Models.PolicySessionLimit
        | Models.PolicyUserLimit
        | Models.PolicyMembershipPrivacy
        | Models.PolicyMfaFactors
        | Models.PolicyDenyAliasedEmail
        | Models.PolicyDenyDisposableEmail
        | Models.PolicyDenyFreeEmail
        | Models.PolicyDenyCorporateEmail
        | Models.PolicyPasskey
    >;
    getPolicy(
        paramsOrFirst: { policyId: ProjectPolicyId } | ProjectPolicyId,
    ): Promise<
        | Models.PolicyPasswordDictionary
        | Models.PolicyPasswordHistory
        | Models.PolicyPasswordStrength
        | Models.PolicyPasswordPersonalData
        | Models.PolicyPasswordPwned
        | Models.PolicyPasswordRotation
        | Models.PolicySessionAlert
        | Models.PolicySessionDuration
        | Models.PolicySessionInvalidation
        | Models.PolicySessionLimit
        | Models.PolicyUserLimit
        | Models.PolicyMembershipPrivacy
        | Models.PolicyMfaFactors
        | Models.PolicyDenyAliasedEmail
        | Models.PolicyDenyDisposableEmail
        | Models.PolicyDenyFreeEmail
        | Models.PolicyDenyCorporateEmail
        | Models.PolicyPasskey
    > {
        let params: { policyId: ProjectPolicyId };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'policyId' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as { policyId: ProjectPolicyId };
        } else {
            params = {
                policyId: paramsOrFirst as ProjectPolicyId,
            };
        }

        const policyId = params.policyId;

        if (typeof policyId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "policyId"',
            );
        }
        const apiPath = '/project/policies/{policyId}'.replace(
            '{policyId}',
            encodeURIComponent(String(policyId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Update properties of a specific protocol. Use this endpoint to enable or disable a protocol in your project.
     *
     * @param {ProjectProtocolId} params.protocolId - Protocol name. Can be one of: rest, graphql, websocket
     * @param {boolean} params.enabled - Protocol status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateProtocol(params: {
        protocolId: ProjectProtocolId;
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Update properties of a specific protocol. Use this endpoint to enable or disable a protocol in your project.
     *
     * @param {ProjectProtocolId} protocolId - Protocol name. Can be one of: rest, graphql, websocket
     * @param {boolean} enabled - Protocol status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateProtocol(
        protocolId: ProjectProtocolId,
        enabled: boolean,
    ): Promise<Models.Project>;
    updateProtocol(
        paramsOrFirst:
            | { protocolId: ProjectProtocolId; enabled: boolean }
            | ProjectProtocolId,
        ...rest: [boolean?]
    ): Promise<Models.Project> {
        let params: { protocolId: ProjectProtocolId; enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('protocolId' in paramsOrFirst || 'enabled' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                protocolId: ProjectProtocolId;
                enabled: boolean;
            };
        } else {
            params = {
                protocolId: paramsOrFirst as ProjectProtocolId,
                enabled: rest[0] as boolean,
            };
        }

        const protocolId = params.protocolId;
        const enabled = params.enabled;

        if (typeof protocolId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "protocolId"',
            );
        }
        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/protocols/{protocolId}'.replace(
            '{protocolId}',
            encodeURIComponent(String(protocolId)),
        );
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update properties of a specific service. Use this endpoint to enable or disable a service in your project.
     *
     * @param {ProjectServiceId} params.serviceId - Service name. Can be one of: account, avatars, databases, tablesdb, locale, health, project, storage, teams, users, vcs, sites, functions, proxy, graphql, migrations, messaging, advisor, oauth2
     * @param {boolean} params.enabled - Service status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateService(params: {
        serviceId: ProjectServiceId;
        enabled: boolean;
    }): Promise<Models.Project>;
    /**
     * Update properties of a specific service. Use this endpoint to enable or disable a service in your project.
     *
     * @param {ProjectServiceId} serviceId - Service name. Can be one of: account, avatars, databases, tablesdb, locale, health, project, storage, teams, users, vcs, sites, functions, proxy, graphql, migrations, messaging, advisor, oauth2
     * @param {boolean} enabled - Service status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateService(
        serviceId: ProjectServiceId,
        enabled: boolean,
    ): Promise<Models.Project>;
    updateService(
        paramsOrFirst:
            | { serviceId: ProjectServiceId; enabled: boolean }
            | ProjectServiceId,
        ...rest: [boolean?]
    ): Promise<Models.Project> {
        let params: { serviceId: ProjectServiceId; enabled: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('serviceId' in paramsOrFirst || 'enabled' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                serviceId: ProjectServiceId;
                enabled: boolean;
            };
        } else {
            params = {
                serviceId: paramsOrFirst as ProjectServiceId,
                enabled: rest[0] as boolean,
            };
        }

        const serviceId = params.serviceId;
        const enabled = params.enabled;

        if (typeof serviceId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "serviceId"',
            );
        }
        if (typeof enabled === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "enabled"',
            );
        }
        const apiPath = '/project/services/{serviceId}'.replace(
            '{serviceId}',
            encodeURIComponent(String(serviceId)),
        );
        const apiPayload: Payload = {};
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the SMTP configuration for your project. Use this endpoint to configure your project's SMTP provider with your custom settings for sending transactional emails.
     *
     * @param {string | null} params.host - SMTP server hostname (domain)
     * @param {number | null} params.port - SMTP server port
     * @param {string | null} params.username - SMTP server username. Pass an empty string to clear a previously set value.
     * @param {string | null} params.password - SMTP server password. Pass an empty string to clear a previously set value. This property is stored securely and cannot be read in future (write-only).
     * @param {string | null} params.senderEmail - Email address shown in inbox as the sender of the email. Pass an empty string to clear a previously set value.
     * @param {string | null} params.senderName - Name shown in inbox as the sender of the email. Pass an empty string to clear a previously set value.
     * @param {string | null} params.replyToEmail - Email used when user replies to the email. Pass an empty string to clear a previously set value.
     * @param {string | null} params.replyToName - Name used when user replies to the email. Pass an empty string to clear a previously set value.
     * @param {ProjectSMTPSecure | null} params.secure - Configures if communication with SMTP server is encrypted. Allowed values are: tls, ssl. Leave empty for no encryption.
     * @param {boolean | null} params.enabled - Enable or disable custom SMTP. Custom SMTP is useful for branding purposes, but also allows use of custom email templates.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateSMTP(params?: {
        host?: string | null;
        port?: number | null;
        username?: string | null;
        password?: string | null;
        senderEmail?: string | null;
        senderName?: string | null;
        replyToEmail?: string | null;
        replyToName?: string | null;
        secure?: ProjectSMTPSecure | null;
        enabled?: boolean | null;
    }): Promise<Models.Project>;
    /**
     * Update the SMTP configuration for your project. Use this endpoint to configure your project's SMTP provider with your custom settings for sending transactional emails.
     *
     * @param {string | null} host - SMTP server hostname (domain)
     * @param {number | null} port - SMTP server port
     * @param {string | null} username - SMTP server username. Pass an empty string to clear a previously set value.
     * @param {string | null} password - SMTP server password. Pass an empty string to clear a previously set value. This property is stored securely and cannot be read in future (write-only).
     * @param {string | null} senderEmail - Email address shown in inbox as the sender of the email. Pass an empty string to clear a previously set value.
     * @param {string | null} senderName - Name shown in inbox as the sender of the email. Pass an empty string to clear a previously set value.
     * @param {string | null} replyToEmail - Email used when user replies to the email. Pass an empty string to clear a previously set value.
     * @param {string | null} replyToName - Name used when user replies to the email. Pass an empty string to clear a previously set value.
     * @param {ProjectSMTPSecure | null} secure - Configures if communication with SMTP server is encrypted. Allowed values are: tls, ssl. Leave empty for no encryption.
     * @param {boolean | null} enabled - Enable or disable custom SMTP. Custom SMTP is useful for branding purposes, but also allows use of custom email templates.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateSMTP(
        host?: string | null,
        port?: number | null,
        username?: string | null,
        password?: string | null,
        senderEmail?: string | null,
        senderName?: string | null,
        replyToEmail?: string | null,
        replyToName?: string | null,
        secure?: ProjectSMTPSecure | null,
        enabled?: boolean | null,
    ): Promise<Models.Project>;
    updateSMTP(
        paramsOrFirst?:
            | {
                  host?: string | null;
                  port?: number | null;
                  username?: string | null;
                  password?: string | null;
                  senderEmail?: string | null;
                  senderName?: string | null;
                  replyToEmail?: string | null;
                  replyToName?: string | null;
                  secure?: ProjectSMTPSecure | null;
                  enabled?: boolean | null;
              }
            | string
            | null,
        ...rest: [
            (number | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (ProjectSMTPSecure | null)?,
            (boolean | null)?,
        ]
    ): Promise<Models.Project> {
        let params: {
            host?: string | null;
            port?: number | null;
            username?: string | null;
            password?: string | null;
            senderEmail?: string | null;
            senderName?: string | null;
            replyToEmail?: string | null;
            replyToName?: string | null;
            secure?: ProjectSMTPSecure | null;
            enabled?: boolean | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                host?: string | null;
                port?: number | null;
                username?: string | null;
                password?: string | null;
                senderEmail?: string | null;
                senderName?: string | null;
                replyToEmail?: string | null;
                replyToName?: string | null;
                secure?: ProjectSMTPSecure | null;
                enabled?: boolean | null;
            };
        } else {
            params = {
                host: paramsOrFirst as string | null,
                port: rest[0] as number | null,
                username: rest[1] as string | null,
                password: rest[2] as string | null,
                senderEmail: rest[3] as string | null,
                senderName: rest[4] as string | null,
                replyToEmail: rest[5] as string | null,
                replyToName: rest[6] as string | null,
                secure: rest[7] as ProjectSMTPSecure | null,
                enabled: rest[8] as boolean | null,
            };
        }

        const host = params.host;
        const port = params.port;
        const username = params.username;
        const password = params.password;
        const senderEmail = params.senderEmail;
        const senderName = params.senderName;
        const replyToEmail = params.replyToEmail;
        const replyToName = params.replyToName;
        const secure = params.secure;
        const enabled = params.enabled;

        const apiPath = '/project/smtp';
        const apiPayload: Payload = {};
        if (typeof host !== 'undefined') {
            apiPayload['host'] = host;
        }
        if (typeof port !== 'undefined') {
            apiPayload['port'] = port;
        }
        if (typeof username !== 'undefined') {
            apiPayload['username'] = username;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof senderEmail !== 'undefined') {
            apiPayload['senderEmail'] = senderEmail;
        }
        if (typeof senderName !== 'undefined') {
            apiPayload['senderName'] = senderName;
        }
        if (typeof replyToEmail !== 'undefined') {
            apiPayload['replyToEmail'] = replyToEmail;
        }
        if (typeof replyToName !== 'undefined') {
            apiPayload['replyToName'] = replyToName;
        }
        if (typeof secure !== 'undefined') {
            apiPayload['secure'] = secure;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Send a test email to verify SMTP configuration.
     *
     * @param {string[]} params.emails - Array of emails to send test email to. Maximum of 10 emails are allowed.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    createSMTPTest(params: { emails: string[] }): Promise<{}>;
    /**
     * Send a test email to verify SMTP configuration.
     *
     * @param {string[]} emails - Array of emails to send test email to. Maximum of 10 emails are allowed.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createSMTPTest(emails: string[]): Promise<{}>;
    createSMTPTest(
        paramsOrFirst: { emails: string[] } | string[],
    ): Promise<{}> {
        let params: { emails: string[] };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { emails: string[] };
        } else {
            params = {
                emails: paramsOrFirst as string[],
            };
        }

        const emails = params.emails;

        if (typeof emails === 'undefined') {
            throw new AppwriteException('Missing required parameter: "emails"');
        }
        const apiPath = '/project/smtp/tests';
        const apiPayload: Payload = {};
        if (typeof emails !== 'undefined') {
            apiPayload['emails'] = emails;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of all custom email templates configured for the project. This endpoint returns an array of all configured email templates and their locales.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EmailTemplateList>}
     */
    listEmailTemplates(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.EmailTemplateList>;
    /**
     * Get a list of all custom email templates configured for the project. This endpoint returns an array of all configured email templates and their locales.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EmailTemplateList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listEmailTemplates(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.EmailTemplateList>;
    listEmailTemplates(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.EmailTemplateList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/project/templates/email';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'emailTemplateList',
            models: {
                emailTemplateList: {
                    defaults: {
                        total: 0,
                        templates: [],
                    },
                    properties: {
                        templates: {
                            items: 'emailTemplate',
                        },
                    },
                },
                emailTemplate: {
                    defaults: {
                        templateId: '',
                        locale: '',
                        message: '',
                        senderName: '',
                        senderEmail: '',
                        replyToEmail: '',
                        replyToName: '',
                        subject: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a custom email template for the specified locale and type. Use this endpoint to modify the content of your email templates.
     *
     * @param {ProjectEmailTemplateId} params.templateId - Custom email template type. Can be one of: verification, magicSession, recovery, invitation, mfaChallenge, sessionAlert, otpSession, otpVerification, otpRecovery
     * @param {ProjectEmailTemplateLocale} params.locale - Custom email template locale. If left empty, the fallback locale (en) will be used.
     * @param {string | null} params.subject - Subject of the email template. Can be up to 255 characters.
     * @param {string | null} params.message - Plain or HTML body of the email template message. Can be up to 10MB of content.
     * @param {string | null} params.senderName - Name of the email sender.
     * @param {string | null} params.senderEmail - Email of the sender. Pass an empty string to clear a previously set value.
     * @param {string | null} params.replyToEmail - Reply to email. Pass an empty string to clear a previously set value.
     * @param {string | null} params.replyToName - Reply to name.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EmailTemplate>}
     */
    updateEmailTemplate(params: {
        templateId: ProjectEmailTemplateId;
        locale?: ProjectEmailTemplateLocale;
        subject?: string | null;
        message?: string | null;
        senderName?: string | null;
        senderEmail?: string | null;
        replyToEmail?: string | null;
        replyToName?: string | null;
    }): Promise<Models.EmailTemplate>;
    /**
     * Update a custom email template for the specified locale and type. Use this endpoint to modify the content of your email templates.
     *
     * @param {ProjectEmailTemplateId} templateId - Custom email template type. Can be one of: verification, magicSession, recovery, invitation, mfaChallenge, sessionAlert, otpSession, otpVerification, otpRecovery
     * @param {ProjectEmailTemplateLocale} locale - Custom email template locale. If left empty, the fallback locale (en) will be used.
     * @param {string | null} subject - Subject of the email template. Can be up to 255 characters.
     * @param {string | null} message - Plain or HTML body of the email template message. Can be up to 10MB of content.
     * @param {string | null} senderName - Name of the email sender.
     * @param {string | null} senderEmail - Email of the sender. Pass an empty string to clear a previously set value.
     * @param {string | null} replyToEmail - Reply to email. Pass an empty string to clear a previously set value.
     * @param {string | null} replyToName - Reply to name.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EmailTemplate>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateEmailTemplate(
        templateId: ProjectEmailTemplateId,
        locale?: ProjectEmailTemplateLocale,
        subject?: string | null,
        message?: string | null,
        senderName?: string | null,
        senderEmail?: string | null,
        replyToEmail?: string | null,
        replyToName?: string | null,
    ): Promise<Models.EmailTemplate>;
    updateEmailTemplate(
        paramsOrFirst:
            | {
                  templateId: ProjectEmailTemplateId;
                  locale?: ProjectEmailTemplateLocale;
                  subject?: string | null;
                  message?: string | null;
                  senderName?: string | null;
                  senderEmail?: string | null;
                  replyToEmail?: string | null;
                  replyToName?: string | null;
              }
            | ProjectEmailTemplateId,
        ...rest: [
            ProjectEmailTemplateLocale?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
        ]
    ): Promise<Models.EmailTemplate> {
        let params: {
            templateId: ProjectEmailTemplateId;
            locale?: ProjectEmailTemplateLocale;
            subject?: string | null;
            message?: string | null;
            senderName?: string | null;
            senderEmail?: string | null;
            replyToEmail?: string | null;
            replyToName?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('templateId' in paramsOrFirst ||
                'locale' in paramsOrFirst ||
                'subject' in paramsOrFirst ||
                'message' in paramsOrFirst ||
                'senderName' in paramsOrFirst ||
                'senderEmail' in paramsOrFirst ||
                'replyToEmail' in paramsOrFirst ||
                'replyToName' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                templateId: ProjectEmailTemplateId;
                locale?: ProjectEmailTemplateLocale;
                subject?: string | null;
                message?: string | null;
                senderName?: string | null;
                senderEmail?: string | null;
                replyToEmail?: string | null;
                replyToName?: string | null;
            };
        } else {
            params = {
                templateId: paramsOrFirst as ProjectEmailTemplateId,
                locale: rest[0] as ProjectEmailTemplateLocale,
                subject: rest[1] as string | null,
                message: rest[2] as string | null,
                senderName: rest[3] as string | null,
                senderEmail: rest[4] as string | null,
                replyToEmail: rest[5] as string | null,
                replyToName: rest[6] as string | null,
            };
        }

        const templateId = params.templateId;
        const locale = params.locale;
        const subject = params.subject;
        const message = params.message;
        const senderName = params.senderName;
        const senderEmail = params.senderEmail;
        const replyToEmail = params.replyToEmail;
        const replyToName = params.replyToName;

        if (typeof templateId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "templateId"',
            );
        }
        const apiPath = '/project/templates/email';
        const apiPayload: Payload = {};
        if (typeof templateId !== 'undefined') {
            apiPayload['templateId'] = templateId;
        }
        if (typeof locale !== 'undefined') {
            apiPayload['locale'] = locale;
        }
        if (typeof subject !== 'undefined') {
            apiPayload['subject'] = subject;
        }
        if (typeof message !== 'undefined') {
            apiPayload['message'] = message;
        }
        if (typeof senderName !== 'undefined') {
            apiPayload['senderName'] = senderName;
        }
        if (typeof senderEmail !== 'undefined') {
            apiPayload['senderEmail'] = senderEmail;
        }
        if (typeof replyToEmail !== 'undefined') {
            apiPayload['replyToEmail'] = replyToEmail;
        }
        if (typeof replyToName !== 'undefined') {
            apiPayload['replyToName'] = replyToName;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'emailTemplate',
            models: {
                emailTemplate: {
                    defaults: {
                        templateId: '',
                        locale: '',
                        message: '',
                        senderName: '',
                        senderEmail: '',
                        replyToEmail: '',
                        replyToName: '',
                        subject: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a custom email template for the specified locale and type. This endpoint returns the template content, subject, and other configuration details.
     *
     * @param {ProjectEmailTemplateId} params.templateId - Custom email template type. Can be one of: verification, magicSession, recovery, invitation, mfaChallenge, sessionAlert, otpSession, otpVerification, otpRecovery
     * @param {ProjectEmailTemplateLocale} params.locale - Custom email template locale. If left empty, the fallback locale (en) will be used.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EmailTemplate>}
     */
    getEmailTemplate(params: {
        templateId: ProjectEmailTemplateId;
        locale?: ProjectEmailTemplateLocale;
    }): Promise<Models.EmailTemplate>;
    /**
     * Get a custom email template for the specified locale and type. This endpoint returns the template content, subject, and other configuration details.
     *
     * @param {ProjectEmailTemplateId} templateId - Custom email template type. Can be one of: verification, magicSession, recovery, invitation, mfaChallenge, sessionAlert, otpSession, otpVerification, otpRecovery
     * @param {ProjectEmailTemplateLocale} locale - Custom email template locale. If left empty, the fallback locale (en) will be used.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EmailTemplate>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getEmailTemplate(
        templateId: ProjectEmailTemplateId,
        locale?: ProjectEmailTemplateLocale,
    ): Promise<Models.EmailTemplate>;
    getEmailTemplate(
        paramsOrFirst:
            | {
                  templateId: ProjectEmailTemplateId;
                  locale?: ProjectEmailTemplateLocale;
              }
            | ProjectEmailTemplateId,
        ...rest: [ProjectEmailTemplateLocale?]
    ): Promise<Models.EmailTemplate> {
        let params: {
            templateId: ProjectEmailTemplateId;
            locale?: ProjectEmailTemplateLocale;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('templateId' in paramsOrFirst || 'locale' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                templateId: ProjectEmailTemplateId;
                locale?: ProjectEmailTemplateLocale;
            };
        } else {
            params = {
                templateId: paramsOrFirst as ProjectEmailTemplateId,
                locale: rest[0] as ProjectEmailTemplateLocale,
            };
        }

        const templateId = params.templateId;
        const locale = params.locale;

        if (typeof templateId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "templateId"',
            );
        }
        const apiPath = '/project/templates/email/{templateId}'.replace(
            '{templateId}',
            encodeURIComponent(String(templateId)),
        );
        const apiPayload: Payload = {};
        if (typeof locale !== 'undefined') {
            apiPayload['locale'] = locale;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'emailTemplate',
            models: {
                emailTemplate: {
                    defaults: {
                        templateId: '',
                        locale: '',
                        message: '',
                        senderName: '',
                        senderEmail: '',
                        replyToEmail: '',
                        replyToName: '',
                        subject: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all project environment variables.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: key, resourceType, resourceId, secret
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.VariableList>}
     */
    listVariables(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.VariableList>;
    /**
     * Get a list of all project environment variables.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: key, resourceType, resourceId, secret
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.VariableList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listVariables(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.VariableList>;
    listVariables(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.VariableList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/project/variables';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'variableList',
            models: {
                variableList: {
                    defaults: {
                        total: 0,
                        variables: [],
                    },
                    properties: {
                        variables: {
                            items: 'variable',
                        },
                    },
                },
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new project environment variable. These variables can be accessed by all functions and sites in the project.
     *
     * @param {string} params.variableId - Variable unique ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.key - Variable key. Letters, digits and underscores only, must not start with a digit. Max length: 255 chars.
     * @param {string} params.value - Variable value. Max length: 8192 chars.
     * @param {boolean} params.secret - Secret variables can be updated or deleted, but only projects can read them during build and runtime.
     * @param {string} params.description - Optional description for the variable.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     */
    createVariable(params: {
        variableId: string;
        key: string;
        value: string;
        secret?: boolean;
        description?: string;
    }): Promise<Models.Variable>;
    /**
     * Create a new project environment variable. These variables can be accessed by all functions and sites in the project.
     *
     * @param {string} variableId - Variable unique ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} key - Variable key. Letters, digits and underscores only, must not start with a digit. Max length: 255 chars.
     * @param {string} value - Variable value. Max length: 8192 chars.
     * @param {boolean} secret - Secret variables can be updated or deleted, but only projects can read them during build and runtime.
     * @param {string} description - Optional description for the variable.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createVariable(
        variableId: string,
        key: string,
        value: string,
        secret?: boolean,
        description?: string,
    ): Promise<Models.Variable>;
    createVariable(
        paramsOrFirst:
            | {
                  variableId: string;
                  key: string;
                  value: string;
                  secret?: boolean;
                  description?: string;
              }
            | string,
        ...rest: [string?, string?, boolean?, string?]
    ): Promise<Models.Variable> {
        let params: {
            variableId: string;
            key: string;
            value: string;
            secret?: boolean;
            description?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                variableId: string;
                key: string;
                value: string;
                secret?: boolean;
                description?: string;
            };
        } else {
            params = {
                variableId: paramsOrFirst as string,
                key: rest[0] as string,
                value: rest[1] as string,
                secret: rest[2] as boolean,
                description: rest[3] as string,
            };
        }

        const variableId = params.variableId;
        const key = params.key;
        const value = params.value;
        const secret = params.secret;
        const description = params.description;

        if (typeof variableId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "variableId"',
            );
        }
        if (typeof key === 'undefined') {
            throw new AppwriteException('Missing required parameter: "key"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        const apiPath = '/project/variables';
        const apiPayload: Payload = {};
        if (typeof variableId !== 'undefined') {
            apiPayload['variableId'] = variableId;
        }
        if (typeof key !== 'undefined') {
            apiPayload['key'] = key;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        if (typeof description !== 'undefined') {
            apiPayload['description'] = description;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'variable',
            models: {
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a variable by its unique ID.
     *
     * @param {string} params.variableId - Variable unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     */
    getVariable(params: { variableId: string }): Promise<Models.Variable>;
    /**
     * Get a variable by its unique ID.
     *
     * @param {string} variableId - Variable unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getVariable(variableId: string): Promise<Models.Variable>;
    getVariable(
        paramsOrFirst: { variableId: string } | string,
    ): Promise<Models.Variable> {
        let params: { variableId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { variableId: string };
        } else {
            params = {
                variableId: paramsOrFirst as string,
            };
        }

        const variableId = params.variableId;

        if (typeof variableId === 'undefined' || variableId === '') {
            throw new AppwriteException(
                'Missing required parameter: "variableId"',
            );
        }
        const apiPath = '/project/variables/{variableId}'.replace(
            '{variableId}',
            encodeURIComponent(String(variableId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'variable',
            models: {
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update variable by its unique ID.
     *
     * @param {string} params.variableId - Variable unique ID.
     * @param {string | null} params.key - Variable key. Letters, digits and underscores only, must not start with a digit. Max length: 255 chars.
     * @param {string | null} params.value - Variable value. Max length: 8192 chars.
     * @param {boolean | null} params.secret - Secret variables can be updated or deleted, but only projects can read them during build and runtime.
     * @param {string | null} params.description - Optional description for the variable.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     */
    updateVariable(params: {
        variableId: string;
        key?: string | null;
        value?: string | null;
        secret?: boolean | null;
        description?: string | null;
    }): Promise<Models.Variable>;
    /**
     * Update variable by its unique ID.
     *
     * @param {string} variableId - Variable unique ID.
     * @param {string | null} key - Variable key. Letters, digits and underscores only, must not start with a digit. Max length: 255 chars.
     * @param {string | null} value - Variable value. Max length: 8192 chars.
     * @param {boolean | null} secret - Secret variables can be updated or deleted, but only projects can read them during build and runtime.
     * @param {string | null} description - Optional description for the variable.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateVariable(
        variableId: string,
        key?: string | null,
        value?: string | null,
        secret?: boolean | null,
        description?: string | null,
    ): Promise<Models.Variable>;
    updateVariable(
        paramsOrFirst:
            | {
                  variableId: string;
                  key?: string | null;
                  value?: string | null;
                  secret?: boolean | null;
                  description?: string | null;
              }
            | string,
        ...rest: [
            (string | null)?,
            (string | null)?,
            (boolean | null)?,
            (string | null)?,
        ]
    ): Promise<Models.Variable> {
        let params: {
            variableId: string;
            key?: string | null;
            value?: string | null;
            secret?: boolean | null;
            description?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                variableId: string;
                key?: string | null;
                value?: string | null;
                secret?: boolean | null;
                description?: string | null;
            };
        } else {
            params = {
                variableId: paramsOrFirst as string,
                key: rest[0] as string | null,
                value: rest[1] as string | null,
                secret: rest[2] as boolean | null,
                description: rest[3] as string | null,
            };
        }

        const variableId = params.variableId;
        const key = params.key;
        const value = params.value;
        const secret = params.secret;
        const description = params.description;

        if (typeof variableId === 'undefined' || variableId === '') {
            throw new AppwriteException(
                'Missing required parameter: "variableId"',
            );
        }
        const apiPath = '/project/variables/{variableId}'.replace(
            '{variableId}',
            encodeURIComponent(String(variableId)),
        );
        const apiPayload: Payload = {};
        if (typeof key !== 'undefined') {
            apiPayload['key'] = key;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        if (typeof description !== 'undefined') {
            apiPayload['description'] = description;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'variable',
            models: {
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a variable by its unique ID.
     *
     * @param {string} params.variableId - Variable unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteVariable(params: { variableId: string }): Promise<{}>;
    /**
     * Delete a variable by its unique ID.
     *
     * @param {string} variableId - Variable unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteVariable(variableId: string): Promise<{}>;
    deleteVariable(
        paramsOrFirst: { variableId: string } | string,
    ): Promise<{}> {
        let params: { variableId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { variableId: string };
        } else {
            params = {
                variableId: paramsOrFirst as string,
            };
        }

        const variableId = params.variableId;

        if (typeof variableId === 'undefined' || variableId === '') {
            throw new AppwriteException(
                'Missing required parameter: "variableId"',
            );
        }
        const apiPath = '/project/variables/{variableId}'.replace(
            '{variableId}',
            encodeURIComponent(String(variableId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }
}
